import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { TemplateDetailSkeleton } from '../components/PageSkeletons'
import ExtractLoader from '../components/ExtractLoader'
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import api, {
  templatesApi,
  jobsApi,
  authApi,
  retentionApi,
  bcApi,
  nsApi,
  outputDestinationsApi,
  boomiApi,
} from "../services/api";
import TemplateAccess from "../components/TemplateAccess";
import TemplateBCSettings from "../components/TemplateBCSettings";
import TemplateBCDashboard from "../components/TemplateBCDashboard";
import TemplateNSSettings from "../components/TemplateNSSettings";
import TemplateJsonImportModal, { exportTemplateJson } from "../components/TemplateJsonImportModal";
import TemplateNSDashboard from "../components/TemplateNSDashboard";
import NSNameMappings from "../components/NSNameMappings";
import NSFieldFreshness from "../components/NSFieldFreshness";
import NSTemplateSetup from "../components/NSTemplateSetup";
import TemplateActivity from "../components/TemplateActivity";
import Toggle from "../components/Toggle";
import { formatDate } from "../utils/dateUtils";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { isReadOnlyUser, READ_ONLY_MESSAGE } from "../utils/readerAccess";
import { emailFieldLabel, isEmailSourced, isTemplateDefault } from "../utils/fieldValueSources";
import { checkMapping, fieldDeliveryNotes, mappingImpact } from "../utils/mappingConsistency";
import EmailFieldsPicker from "../components/EmailFieldsPicker";
import SuggestionQuestions from "../components/ai-suggest/SuggestionQuestions";
import TableSuggestionDetail from "../components/ai-suggest/TableSuggestionDetail";
import PlacementBadge from "../components/ai-suggest/PlacementBadge";
import { isOptionalSuggestion, planSuggestionChanges } from "../utils/applySuggestionPlan";
import {
  STANDARD_NS_BID_FIELDS,
  bidFieldsFromMappings,
  excludedBidLabels,
  isKnownLayoutMapping,
} from "../utils/nsBidFields";
import {
  ArrowLeft,
  Plus,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Bot,
  Pencil,
  Trash2,
  Save,
  CheckCircle2,
  Upload,
  Sparkles,
  FileText,
  X,
  Check,
  ArrowRight,
  CornerDownRight,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  Info,
  User,
  Calendar,
  Clock,
  Webhook,
  Link2,
  Unlink,
  RefreshCw,
  Activity,
  Hash,
  FileDown,
  UserCheck,
  FolderCog,
  Mail,
  Cpu,
  BarChart3,
  CreditCard,
  Zap,
  RotateCcw,
  Building2,
  FileStack,
  Braces,
  Copy,
  Server,
  Rows,
  FileJson,
  Download,
} from "lucide-react";
import { inWorkspace } from "../config/opcoWorkspace";
import { TemplateOpCoActions, TemplateOpCoChip } from "../components/opco/TemplateOpCo";

// Rich extraction hints for well-known NetSuite PO field keys — used instead of a
// generic "Maps to NS field: X" description so Azure CU and the LLM fallback know
// precisely what to look for (this matters most for 'entity', which is commonly
// mis-targeted at the buyer/ship-to company instead of the vendor/supplier).
const NS_FIELD_HINTS = {
  entity:
    'The vendor/supplier company name — the business that issued this purchase order and that goods or services are being bought from. This is NOT the buyer, "Deliver To", or "Ship To" company.',
  tranDate: "The transaction/order date the purchase order was issued.",
  tranId:
    'The purchase order number (PO number) as printed on the document, e.g. "PO-1234" or "Order No: 1587626".',
  subsidiary:
    "The subsidiary or business entity this purchase order belongs to.",
  location:
    "The delivery/ship-to location, site, or warehouse for this purchase order.",
  currency:
    "The currency the amounts on this purchase order are stated in (e.g. USD, AUD, GBP).",
  terms:
    'The payment terms shown on the document (e.g. "Net 30", "30 Days from EOM").',
  memo: "Any notes, comments, or remarks printed on the purchase order.",
};

// Standard NetSuite PO header/line fields — used to seed a template's "NetSuite PO"
// field group in one click when no AI-suggested fields exist yet for it (e.g. NS
// integration was turned on without ever running "Upload NetSuite PDF").
const STANDARD_NS_PO_FIELDS = [
  { field_name: "tranDate", display_name: "Date", ns_key: "tranDate" },
  { field_name: "tranId", display_name: "Order No", ns_key: "tranId" },
  { field_name: "entity", display_name: "Supplier", ns_key: "entity" },
  { field_name: "location", display_name: "Deliver To", ns_key: "location" },
  { field_name: "currency", display_name: "Currency", ns_key: "currency" },
  {
    field_name: "subsidiary",
    display_name: "digiDirect",
    ns_key: "subsidiary",
  },
]
  .map((f) => ({
    ...f,
    field_type: "text",
    category: "NetSuite PO",
    description: `${NS_FIELD_HINTS[f.ns_key]} (appears on the document as "${f.display_name}")`,
  }))
  .concat([
    {
      field_name: "line_items",
      display_name: "Line Items",
      field_type: "array_of_objects",
      category: "NetSuite PO",
      description:
        "Purchase order line items (item code, description, quantity, unit price, UOM)",
      columns: [
        {
          name: "item_code",
          type: "text",
          description: "Item/product code as it appears on the document",
        },
        { name: "description", type: "text", description: "Item description" },
        { name: "quantity", type: "number", description: "Quantity ordered" },
        {
          name: "unit_price",
          type: "currency",
          description: "Unit price per item",
        },
        {
          name: "uom",
          type: "text",
          description: "Unit of measure (e.g. EA, BOX)",
        },
      ],
    },
  ]);

// Special Pricing (Vendor Bid) field logic lives in utils/nsBidFields.js so
// this page and the create-template wizard build identical templates from the
// same PDF — see that file for why they must not have separate copies.

// ─── NetSuite PO field options ───────────────────────────────────────────────
// These are loaded DYNAMICALLY from the backend via nsApi.detectFields()
// The grouping helper below organizes the flat array into optgroup categories.
function groupNsFields(flatFields) {
  if (!flatFields || flatFields.length === 0) return [];
  const required = flatFields.filter((f) => f.mandatory);
  const custom = flatFields.filter((f) => f.is_custom && !f.mandatory);
  const standard = flatFields.filter((f) => !f.is_custom && !f.mandatory);
  const groups = [];
  if (required.length) groups.push({ group: "Required", fields: required });
  if (standard.length) groups.push({ group: "Standard", fields: standard });
  if (custom.length)
    groups.push({ group: "Custom (custbody)", fields: custom });
  return groups;
}

// ─── Searchable NS Field Dropdown ─────────────────────────────────────────────
// Replaces plain <select> with a search-filterable dropdown for 248+ fields.
function NsFieldSearchDropdown({
  value,
  onChange,
  catalog,
  loadError = '',
  compact = false,
  placeholder = "Select NS field…",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(null);
  const dropRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => (catalog || []).find((f) => f.key === value),
    [catalog, value],
  );

  // Always show the saved key even when catalog hasn't loaded yet
  const displayText = useMemo(() => {
    if (selected) {
      // Scope is part of the identity, not decoration: mapping a header
      // value onto a LINE field silently moves it into the line table at
      // review time, which is invisible from the key alone.
      const scope = selected.scope === "line" ? " · Line" : "";
      return compact
        ? `${selected.label}${scope}`
        : `${selected.label} (${selected.key})${scope}`;
    }
    if (value) return value; // fallback: show raw key from DB
    return placeholder;
  }, [selected, value, compact, placeholder]);

  const filtered = useMemo(() => {
    const src = catalog || [];
    if (!query.trim()) return src;
    const q = query.toLowerCase().trim();
    return src.filter(
      (f) =>
        f.key.toLowerCase().includes(q) ||
        (f.label || "").toLowerCase().includes(q),
    );
  }, [catalog, query]);

  const groupedFiltered = useMemo(() => groupNsFields(filtered), [filtered]);

  const minWidth = compact ? 300 : 320;
  const maxPanelHeight = compact ? 240 : 288; // matches max-h-60 / max-h-72

  const updateCoords = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < maxPanelHeight && rect.top > spaceBelow;
    setCoords({
      left: rect.left,
      width: Math.max(rect.width, minWidth),
      top: openUpward ? null : rect.bottom + 4,
      bottom: openUpward ? window.innerHeight - rect.top + 4 : null,
    });
  }, [maxPanelHeight, minWidth]);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    function handleClickOutside(e) {
      if (
        dropRef.current &&
        !dropRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSelect = (key) => {
    onChange(key);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={dropRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        title={
          disabled
            ? "You do not have permission to edit NetSuite mappings"
            : selected
              ? `${selected.label} (${selected.key})`
              : undefined
        }
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-1 rounded-lg border text-left ${compact ? "px-2 py-1 text-[10px]" : "px-3 py-2 text-sm"} ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${
          value
            ? "border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
            : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500"
        }`}
      >
        {compact && (selected || value) ? (
          // The NetSuite internal id is what actually gets posted, and several
          // custom fields share a label, so it stays visible on the closed
          // control rather than only inside the open list.
          <span className="min-w-0 flex-1 flex flex-col items-start leading-tight">
            <span className="w-full truncate">{displayText}</span>
            <span className="w-full truncate font-mono text-[9px] text-purple-500/80 dark:text-purple-400/80">
              {selected ? selected.key : value}
            </span>
          </span>
        ) : (
          <span className={`truncate ${compact ? "font-mono" : ""}`}>
            {displayText}
          </span>
        )}
        <ChevronDown
          className={`flex-shrink-0 transition-transform ${compact ? "h-3 w-3" : "h-4 w-4"} ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className={`fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl flex flex-col ${compact ? "max-h-60" : "max-h-72"}`}
            style={{
              top: coords.top != null ? `${coords.top}px` : undefined,
              bottom: coords.bottom != null ? `${coords.bottom}px` : undefined,
              left: `${coords.left}px`,
              width: `${coords.width}px`,
            }}
          >
            {/* Search input */}
            <div className="p-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fields…"
                className="w-full px-2.5 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>

            {/* Clear option */}
            {value && (
              <button
                onClick={() => handleSelect("")}
                className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 border-b border-gray-100 dark:border-gray-700"
              >
                ✕ Clear mapping
              </button>
            )}

            {/* Catalog not loaded — distinguish "still fetching" from "failed",
                since it used to say "Loading…" forever on a failure the fetch
                had already given up on (e.g. a permission error), which looked
                identical to the dropdown being stuck. */}
            {(!catalog || catalog.length === 0) && (
              <p className={`px-3 py-3 text-xs italic text-center ${
                loadError
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}>
                {loadError || 'Loading NS fields from NetSuite…'}
              </p>
            )}

            {/* Scrollable results */}
            <div className="overflow-y-auto flex-1">
              {catalog &&
                catalog.length > 0 &&
                groupedFiltered.length === 0 && (
                  <p className="px-3 py-4 text-xs text-gray-400 italic text-center">
                    No fields match "{query}"
                  </p>
                )}
              {groupedFiltered.map((group) => (
                <div key={group.group}>
                  <div className="px-3 pt-2 pb-0.5 sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      {group.group}
                    </span>
                  </div>
                  {group.fields.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => handleSelect(f.key)}
                      title={`${f.label} (${f.key})`}
                      className={`w-full text-left px-3 py-1.5 flex flex-col gap-0.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 ${value === f.key ? "bg-purple-100 dark:bg-purple-900/30" : ""}`}
                    >
                      <span className="text-xs text-gray-900 dark:text-gray-100 leading-snug break-words">
                        {f.label}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        {f.key}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function groupBcFields(flatFields) {
  if (!flatFields || flatFields.length === 0) return []
  const required = flatFields.filter(f => f.mandatory)
  const standard = flatFields.filter(f => !f.mandatory)
  const groups = []
  if (required.length) groups.push({ group: 'Required', fields: required })
  if (standard.length) groups.push({ group: 'Standard', fields: standard })
  return groups
}

// ─── Searchable BC Field Dropdown ─────────────────────────────────────────────
// Mirrors NsFieldSearchDropdown exactly (same search-filterable pattern), with
// an indigo color scheme instead of purple so BC and NS mappings stay visually
// distinct in a template that could — in principle — have both configured.
function BcFieldSearchDropdown({ value, onChange, catalog, compact = false, placeholder = 'Select BC field…', disabled = false }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [coords, setCoords] = useState(null)
  const dropRef = useRef(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const inputRef = useRef(null)

  const selected = useMemo(() => (catalog || []).find(f => f.key === value), [catalog, value])

  const displayText = useMemo(() => {
    if (selected) return compact ? selected.key : `${selected.label} (${selected.key})`
    if (value) return value
    return placeholder
  }, [selected, value, compact, placeholder])

  const filtered = useMemo(() => {
    const src = catalog || []
    if (!query.trim()) return src
    const q = query.toLowerCase().trim()
    return src.filter(f =>
      f.key.toLowerCase().includes(q) ||
      (f.label || '').toLowerCase().includes(q)
    )
  }, [catalog, query])

  const groupedFiltered = useMemo(() => groupBcFields(filtered), [filtered])

  const minWidth = compact ? 300 : 320
  const maxPanelHeight = compact ? 240 : 288

  const updateCoords = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < maxPanelHeight && rect.top > spaceBelow
    setCoords({
      left: rect.left,
      width: Math.max(rect.width, minWidth),
      top: openUpward ? null : rect.bottom + 4,
      bottom: openUpward ? window.innerHeight - rect.top + 4 : null,
    })
  }, [maxPanelHeight, minWidth])

  useEffect(() => {
    if (!open) return
    updateCoords()
    function handleClickOutside(e) {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [open, updateCoords])

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus() }, [open])

  const handleSelect = (key) => {
    onChange(key)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={dropRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        title={disabled ? 'You do not have permission to edit Business Central mappings' : undefined}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-1 rounded-lg border text-left ${compact ? 'px-2 py-1 text-[10px]' : 'px-3 py-2 text-sm'} ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${
          value
            ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
            : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500'
        }`}
      >
        <span className={`truncate ${compact ? 'font-mono' : ''}`}>
          {displayText}
        </span>
        <ChevronDown className={`flex-shrink-0 transition-transform ${compact ? 'h-3 w-3' : 'h-4 w-4'} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && coords && createPortal(
        <div
          ref={panelRef}
          className={`fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl flex flex-col ${compact ? 'max-h-60' : 'max-h-72'}`}
          style={{
            top: coords.top != null ? `${coords.top}px` : undefined,
            bottom: coords.bottom != null ? `${coords.bottom}px` : undefined,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
          }}
        >
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search fields…"
              className="w-full px-2.5 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
          </div>

          {value && (
            <button
              onClick={() => handleSelect('')}
              className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 border-b border-gray-100 dark:border-gray-700"
            >
              ✕ Clear mapping
            </button>
          )}

          {(!catalog || catalog.length === 0) && (
            <p className="px-3 py-3 text-xs text-amber-600 dark:text-amber-400 italic text-center">Loading BC fields from Business Central…</p>
          )}

          <div className="overflow-y-auto flex-1">
            {catalog && catalog.length > 0 && groupedFiltered.length === 0 && (
              <p className="px-3 py-4 text-xs text-gray-400 italic text-center">No fields match "{query}"</p>
            )}
            {groupedFiltered.map(group => (
              <div key={group.group}>
                <div className="px-3 pt-2 pb-0.5 sticky top-0 bg-white dark:bg-gray-800 z-10">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{group.group}</span>
                </div>
                {group.fields.map(f => (
                  <button
                    key={f.key}
                    onClick={() => handleSelect(f.key)}
                    title={`${f.label} (${f.key})`}
                    className={`w-full text-left px-3 py-1.5 flex flex-col gap-0.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 ${value === f.key ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''}`}
                  >
                    <span className="text-xs text-gray-900 dark:text-gray-100 leading-snug break-words">{f.label}</span>
                    <span className="text-[10px] text-gray-400 font-mono">{f.key}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

const DATA_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "percentage", label: "Percentage" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean" },
  { value: "dropdown", label: "Dropdown" },
  { value: "array_of_objects", label: "Table (Line Items)" },
];

// How a template's documents write dates (Settings tab). 12/02/2026 is read
// with this; the field's Output Format below only changes how it is written out.
const DATE_ORDERS = [
  { value: "day_first", label: "Day first (31/12/2026)" },
  { value: "month_first", label: "Month first (12/31/2026)" },
  { value: "year_first", label: "Year first (26/12/31)" },
];

const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
  { value: "DD-MMM-YYYY", label: "DD-MMM-YYYY" },
];

const COLUMN_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "date", label: "Date" },
];

function formatSampleValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  if (Array.isArray(value))
    return `[${value.length} item${value.length !== 1 ? "s" : ""}]`;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// Retention: Entity type metadata (template-scoped only)
const RETENTION_ENTITY_TYPES = [
  {
    key: "documents",
    label: "Documents / PDFs",
    icon: FileText,
    description: "Uploaded PDF files and their metadata.",
    defaultDays: 180,
    hasBlobs: true,
    hasStatusFilter: true,
  },
  {
    key: "requests",
    label: "Requests",
    icon: FileText,
    description: "Work items with extracted field data.",
    defaultDays: 365,
    hasBlobs: false,
    hasStatusFilter: true,
  },
  {
    key: "emails",
    label: "Emails",
    icon: Mail,
    description: "Source email metadata linked to this template.",
    defaultDays: 365,
    hasBlobs: false,
    hasStatusFilter: true,
  },
  {
    key: "async_jobs",
    label: "Processing Jobs",
    icon: Cpu,
    description: "Completed/failed analysis job records.",
    defaultDays: 60,
    hasBlobs: false,
    hasStatusFilter: false,
  },
  {
    key: "analysis_runs",
    label: "Analysis Runs",
    icon: BarChart3,
    description: "AI extraction payloads (latest per document kept).",
    defaultDays: 60,
    hasBlobs: false,
    hasStatusFilter: false,
  },
  {
    key: "webhook_deliveries",
    label: "Webhook Delivery Logs",
    icon: Webhook,
    description: "Webhook fire history for this template.",
    defaultDays: 30,
    hasBlobs: false,
    hasStatusFilter: false,
  },
  {
    key: "metered_usage",
    label: "Metered Usage Records",
    icon: CreditCard,
    description: "Billing records (only reported ones).",
    defaultDays: 730,
    hasBlobs: false,
    hasStatusFilter: false,
  },
];

const RETENTION_STATUS_OPTIONS = [
  "pending",
  "in_progress",
  "processing",
  "queued",
  "extracted",
  "reviewing",
  "pending_approval",
  "in_review",
  "approved",
];

const RETENTION_IMPACT_LABELS = {
  documents: "documents",
  requests: "requests",
  emails: "emails",
  async_jobs: "processing jobs",
  analysis_runs: "analysis runs",
  webhook_deliveries: "webhook delivery logs",
  metered_usage: "metered usage records",
};

// Retention: category groupings
const RETENTION_GROUPS = [
  {
    title: "Core Data",
    subtitle: "Primary business data: documents, requests, and email metadata",
    keys: ["documents", "requests", "emails"],
  },
  {
    title: "Processing & Logs",
    subtitle: "System-generated records: safe to clean up more aggressively",
    keys: [
      "async_jobs",
      "analysis_runs",
      "webhook_deliveries",
      "metered_usage",
    ],
  },
];

// Route map for clicking preview items
const ENTITY_ROUTES = {
  documents: (item) => `/documents/${item.id}`,
  requests: (item) => `/requests/${item.id}`,
  emails: (item) => `/email-queue`,
  async_jobs: (item) => `/activity`,
  analysis_runs: (item) => null,
  webhook_deliveries: (item) => null,
  metered_usage: (item) => null,
  notifications: (item) => null,
};

// Retention: Compact Policy Row (table-style within grouped card)
function RetentionPolicyRow({
  entityMeta,
  policy,
  localState,
  onLocalChange,
  onPreview,
  onPreviewItems,
  readOnly = false,
}) {
  const [previewing, setPreviewing] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewItems, setPreviewItems] = useState([]);
  const [showItems, setShowItems] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const Icon = entityMeta.icon;

  const { days, enabled, deleteBlobs, excludeStatuses } = localState;

  const doCheck = async () => {
    setPreviewing(true);
    try {
      const data = {
        entity_type: entityMeta.key,
        template_id: localState.templateId,
        days,
        exclude_statuses: entityMeta.hasStatusFilter ? excludeStatuses : [],
      };
      const result = await onPreviewItems(data);
      setPreviewItems(result.items || []);
      setPreviewCount(result.total || 0);
      setShowItems(true);
    } finally {
      setPreviewing(false);
    }
  };
  // Reset preview when config changes
  useEffect(() => {
    setPreviewCount(null);
    setPreviewItems([]);
    setShowItems(false);
  }, [days, enabled, excludeStatuses, deleteBlobs]);

  // Calculate age label
  const getAge = (createdAt) => {
    if (!createdAt) return "\u2014";
    const diff = Date.now() - new Date(createdAt).getTime();
    const d = Math.floor(diff / 86400000);
    return `${d}d old`;
  };

  // Get route for an item
  const getItemRoute = (item) => {
    const routeFn = ENTITY_ROUTES[entityMeta.key];
    return routeFn ? routeFn(item) : null;
  };

  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center gap-5 px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors">
        {/* Icon */}
        <div
          className={`p-2 rounded-lg flex-shrink-0 ${enabled ? "bg-[var(--token-primary,#1946FA)]/10" : "bg-gray-100 dark:bg-gray-800"}`}
        >
          <Icon
            className={`h-4.5 w-4.5 ${enabled ? "text-[var(--token-primary,#1946FA)]" : "text-gray-400 dark:text-gray-500"}`}
          />
        </div>

        {/* Label + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-medium text-gray-900 dark:text-gray-100">
              {entityMeta.label}
            </span>
            {policy?.enabled && !localState._dirty && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[var(--token-primary,#1946FA)]/10 text-[var(--token-primary,#1946FA)] uppercase tracking-wide">
                Active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
            {entityMeta.description}
          </p>
        </div>

        {/* Days input */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="number"
            min={1}
            max={9999}
            value={days}
            onChange={(e) =>
              onLocalChange(entityMeta.key, {
                days: Math.max(1, parseInt(e.target.value) || 1),
              })
            }
            disabled={readOnly || !enabled}
            className="w-[64px] text-sm font-medium py-2 px-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-center disabled:opacity-40 disabled:cursor-not-allowed focus:ring-2 focus:ring-[var(--token-primary,#1946FA)]/20 focus:border-[var(--token-primary,#1946FA)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-xs text-gray-400">days</span>
        </div>

        {/* Check button */}
        {enabled && (
          <button
            onClick={doCheck}
            disabled={readOnly || previewing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-[var(--token-primary,#1946FA)]/10 text-[var(--token-primary,#1946FA)] border border-[var(--token-primary,#1946FA)]/20 hover:bg-[var(--token-primary,#1946FA)]/15 transition-colors disabled:opacity-40 flex-shrink-0"
          >
            {previewing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Activity className="h-3 w-3" />
            )}
            Check
          </button>
        )}

        {/* Options button */}
        {(entityMeta.hasBlobs || entityMeta.hasStatusFilter) && enabled && (
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors flex-shrink-0 ${
              showAdvanced
                ? "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
          >
            <FolderCog className="h-3 w-3" />
            Options
          </button>
        )}

        {/* Toggle */}
        <Toggle
          checked={enabled}
          onChange={() => onLocalChange(entityMeta.key, { enabled: !enabled })}
          disabled={readOnly}
        />
      </div>

      {/* Advanced panel below row */}
      {showAdvanced &&
        (entityMeta.hasBlobs || entityMeta.hasStatusFilter) &&
        enabled && (
          <RetentionAdvancedPanel
            entityMeta={entityMeta}
            localState={localState}
            onLocalChange={onLocalChange}
            previewCount={previewCount}
          />
        )}

      {/* Preview items table */}
      {showItems && previewCount !== null && enabled && (
        <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20">
          {/* Warning line */}
          <div className="flex items-center gap-2 px-5 py-2.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              {previewCount === 0
                ? "No items match this policy"
                : `${previewCount.toLocaleString()} ${entityMeta.label.toLowerCase()} older than ${days} days will be deleted`}
            </span>
          </div>
          {/* Items table */}
          {previewItems.length > 0 && (
            <div className="max-h-[280px] overflow-y-auto">
              <div className="grid grid-cols-[1fr_100px_100px_70px] gap-2 px-5 py-2 bg-gray-100/70 dark:bg-gray-800/60 border-y border-gray-100 dark:border-gray-800 sticky top-0">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  Name
                </span>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  Status
                </span>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  Created
                </span>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-right">
                  Age
                </span>
              </div>
              {previewItems.map((item) => {
                const route = getItemRoute(item);
                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr_100px_100px_70px] gap-2 px-5 py-2 items-center border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-white/60 dark:hover:bg-gray-800/40"
                  >
                    {route ? (
                      <a
                        href={inWorkspace(route)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--token-primary,#1946FA)] hover:underline truncate font-medium"
                      >
                        {item.name || `#${item.id}`}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate font-medium">
                        {item.name || `#${item.id}`}
                      </span>
                    )}
                    <span
                      className={`text-[11px] font-medium capitalize ${item.status ? "text-gray-600 dark:text-gray-400" : "text-gray-300 dark:text-gray-600"}`}
                    >
                      {item.status ? item.status.replace(/_/g, " ") : "\u2014"}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString(
                            "en-GB",
                            { day: "2-digit", month: "short", year: "2-digit" },
                          )
                        : "\u2014"}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 text-right">
                      {getAge(item.created_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Retention: Advanced options panel (renders below expanded row)
function RetentionAdvancedPanel({
  entityMeta,
  localState,
  onLocalChange,
  previewCount,
}) {
  const { deleteBlobs, excludeStatuses } = localState;

  return (
    <div className="px-4 py-3 bg-gray-50/50 dark:bg-gray-800/20 border-b border-gray-100 dark:border-gray-800">
      <div className="pl-10 space-y-3">
        {entityMeta.hasBlobs && (
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 w-24">
              Blob files:
            </span>
            <div className="flex gap-1.5">
              <button
                onClick={() =>
                  onLocalChange(entityMeta.key, { deleteBlobs: true })
                }
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                  deleteBlobs
                    ? "border-[var(--token-error,#d94a44)] bg-[var(--token-error,#d94a44)]/5 text-[var(--token-error,#d94a44)]"
                    : "border-gray-200 dark:border-gray-700 text-gray-400"
                }`}
              >
                Delete
              </button>
              <button
                onClick={() =>
                  onLocalChange(entityMeta.key, { deleteBlobs: false })
                }
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                  !deleteBlobs
                    ? "border-[var(--token-success,#3b9b5e)] bg-[var(--token-success,#3b9b5e)]/5 text-[var(--token-success,#3b9b5e)]"
                    : "border-gray-200 dark:border-gray-700 text-gray-400"
                }`}
              >
                Keep
              </button>
            </div>
          </div>
        )}
        {entityMeta.hasStatusFilter && (
          <div>
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 block mb-1.5">
              Protected statuses (won't be deleted):
            </span>
            <div className="flex flex-wrap gap-1.5">
              {RETENTION_STATUS_OPTIONS.map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    const next = excludeStatuses.includes(status)
                      ? excludeStatuses.filter((s) => s !== status)
                      : [...excludeStatuses, status];
                    onLocalChange(entityMeta.key, { excludeStatuses: next });
                  }}
                  className={`px-2 py-0.5 text-[11px] rounded-md border font-medium transition-colors ${
                    excludeStatuses.includes(status)
                      ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      : "border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {excludeStatuses.includes(status) ? "✓ " : ""}
                  {status.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
        )}
        {previewCount !== null && (
          <div
            className={`flex items-center gap-2 pt-1 ${previewCount === 0 ? "text-[var(--token-success,#3b9b5e)]" : "text-amber-600 dark:text-amber-400"}`}
          >
            {previewCount === 0 ? (
              <CheckCircle2 className="h-3 w-3" />
            ) : (
              <AlertTriangle className="h-3 w-3" />
            )}
            <span className="text-[11px] font-medium">
              {previewCount === 0
                ? "No items match this policy"
                : `${previewCount.toLocaleString()} items will be affected`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Recycle Bin Section
function RetentionRecycleBinSection({ templateId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [page, setPage] = useState(1);
  const [restoringId, setRestoringId] = useState(null);
  const toast = useToast();
  const perPage = 10;

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await retentionApi.getRecycleBin({
        template_id: templateId,
        page: p,
        per_page: perPage,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
      setEndpointMissing(false);
      setLoaded(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        setEndpointMissing(true);
        setItems([]);
        setTotal(0);
        setLoaded(true);
      } else {
        toast.error("Failed to load recycle bin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (itemId) => {
    setRestoringId(itemId);
    try {
      await retentionApi.restoreItem(itemId);
      toast.success("Item restored");
      load(page);
    } catch (e) {
      toast.error("Failed to restore");
    } finally {
      setRestoringId(null);
    }
  };

  useEffect(() => {
    load();
  }, [templateId]);

  if (!loaded && loading)
    return (
      <div className="py-6 text-center">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400 mx-auto" />
      </div>
    );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Trash2 className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Recycle Bin
          </h3>
          {total > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
              {total}
            </span>
          )}
        </div>
        <span className="text-[11px] text-gray-400">
          Items recoverable for 7 days
        </span>
      </div>
      {endpointMissing ? (
        <div className="py-8 px-5 text-center">
          <AlertCircle className="h-5 w-5 text-amber-500 mx-auto mb-2" />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Recycle Bin is not available in this deployed backend version yet.
          </p>
        </div>
      ) : total === 0 ? (
        <div className="py-8 text-center">
          <CheckCircle2 className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Recycle bin is empty
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_80px_80px_60px] gap-2 px-5 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Item
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Type
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Expires
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-right">
              Action
            </span>
          </div>
          {items.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[1fr_80px_80px_60px] gap-2 px-5 py-2.5 items-center border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/30"
            >
              <span className="text-xs text-gray-800 dark:text-gray-200 truncate font-medium">
                {item.entity_name || `#${item.entity_id}`}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">
                {item.entity_type}
              </span>
              <span
                className={`text-[11px] font-medium ${(item.days_remaining || 0) <= 2 ? "text-[var(--token-error,#d94a44)]" : "text-gray-500"}`}
              >
                {item.days_remaining || 0}d left
              </span>
              <div className="flex justify-end">
                <button
                  onClick={() => handleRestore(item.id)}
                  disabled={restoringId === item.id}
                  className="text-[11px] font-medium text-[var(--token-primary,#1946FA)] hover:underline disabled:opacity-50"
                >
                  {restoringId === item.id ? "..." : "Restore"}
                </button>
              </div>
            </div>
          ))}
          {total > perPage && (
            <div className="px-5 py-2 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800">
              <span className="text-[11px] text-gray-400">{total} total</span>
              <div className="flex gap-2">
                <button
                  onClick={() => load(page - 1)}
                  disabled={page <= 1 || loading}
                  className="text-[11px] text-[var(--token-primary,#1946FA)] disabled:opacity-30"
                >
                  Prev
                </button>
                <button
                  onClick={() => load(page + 1)}
                  disabled={page * perPage >= total || loading}
                  className="text-[11px] text-[var(--token-primary,#1946FA)] disabled:opacity-30"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Deletion Log Section
function RetentionDeletionLog({ templateId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await retentionApi.getAuditLog({ limit: 50 });
        setEntries(res.data.entries || []);
        setLoaded(true);
      } catch (e) {
        toast.error("Failed to load deletion log");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [templateId]);

  if (!loaded && loading)
    return (
      <div className="py-6 text-center">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400 mx-auto" />
      </div>
    );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Clock className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Deletion Log
          </h3>
          {entries.length > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              {entries.length}
            </span>
          )}
        </div>
        <span className="text-[11px] text-gray-400">
          Permanent deletions (audit trail)
        </span>
      </div>
      {entries.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            No items permanently deleted yet
          </p>
        </div>
      ) : (
        <div className="max-h-[280px] overflow-y-auto">
          <div className="grid grid-cols-[1fr_80px_100px] gap-2 px-5 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 sticky top-0">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Item
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Type
            </span>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Deleted
            </span>
          </div>
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="grid grid-cols-[1fr_80px_100px] gap-2 px-5 py-2 items-center border-b border-gray-50 dark:border-gray-800/50 last:border-0"
            >
              <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                {entry.entity_name || `#${entry.entity_id}`}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">
                {entry.entity_type}
              </span>
              <span className="text-[11px] text-gray-400">
                {entry.deleted_at
                  ? new Date(entry.deleted_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "2-digit",
                    })
                  : "-"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function slugify(value = "") {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function formatDateTimeShort(value) {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDeliveryDuration(createdAt, completedAt) {
  if (!createdAt || !completedAt) return "N/A";
  const start = new Date(createdAt).getTime();
  const end = new Date(completedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
    return "N/A";
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(ms >= 10000 ? 0 : 1)}s`;
}

function SectionCombobox({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const trimmed = query.trim();
  const filtered = trimmed
    ? options.filter((o) => o.toLowerCase().includes(trimmed.toLowerCase()))
    : options;
  const exactMatch = options.some(
    (o) => o.toLowerCase() === trimmed.toLowerCase(),
  );
  const canCreate = trimmed.length > 0 && !exactMatch;
  const totalItems = filtered.length + 1;

  useEffect(() => {
    setHighlightIdx(-1);
  }, [query, open]);

  const select = (val) => {
    onChange(val);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      setHighlightIdx((i) => Math.min(i + 1, totalItems - 1));
      e.preventDefault();
    }
    if (e.key === "ArrowUp") {
      setHighlightIdx((i) => Math.max(i - 1, -1));
      e.preventDefault();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIdx === -1) {
        if (canCreate) select(trimmed);
      } else if (highlightIdx < filtered.length) {
        select(filtered[highlightIdx]);
      } else if (canCreate) {
        select(trimmed);
      }
    }
  };

  const displayValue = value || "";

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex items-center w-full px-3 py-2.5 rounded-lg border text-sm cursor-pointer transition-colors ${
          open
            ? "border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-800"
            : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
        }`}
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={displayValue || "Search or type to create\u2026"}
            className="flex-1 bg-transparent outline-none text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            autoFocus
          />
        ) : (
          <span
            className={`flex-1 flex items-center ${displayValue ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}
          >
            <span className="truncate">{displayValue || "None"}</span>
            {displayValue && !options.includes(displayValue) && (
              <span className="ml-auto text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 leading-none">
                new
              </span>
            )}
          </span>
        )}
        <ChevronDown
          size={16}
          className={`ml-2 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          <button
            type="button"
            className={`w-full text-left px-3 py-2 text-sm transition-colors ${
              !value
                ? "bg-gray-100 dark:bg-gray-700 font-medium"
                : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
            } text-gray-900 dark:text-gray-100`}
            onClick={() => select("")}
          >
            None
          </button>

          {filtered.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700">
              {filtered.map((name, idx) => (
                <button
                  key={name}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm transition-colors text-gray-900 dark:text-gray-100 ${
                    highlightIdx === idx
                      ? "bg-gray-100 dark:bg-gray-700"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  } ${value === name ? "font-medium" : ""}`}
                  onClick={() => select(name)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              disabled={!canCreate}
              className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                canCreate
                  ? highlightIdx === filtered.length
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    : "text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
              }`}
              onClick={() => {
                if (canCreate) select(trimmed);
              }}
              onMouseEnter={() => {
                if (canCreate) setHighlightIdx(filtered.length);
              }}
            >
              <Plus size={14} />
              {canCreate ? (
                <span>
                  Create "<strong>{trimmed}</strong>"
                </span>
              ) : (
                <span>Create new section</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

let _columnUidSeq = 0;
const nextColumnUid = () => `col-${++_columnUidSeq}`;
const newColumn = () => ({ _uid: nextColumnUid(), key: "", label: "", type: "text", description: "" });
const EMPTY_VALIDATION_RULES = { min: "", max: "", date_format: "" };

export function FieldModal({
  open,
  onClose,
  onSave,
  categories,
  existingCategoryNames,
  initialField,
  saving,
  isNsTemplate,
  nsFieldCatalog,
  nsFieldCatalogError,
  isBcTemplate,
  bcFieldCatalog,
  bcLineFieldCatalog,
}) {
  const [form, setForm] = useState({
    display_name: "",
    field_name: "",
    field_type: "text",
    category: "",
    description: "",
    is_required: false,
    ns_field: "",
    bc_field: "",
    normalisation_instruction: "",
  });
  const [validationRules, setValidationRules] = useState(EMPTY_VALIDATION_RULES);
  // Each column carries a client-only _uid so expand/collapse and reorder
  // survive edits; it is stripped before saving.
  const [expandedColumns, setExpandedColumns] = useState(() => new Set());
  const [columns, setColumns] = useState(() => [newColumn()]);
  const [dropdownOptions, setDropdownOptions] = useState("");

  useEffect(() => {
    if (!open) return;

    if (initialField) {
      setForm({
        display_name: initialField.display_name || "",
        field_name: initialField.field_name || "",
        field_type: initialField.field_type || "text",
        category:
          initialField.category_display_name || initialField.category || "",
        description: initialField.description || "",
        is_required: !!initialField.is_required,
        ns_field: initialField.ns_field || "",
        bc_field: initialField.bc_field || "",
        normalisation_instruction: initialField.normalisation_instruction || "",
      });
      // Parse validation_rules JSON
      if (initialField.validation_rules) {
        try {
          const parsed =
            typeof initialField.validation_rules === "string"
              ? JSON.parse(initialField.validation_rules)
              : initialField.validation_rules;
          setValidationRules({
            min: parsed.min != null ? String(parsed.min) : "",
            max: parsed.max != null ? String(parsed.max) : "",
            date_format: parsed.date_format || "",
          });
        } catch {
          setValidationRules(EMPTY_VALIDATION_RULES);
        }
      } else {
        setValidationRules(EMPTY_VALIDATION_RULES);
      }
      // Parse dropdown options from field_values for dropdown fields
      if (initialField.field_type === "dropdown" && initialField.field_values) {
        setDropdownOptions(initialField.field_values);
      } else {
        setDropdownOptions("");
      }
      // Parse column schema from field_values for array_of_objects fields
      if (
        initialField.field_type === "array_of_objects" &&
        initialField.field_values
      ) {
        try {
          const parsed =
            typeof initialField.field_values === "string"
              ? JSON.parse(initialField.field_values)
              : initialField.field_values;
          if (Array.isArray(parsed) && parsed.length > 0) {
            const loaded = (
              // Spread first so per-column settings the editor doesn't show
              // (ns_field, posted, required, inherited_from_header) survive an
              // edit; BC-imported columns carry only `name`, so fall back to it.
              parsed.map((c) => ({
                ...c,
                _uid: nextColumnUid(),
                key: c.key || c.name || "",
                label: c.label || c.name || c.key || "",
                type: c.type || "text",
                description: c.description || "",
              }))
            );
            setColumns(loaded);
            // A short list opens fully; a long one starts collapsed so every
            // column is visible at a glance.
            setExpandedColumns(new Set(loaded.length <= 3 ? loaded.map((c) => c._uid) : []));
          } else {
            setColumns([newColumn()]);
          }
        } catch {
          setColumns([newColumn()]);
        }
      } else {
        setColumns([newColumn()]);
      }
    } else {
      setForm({
        display_name: "",
        field_name: "",
        field_type: "text",
        category: "",
        description: "",
        is_required: false,
        ns_field: "",
        bc_field: "",
        normalisation_instruction: "",
      });
      setValidationRules(EMPTY_VALIDATION_RULES);
      setColumns([newColumn()]);
      setDropdownOptions("");
    }
  }, [open, initialField, categories]);

  if (!open) return null;

  const isEdit = !!initialField;
  // Fields filled from the e-mail are added finished from "Add field -> E-mail
  // details"; editing one only renames it or moves it to another section.
  const fromEmail = isEmailSourced(initialField);

  const autoGenerateFieldName = () => {
    if (!form.display_name) return;
    setForm((prev) => ({ ...prev, field_name: slugify(prev.display_name) }));
  };

  const addColumn = () => {
    const col = newColumn();
    setColumns((prev) => [...prev, col]);
    // A new column opens straight away so it can be filled in.
    setExpandedColumns((prev) => new Set(prev).add(col._uid));
  };
  const removeColumn = (idx) =>
    setColumns((prev) => prev.filter((_, i) => i !== idx));
  const moveColumn = (idx, delta) =>
    setColumns((prev) => {
      const to = idx + delta;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  const toggleColumn = (uid) =>
    setExpandedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  const allColumnsExpanded =
    columns.length > 0 && columns.every((c) => expandedColumns.has(c._uid));
  const setAllColumnsExpanded = (open) =>
    setExpandedColumns(open ? new Set(columns.map((c) => c._uid)) : new Set());
  const updateColumn = (idx, field, value) => {
    setColumns((prev) =>
      prev.map((col, i) => (i === idx ? { ...col, [field]: value } : col)),
    );
  };
  const autoColumnKey = (idx) => {
    setColumns((prev) =>
      prev.map((col, i) => {
        // Only fill an EMPTY key. Rewriting an existing key whenever the label
        // changed silently broke anything referencing it, e.g. {{ean}} in a
        // webhook mapping.
        if (i !== idx || !col.label || col.key) return col;
        const key = col.label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_|_$/g, "");
        return { ...col, key };
      }),
    );
  };

  const save = () => {
    if (!form.display_name.trim() || !form.field_name.trim()) return;
    if (fromEmail) {
      onSave({
        display_name: form.display_name.trim(),
        category: form.category.trim() || null,
      });
      return;
    }
    const payload = {
      display_name: form.display_name.trim(),
      field_name: form.field_name.trim(),
      field_type: form.field_type,
      category: form.category.trim() || null,
      description: form.description.trim() || null,
      is_required: form.is_required,
      ns_field: form.ns_field || null,
      bc_field: form.bc_field || null,
    };
    if (form.field_type !== "array_of_objects") {
      payload.normalisation_instruction = form.normalisation_instruction.trim();
    }
    // Build validation_rules JSON based on field type
    const rules = {};
    if (["number", "currency", "percentage"].includes(form.field_type)) {
      if (validationRules.min !== "") rules.min = Number(validationRules.min);
      if (validationRules.max !== "") rules.max = Number(validationRules.max);
    }
    if (form.field_type === "date" && validationRules.date_format) {
      rules.date_format = validationRules.date_format;
    }
    // An empty string clears stored rules: the update route reads null as
    // "leave unchanged", so turning a check off must not send null.
    payload.validation_rules = Object.keys(rules).length > 0 ? JSON.stringify(rules) : "";
    // For dropdown, include options as field_values
    if (form.field_type === "dropdown" && dropdownOptions.trim()) {
      payload.field_values = dropdownOptions.trim();
    }
    // For array_of_objects, include column schema
    if (form.field_type === "array_of_objects") {
      const validCols = columns
        .filter((c) => c.key && c.label)
        .map(({ description, _uid, ...rest }) => ({
          ...rest,
          ...(description ? { description } : {}),
        }));
      if (validCols.length > 0) {
        payload.columns = validCols;
        payload.field_values = JSON.stringify(validCols);
      }
    }
    onSave(payload);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative w-full ${form.field_type === "array_of_objects" ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {isEdit ? "Edit Field" : "Add Field"}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {fromEmail
            ? "This field is copied from the details of the e-mail the request came from."
            : "Keep descriptions clear and specific - they guide the analyzer to extract accurately."}
        </p>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Display name
            </label>
            <input
              value={form.display_name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, display_name: e.target.value }))
              }
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
              placeholder="Coupon Rate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Field name (internal)
            </label>
            <div className="flex gap-2">
              <input
                value={form.field_name}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    field_name: slugify(e.target.value),
                  }))
                }
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono"
                placeholder="coupon_rate"
              />
              <button
                onClick={autoGenerateFieldName}
                type="button"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
              >
                Auto
              </button>
            </div>
          </div>

          {fromEmail && (
            <div className="md:col-span-2 flex items-start gap-2.5 rounded-lg border border-violet-200 dark:border-violet-800/50 bg-violet-50 dark:bg-violet-900/20 px-3 py-2.5">
              <Mail className="h-4 w-4 mt-0.5 text-violet-600 dark:text-violet-400 flex-shrink-0" />
              <p className="text-sm text-violet-900 dark:text-violet-200">
                Source: <span className="font-semibold">{emailFieldLabel(initialField.value_source) || "the e-mail"}</span>.
                Not read from the document, so there's nothing else to set up.
                To use a different detail, add it from <span className="font-semibold">Add field → E-mail details</span>.
              </p>
            </div>
          )}

          {!fromEmail && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Data type
            </label>
            <select
              value={form.field_type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, field_type: e.target.value }))
              }
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
            >
              {DATA_TYPES.map((dt) => (
                <option key={dt.value} value={dt.value}>
                  {dt.label}
                </option>
              ))}
            </select>
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Section
            </label>
            <SectionCombobox
              value={form.category === "__new__" ? "" : form.category}
              onChange={(val) =>
                setForm((prev) => ({
                  ...prev,
                  category: val,
                  _newCategory: "",
                }))
              }
              options={existingCategoryNames}
            />
          </div>
        </div>

        {/* ── NetSuite Field Mapping Dropdown ── */}
        {isNsTemplate && !fromEmail && (
          <div className="mt-4 p-4 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/30">
            <label className="block text-sm font-semibold text-purple-900 dark:text-purple-200 mb-1">
              NetSuite Field Mapping
            </label>
            <p className="text-xs text-purple-600 dark:text-purple-400 mb-3">
              Select which NetSuite Purchase Order field this maps to when
              posting.
            </p>
            {nsFieldCatalogError && (
              <p className="mb-2 text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                {nsFieldCatalogError}
              </p>
            )}
            <NsFieldSearchDropdown
              value={form.ns_field}
              onChange={(val) =>
                setForm((prev) => ({ ...prev, ns_field: val }))
              }
              catalog={nsFieldCatalog}
              loadError={nsFieldCatalogError}
              placeholder="— Not mapped —"
            />
            {form.ns_field && (
              <p className="mt-2 text-xs text-purple-700 dark:text-purple-300 font-mono bg-purple-100 dark:bg-purple-900/40 px-2 py-1 rounded">
                ns_field: {form.ns_field}
                {nsFieldCatalog.some((f) => f.key === form.ns_field) && (
                  <span className="text-purple-500 ml-2">✓ valid</span>
                )}
              </p>
            )}
          </div>
        )}

        {/* ── Business Central Field Mapping Dropdown ── */}
        {isBcTemplate && !fromEmail && (
          <div className="mt-4 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/30">
            <label className="block text-sm font-semibold text-indigo-900 dark:text-indigo-200 mb-1">Business Central Field Mapping</label>
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-3">Select which Business Central Purchase Order field this maps to when posting.</p>
            <BcFieldSearchDropdown
              value={form.bc_field}
              onChange={val => setForm(prev => ({ ...prev, bc_field: val }))}
              catalog={bcFieldCatalog}
              placeholder="— Not mapped —"
            />
            {form.bc_field && (
              <p className="mt-2 text-xs text-indigo-700 dark:text-indigo-300 font-mono bg-indigo-100 dark:bg-indigo-900/40 px-2 py-1 rounded">
                bc_field: {form.bc_field}
                {bcFieldCatalog.some(f => f.key === form.bc_field) && (
                  <span className="text-indigo-500 ml-2">✓ valid</span>
                )}
              </p>
            )}
          </div>
        )}

        {!fromEmail && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Description
          </label>
          <textarea
            rows={form.field_type === "array_of_objects" ? 2 : 4}
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
            placeholder={
              form.field_type === "array_of_objects"
                ? "Example: Extract all line items from the purchase order table including product codes, quantities, and prices"
                : "Example: Annual coupon rate shown in percentage near financial terms section"
            }
          />
        </div>
        )}

        {/* Value Range - for number/currency/percentage fields */}
        {!fromEmail && ["number", "currency", "percentage"].includes(form.field_type) && (
          <div className="mt-4 p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
            <label className="block text-sm font-semibold text-blue-900 dark:text-blue-200">
              {form.field_type === "currency"
                ? "Currency Range"
                : form.field_type === "percentage"
                  ? "Percentage Range"
                  : "Number Range"}
            </label>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 mb-3">
              {form.field_type === "currency"
                ? "Extracted currency values outside this range will be flagged during review"
                : form.field_type === "percentage"
                  ? "Extracted percentages outside this range will be flagged during review (e.g. 0-100)"
                  : "Extracted numbers outside this range will be flagged during review"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Minimum
                </label>
                <input
                  type="number"
                  value={validationRules.min}
                  onChange={(e) =>
                    setValidationRules((prev) => ({
                      ...prev,
                      min: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                  placeholder={
                    form.field_type === "percentage"
                      ? "e.g. 0"
                      : form.field_type === "currency"
                        ? "e.g. 1000"
                        : "e.g. 0"
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Maximum
                </label>
                <input
                  type="number"
                  value={validationRules.max}
                  onChange={(e) =>
                    setValidationRules((prev) => ({
                      ...prev,
                      max: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                  placeholder={
                    form.field_type === "percentage"
                      ? "e.g. 100"
                      : form.field_type === "currency"
                        ? "e.g. 1000000"
                        : "e.g. 100"
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* Normalisation instruction - how the AI should clean up the value */}
        {!fromEmail && form.field_type !== "array_of_objects" && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Normalisation instruction{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              aria-label="Normalisation instruction"
              rows={2}
              value={form.normalisation_instruction}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, normalisation_instruction: e.target.value }))
              }
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
              placeholder="Example: Remove spaces and dots and use capital letters"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              How the AI should clean up the extracted value before review. It
              can use other fields by name, and say when to flag the value: a
              flagged value is marked on the review page and the request waits
              for review.
            </p>
          </div>
        )}

        {/* Date Format - for date fields */}
        {!fromEmail && form.field_type === "date" && (
          <div className="mt-4 p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
            <label className="block text-sm font-semibold text-blue-900 dark:text-blue-200">
              Output Format
            </label>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 mb-3">
              How this date is written when it is saved and sent on. How dates
              are read from documents is the template's Dates on Documents
              setting (Settings tab).
            </p>
            <select
              value={validationRules.date_format}
              onChange={(e) =>
                setValidationRules((prev) => ({
                  ...prev,
                  date_format: e.target.value,
                }))
              }
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
            >
              <option value="">YYYY-MM-DD</option>
              {DATE_FORMATS.map((df) => (
                <option key={df.value} value={df.value}>
                  {df.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dropdown Options - for dropdown fields */}
        {!fromEmail && form.field_type === "dropdown" && (
          <div className="mt-4 p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
            <label className="block text-sm font-semibold text-blue-900 dark:text-blue-200">
              Dropdown Options
            </label>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 mb-3">
              Enter allowed values separated by commas. The AI will pick the
              closest match from this list.
            </p>
            <textarea
              rows={2}
              value={dropdownOptions}
              onChange={(e) => setDropdownOptions(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
              placeholder="e.g. Fixed, Floating, Variable, Zero Coupon"
            />
            {dropdownOptions.trim() && (
              <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                ✓ {dropdownOptions.split(",").filter((o) => o.trim()).length}{" "}
                option
                {dropdownOptions.split(",").filter((o) => o.trim()).length !== 1
                  ? "s"
                  : ""}{" "}
                defined
              </div>
            )}
          </div>
        )}

        {/* Column Schema Builder for Table (array_of_objects) fields.
            Each column is a collapsible card: collapsed it is one line (name,
            key, type, description preview) so every column is visible at once;
            expanded it edits in full, with the description - the instruction
            the AI follows for that column - in a multi-line box. */}
        {form.field_type === "array_of_objects" && (
          <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Table columns
                  </span>
                  <span className="px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                    {columns.length}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {isBcTemplate && bcLineFieldCatalog.length > 0
                    ? "Map each column to a real Business Central purchase order line field."
                    : "Extracted for every line. Each column's description is the instruction the AI follows."}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {columns.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setAllColumnsExpanded(!allColumnsExpanded)}
                    className="text-xs font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {allColumnsExpanded ? "Collapse all" : "Expand all"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={addColumn}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-navy-800 text-white hover:bg-navy-700 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" /> Add column
                </button>
              </div>
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {columns.map((col, idx) => {
                const isOpen =
                  expandedColumns.has(col._uid) || (!col.key && !col.label);
                const description = col.description || "";
                const typeLabel =
                  (COLUMN_TYPES.find((ct) => ct.value === col.type) || {}).label ||
                  col.type || "Text";
                const descriptionRows = Math.min(
                  14,
                  Math.max(
                    3,
                    description
                      .split("\n")
                      .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 90)), 0),
                  ),
                );
                return (
                  <li key={col._uid} className={isOpen ? "bg-white dark:bg-gray-900" : ""}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleColumn(col._uid)}
                        aria-expanded={isOpen}
                        className="flex-1 min-w-0 flex items-center gap-2 text-left"
                      >
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                        )}
                        <span className="shrink-0 max-w-[12rem] truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {col.label || (
                            <span className="italic font-normal text-gray-400">New column</span>
                          )}
                        </span>
                        {col.key && col.key !== col.label && (
                          <span className="shrink-0 max-w-[10rem] truncate font-mono text-[11px] text-gray-400">
                            {col.key}
                          </span>
                        )}
                        <span className="shrink-0 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                          {typeLabel}
                        </span>
                        {!isOpen &&
                          (description.trim() ? (
                            <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
                              {description}
                            </span>
                          ) : (
                            <span className="shrink-0 text-xs text-amber-600 dark:text-amber-400">
                              No description
                            </span>
                          ))}
                      </button>
                      <div className="flex items-center shrink-0 text-gray-400">
                        <button
                          type="button"
                          onClick={() => moveColumn(idx, -1)}
                          disabled={idx === 0}
                          className="p-1 rounded hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Move up"
                          aria-label="Move column up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveColumn(idx, 1)}
                          disabled={idx === columns.length - 1}
                          className="p-1 rounded hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Move down"
                          aria-label="Move column down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeColumn(idx)}
                          disabled={columns.length <= 1}
                          className="p-1 rounded hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Remove column"
                          aria-label="Remove column"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 pl-9 space-y-3">
                        {isBcTemplate && bcLineFieldCatalog.length > 0 && (
                          <select
                            value=""
                            onChange={e => {
                              const key = e.target.value
                              if (!key) return
                              const bcField = bcLineFieldCatalog.find(f => f.key === key)
                              if (!bcField) return
                              setColumns(prev => prev.map((c, i) => i === idx ? {
                                ...c,
                                key: bcField.key,
                                label: bcField.label,
                                description: `Business Central purchase order line field: ${bcField.key}`,
                              } : c))
                            }}
                            className="w-full px-2 py-1 text-[10px] rounded-md border border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-mono"
                            title="Quick-fill this column from a real BC line field"
                          >
                            <option value="">— quick-fill from BC line field —</option>
                            {bcLineFieldCatalog.map(f => (
                              <option key={f.key} value={f.key}>
                                {f.mandatory ? 'Required · ' : ''}{f.key} ({f.label})
                              </option>
                            ))}
                          </select>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_12rem_8rem] gap-3">
                          <label className="block">
                            <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                              Label
                            </span>
                            <input
                              value={col.label}
                              onChange={(e) => updateColumn(idx, "label", e.target.value)}
                              onBlur={() => autoColumnKey(idx)}
                              placeholder="e.g. Unit price"
                              className="w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                            />
                          </label>
                          <label className="block">
                            <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                              Key
                            </span>
                            <input
                              value={col.key}
                              onChange={(e) => updateColumn(idx, "key", e.target.value)}
                              placeholder="unit_price"
                              title={
                                isBcTemplate && bcLineFieldCatalog.some((f) => f.key === col.key)
                                  ? "Matches a real BC line field"
                                  : "Used in mappings, e.g. {{" + (col.key || "key") + "}}"
                              }
                              className={`w-full px-2.5 py-1.5 text-sm rounded-md border bg-white dark:bg-gray-800 font-mono ${
                                isBcTemplate && bcLineFieldCatalog.some((f) => f.key === col.key)
                                  ? "border-indigo-300 dark:border-indigo-700"
                                  : "border-gray-300 dark:border-gray-600"
                              }`}
                            />
                          </label>
                          <label className="block">
                            <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                              Type
                            </span>
                            <select
                              value={col.type}
                              onChange={(e) => updateColumn(idx, "type", e.target.value)}
                              className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                            >
                              {COLUMN_TYPES.map((ct) => (
                                <option key={ct.value} value={ct.value}>
                                  {ct.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {col.type === "date" && (
                          <label className="block">
                            <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                              Output Format
                              <span className="ml-1 font-normal text-gray-400">
                                how the date is saved and sent on; it is read using the
                                template's Dates on Documents setting
                              </span>
                            </span>
                            <select
                              value={col.date_format || ""}
                              onChange={(e) => updateColumn(idx, "date_format", e.target.value)}
                              className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                            >
                              <option value="">YYYY-MM-DD</option>
                              {DATE_FORMATS.filter((df) => df.value !== "YYYY-MM-DD").map((df) => (
                                <option key={df.value} value={df.value}>
                                  {df.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        <label className="block">
                          <span className="flex items-baseline justify-between gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                              Description
                              <span className="ml-1 font-normal text-gray-400">
                                what the AI should extract, and how
                              </span>
                            </span>
                            <span className="text-[10px] text-gray-400 tabular-nums">
                              {description.length} chars
                            </span>
                          </span>
                          <textarea
                            value={description}
                            onChange={(e) => updateColumn(idx, "description", e.target.value)}
                            rows={descriptionRows}
                            placeholder="e.g. The price per single unit ('Prijs', 'Unit Cost'). Return the number only, without a currency symbol."
                            className="w-full px-3 py-2 text-sm leading-relaxed rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 resize-y"
                          />
                        </label>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Require User Input toggle */}
        {!fromEmail && (
        <div className="mt-4 flex items-center gap-3">
          <Toggle
            checked={form.is_required}
            onChange={() =>
              setForm((prev) => ({ ...prev, is_required: !prev.is_required }))
            }
          />
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Require User Input
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When enabled, users must fill in this field in the plugin before
              submitting
            </p>
          </div>
        </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2.5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={
              !form.display_name.trim() || !form.field_name.trim() || saving
            }
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Add Field"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Result of comparing a Map Payload with the template's fields, shown under
// the Map Payload editor. The editor always saves "not sent" marks, so every
// template field must be used or marked: problems here block the save, and an
// unmarked field can be marked "not sent" with one click.
function MappingCheckPanel({ check, excluded, onExclude, onInclude }) {
  if (!check || !check.checked) return null;
  const unmapped = check.errors.filter((e) => e.code === "unmapped_field").map((e) => e.field);
  const broken = check.errors.filter((e) => e.code !== "unmapped_field" && e.code !== "missing_on_request");
  return (
    <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 space-y-2">
      {check.status === "ok" && (
        <p className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
          Matches the template — every field is sent or marked "not sent".
        </p>
      )}
      {broken.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
            Doesn't match the template
          </p>
          {broken.map((e) => (
            <p key={`${e.code}:${e.field}`} className="flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{e.message}</span>
            </p>
          ))}
        </div>
      )}
      {unmapped.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              Not sent and not marked ({unmapped.length})
            </p>
            {unmapped.length > 1 && (
              <button
                type="button"
                onClick={() => onExclude(unmapped)}
                className="text-[11px] font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
              >
                Mark all "not sent"
              </button>
            )}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">
            Map each one in the JSON above, or mark it as not sent to this connector.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {unmapped.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => onExclude([key])}
                title={`Mark "${key}" as not sent to this connector`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-[11px] font-mono text-amber-800 dark:text-amber-300 hover:bg-amber-100"
              >
                {key}
                <span className="font-sans text-[10px] opacity-70">· not sent</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {excluded.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Not sent to this connector ({excluded.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {excluded.map((key) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-[11px] font-mono text-gray-600 dark:text-gray-300"
              >
                {key}
                <button
                  type="button"
                  onClick={() => onInclude(key)}
                  title="Send this field again"
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const features = useFeatures();

  const [template, setTemplate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [addFieldMenuOpen, setAddFieldMenuOpen] = useState(false);
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [addingEmailFields, setAddingEmailFields] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [savingField, setSavingField] = useState(false);
  const [buildingAnalyzer, setBuildingAnalyzer] = useState(false);

  // AI Suggest state
  const [showAiSuggest, setShowAiSuggest] = useState(false);
  const [aiFiles, setAiFiles] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { fields, document_type, ... }
  const [aiSelected, setAiSelected] = useState({}); // field_name -> true/false
  const [aiSaving, setAiSaving] = useState(false);
  const [aiJobId, setAiJobId] = useState(null); // async job tracking
  const [aiJobProgress, setAiJobProgress] = useState(null); // { percent, message }
  // Tracks which async_jobs row's result is currently loaded into aiResult,
  // independent of aiJobId (which is cleared once the job finishes
  // polling). Used to mark the job "consumed" (accepted/partially accepted/
  // dismissed) so the suggestion panel does not reappear on template reopen.
  const [aiCurrentJobId, setAiCurrentJobId] = useState(null);
  const aiPollRef = useRef(null);
  // Sent with the prompt: return only the fields the prompt names, and how
  // much to suggest when it doesn't.
  const [aiRestrict, setAiRestrict] = useState(false);
  const [aiDetailLevel, setAiDetailLevel] = useState("standard");
  const [aiApplyingAnswers, setAiApplyingAnswers] = useState(false);
  const [aiExpanded, setAiExpanded] = useState({}); // field_name -> table columns shown
  const [aiShowOptional, setAiShowOptional] = useState(false);
  // The suggestion a running re-run answers; restored if the re-run fails.
  const aiRefineParentRef = useRef(null);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [exportingJson, setExportingJson] = useState(false);

  // Dynamic NS field catalog fetched from live NetSuite metadata
  const [nsFieldCatalog, setNsFieldCatalog] = useState([]);
  const [nsFieldCatalogError, setNsFieldCatalogError] = useState('');
  const nsFieldCatalogFetched = useRef(false);

  // Dynamic BC field catalog fetched from live Business Central $metadata
  const [bcFieldCatalog, setBcFieldCatalog] = useState([])
  // Each holds the id of the template it last loaded for (not true/false), so moving to
  // another template in the same page loads that template's catalog instead of keeping
  // the previous one.
  const bcFieldCatalogFetched = useRef(null)

  // BC entity types (Purchase Order, Purchase Invoice, ...), fetched once so the
  // Fields-tab upload panel can say which document type it's actually building
  // a template for, instead of a generic "Business Central PDF" for every type.
  const [bcEntityTypes, setBcEntityTypes] = useState([])
  const bcEntityTypesFetched = useRef(null)

  // Registry field catalog for THIS template's BC entity type, keyed by field name.
  // A field that becomes a document line legitimately has no bc_field, which the
  // BC Column dropdown would otherwise render as "— None —" — indistinguishable
  // from an unmapped field. This supplies the difference.
  const [bcEntityCatalog, setBcEntityCatalog] = useState({})
  const bcEntityKey = template?.bc_entity_type || template?.template_bc_entity_type || null

  useEffect(() => {
    // The backend reports 'purchase_order' for every template, so without this every
    // NetSuite and plain template page fetched a Business Central catalog (a 404 when
    // the integration is off).
    const isBcTpl = !!(template?.bc_enabled || template?.template_bc_enabled ||
      template?.template_creation_method === 'bc_purchase_order')
    if (!bcEntityKey || !features.business_central || !isBcTpl) { setBcEntityCatalog({}); return }
    let cancelled = false
    bcApi.getEntityFieldCatalog(bcEntityKey)
      .then(res => {
        if (cancelled) return
        const byName = {}
        for (const entry of (res.data?.catalog || [])) byName[entry.field_name] = entry
        setBcEntityCatalog(byName)
      })
      .catch(() => { if (!cancelled) setBcEntityCatalog({}) })
    return () => { cancelled = true }
  }, [bcEntityKey, features.business_central, template?.bc_enabled, template?.template_bc_enabled, template?.template_creation_method])

  // Dynamic BC *line* field catalog (purchaseOrderLine entity) — drives the
  // line-item column mapping UI with BC's real field names instead of
  // hand-picked guesses.
  const [bcLineFieldCatalog, setBcLineFieldCatalog] = useState([])
  const bcLineFieldCatalogFetched = useRef(null)

  // In-flight "User Input" / "Required" toggle values (field_id -> bool). These
  // toggles save immediately: neither property is part of the Content
  // Understanding analyzer, so they never need Save / an analyzer rebuild.
  // The entry only exists while the request is running (optimistic UI).
  const [pendingRequired, setPendingRequired] = useState({});
  const [savingFieldToggles, setSavingFieldToggles] = useState(false);
  const hasPendingRequired = Object.keys(pendingRequired).length > 0;

  // Track local extraction-is-required (mandatory) toggle overrides
  const [pendingMandatory, setPendingMandatory] = useState({});
  const hasPendingMandatory = Object.keys(pendingMandatory).length > 0;

  const [savingAiLearningToggle, setSavingAiLearningToggle] = useState(false);

  // Staged (unsaved) edits for the template-level Settings tab toggles/select.
  // Nothing here is sent to the server until the user clicks "Save Changes".
  const [pendingSettings, setPendingSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [learningRules, setLearningRules] = useState([]);
  const [loadingLearningRules, setLoadingLearningRules] = useState(false);
  const [learningRulesError, setLearningRulesError] = useState("");
  const connectorsEnabled = features.connectors;

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    return [
      "fields",
      "webhooks",
      "settings",
      "learning",
      "retention",
      "bc",
      "ns",
      "activity",
    ].includes(tab)
      ? tab
      : "fields";
  });

  // Retention state
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionBulkDays, setRetentionBulkDays] = useState(365);
  const [retentionBulkSaving, setRetentionBulkSaving] = useState(false);
  const [retentionRunNowSaving, setRetentionRunNowSaving] = useState(false);
  // Which half of the Business Central tab is showing. Defaults to the
  // dashboard: "did my documents post?" is the daily question, while the sync
  // and connection settings are configured once. ?bcTab=settings still lands
  // straight on configuration, so the post-creation link can point there.
  const [bcSubTab, setBcSubTab] = useState(() => {
    const sub = new URLSearchParams(window.location.search).get('bcTab')
    return sub === 'settings' ? 'settings' : 'dashboard'
  })

  // Same reasoning as bcSubTab: "did my documents post?" is the daily
  // question, sync/mapping settings are configured once. ?nsTab=settings
  // still lands on configuration, so a post-creation link can point there.
  const [nsSubTab, setNsSubTab] = useState(() => {
    const sub = new URLSearchParams(window.location.search).get('nsTab')
    return sub === 'settings' || sub === 'mappings' ? sub : 'dashboard'
  })

  const [retentionSubTab, setRetentionSubTab] = useState("policies");
  const [retentionLocalEdits, setRetentionLocalEdits] = useState({});
  const [retentionShowConfirm, setRetentionShowConfirm] = useState(false);
  const [retentionConfirmImpact, setRetentionConfirmImpact] = useState({});
  const [retentionImpactLoading, setRetentionImpactLoading] = useState(false);
  const [storageDisclosureOpen, setStorageDisclosureOpen] = useState(false);

  // Template-webhook linking state
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingWebhooks, setLoadingWebhooks] = useState(true);
  const [allOrgWebhooks, setAllOrgWebhooks] = useState([]);
  const [linkedWebhooks, setLinkedWebhooks] = useState([]);
  const [selectedWebhookId, setSelectedWebhookId] = useState("");
  const [webhookDeliveries, setWebhookDeliveries] = useState({});
  const [expandedWebhookId, setExpandedWebhookId] = useState(null);
  const [expandedDeliveryId, setExpandedDeliveryId] = useState(null);
  const [deliveryDetails, setDeliveryDetails] = useState({});
  const [loadingDeliveryDetailsId, setLoadingDeliveryDetailsId] =
    useState(null);
  const [linkingWebhookId, setLinkingWebhookId] = useState(null);
  const [unlinkingWebhookId, setUnlinkingWebhookId] = useState(null);
  // Webhook pending a disconnect confirmation (object with at least { id, name, url }) or null
  const [webhookPendingDisconnect, setWebhookPendingDisconnect] =
    useState(null);
  const [retryingDeliveryId, setRetryingDeliveryId] = useState(null);
  const [loadingDeliveriesId, setLoadingDeliveriesId] = useState(null);
  // Payload mapping modal state
  const [mappingModalWebhookId, setMappingModalWebhookId] = useState(null);
  const [savingMappingId, setSavingMappingId] = useState(null);
  const [mappingFormat, setMappingFormat] = useState("json");
  // Boomi structured-validation state, consolidated into the mapping modal
  // (this replaced a standalone Boomi tab). Only exercised when the template
  // has template_boomi_enabled set.
  const [boomiValidating, setBoomiValidating] = useState(false);
  const [boomiValidationResult, setBoomiValidationResult] = useState(null);
  const [boomiValidateBeforePosting, setBoomiValidateBeforePosting] = useState(true);
  const [boomiBlockOnErrors, setBoomiBlockOnErrors] = useState(true);
  const [boomiTogglesDirty, setBoomiTogglesDirty] = useState(false);
  const [boomiTogglesSaving, setBoomiTogglesSaving] = useState(false);
  const [jsonEditorValue, setJsonEditorValue] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [clearMappingPending, setClearMappingPending] = useState(false);
  const [mappingFieldSearch, setMappingFieldSearch] = useState("");
  // Template fields (and table columns) deliberately not sent to this
  // connector. Saved inside the mapping as excluded_fields; every template
  // field must be either used in the Map Payload or listed here.
  const [mappingExcluded, setMappingExcluded] = useState([]);
  // The server's verdict when it rejected a save (422) — shown until the
  // next edit.
  const [mappingServerCheck, setMappingServerCheck] = useState(null);
  const mappingLiveCheck = useMemo(() => {
    if (!mappingModalWebhookId) return null;
    let parsed;
    try {
      parsed = JSON.parse(jsonEditorValue);
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return checkMapping(
      { type: "json_template", template: parsed, excluded_fields: mappingExcluded },
      template?.fields || [],
    );
  }, [mappingModalWebhookId, jsonEditorValue, mappingExcluded, template?.fields]);
  // Custom headers modal state
  const [headersModalWebhookId, setHeadersModalWebhookId] = useState(null);
  const [headerRows, setHeaderRows] = useState([]);
  const [savingHeadersId, setSavingHeadersId] = useState(null);
  const [headersFieldSearch, setHeadersFieldSearch] = useState("");

  // Output destinations (FTP/SFTP) linking state (AC4/AC5)
  const [loadingOutputDestinations, setLoadingOutputDestinations] =
    useState(true);
  const [allOrgOutputDestinations, setAllOrgOutputDestinations] = useState([]);
  const [linkedOutputDestinations, setLinkedOutputDestinations] = useState([]);
  const [selectedOutputDestinationId, setSelectedOutputDestinationId] =
    useState("");
  const [linkingOutputDestinationId, setLinkingOutputDestinationId] =
    useState(null);
  const [unlinkingOutputDestinationId, setUnlinkingOutputDestinationId] =
    useState(null);
  const [outputDestinationPendingDisconnect, setOutputDestinationPendingDisconnect] =
    useState(null);
  // Field mapping modal state (mirrors mappingModalWebhookId pattern)
  const [expandedOutputDestinationId, setExpandedOutputDestinationId] =
    useState(null);
  const [mappingModalDestinationId, setMappingModalDestinationId] =
    useState(null);
  const [savingOutputMappingId, setSavingOutputMappingId] = useState(null);
  const [outputFieldMappingRows, setOutputFieldMappingRows] = useState([]);
  const [outputFilenamePattern, setOutputFilenamePattern] = useState("");
  const [outputMappingError, setOutputMappingError] = useState(null);

  // Track whether fields have changed since last analyzer build
  const [fieldsDirty, setFieldsDirty] = useState(false);
  // Only analyzer-relevant edits (name, type, description, table columns,
  // adding/removing fields) need Save; row toggles save on their own.
  const hasUnsavedFieldChanges = fieldsDirty;

  const saveFieldToggle = async (field, prop, setPending) => {
    const current = !!field[prop];
    const next = !current;
    setPending((prev) => ({ ...prev, [field.id]: next }));
    try {
      await templatesApi.updateField(id, field.id, { [prop]: next });
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              fields: (prev.fields || []).map((f) =>
                f.id === field.id ? { ...f, [prop]: next } : f,
              ),
            }
          : prev,
      );
    } catch (e) {
      toast.error(
        "Couldn't save",
        e?.response?.data?.error || "The change wasn't saved. Please try again.",
      );
    } finally {
      setPending((prev) => {
        const rest = { ...prev };
        delete rest[field.id];
        return rest;
      });
    }
  };

  // BC schema drift: new columns available in BC that aren't in this template
  const [bcDrift, setBcDrift] = useState(null); // { has_drift, new_columns, bc_available, checked_at } | null
  const [bcDriftLoading, setBcDriftLoading] = useState(false);
  const [bcDriftApplying, setBcDriftApplying] = useState(false);

  // On template load, check if any field was modified after the analyzer was last built
  useEffect(() => {
    if (!template) return;
    const analyzerUpdatedAt = template.analyzer?.updated_at;
    if (!analyzerUpdatedAt) {
      // No analyzer built yet - if there are fields, they need a build
      if ((template.fields || []).length > 0) setFieldsDirty(true);
      else setFieldsDirty(false);
      return;
    }
    const analyzerTime = new Date(analyzerUpdatedAt).getTime();
    const needsRebuild = (template.fields || []).some((f) => {
      if (!f.updated_at) return false;
      return new Date(f.updated_at).getTime() > analyzerTime;
    });
    setFieldsDirty(needsRebuild);
  }, [template]);

  // BC schema drift check - runs when the template loads and is a bc_purchase_order template
  useEffect(() => {
    if (!template) return;
    if (template.creation_method !== "bc_purchase_order") return;
    if (!template.bc_enabled) return;
    if (!features.business_central) return;
    let cancelled = false;
    setBcDriftLoading(true);
    bcApi
      .checkSchemaDrift(template.id || id)
      .then((res) => {
        if (!cancelled) setBcDrift(res.data);
      })
      .catch(() => {
        // Silent - drift check is non-critical
      })
      .finally(() => {
        if (!cancelled) setBcDriftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    template?.id,
    template?.creation_method,
    template?.bc_enabled,
    features.business_central,
  ]);

  // Generic confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, confirmLabel, variant, onConfirm }
  const showConfirm = (opts) =>
    new Promise((resolve) => {
      setConfirmDialog({
        ...opts,
        onConfirm: () => {
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmDialog(null);
          resolve(false);
        },
      });
    });

  // Text describing which connectors' Map Payloads a proposed field list would
  // newly break, or "" when none. Appended to the confirmation for a field
  // change so the user decides knowing which connectors stop publishing.
  const mappingImpactNote = (nextFields) => {
    const impact = mappingImpact(linkedWebhooks, template?.fields || [], nextFields);
    if (!impact.length) return "";
    const lines = impact.map((i) => {
      const shown = i.errors.slice(0, 3).map((e) => e.message).join("; ");
      const more = i.errors.length > 3 ? ` (+${i.errors.length - 3} more)` : "";
      return `• ${i.name}: ${shown}${more}`;
    });
    return (
      `\n\nThis breaks the Map Payload of ${impact.length === 1 ? "a connector" : `${impact.length} connectors`}:\n` +
      lines.join("\n") +
      `\n\nPublishing to ${impact.length === 1 ? "it" : "them"} will be blocked until the Map Payload is updated on the Connectors tab.`
    );
  };

  // Auth and template assignment state
  const { user: authUser } = useAuth();
  const userRole = authUser?.role || "user";
  const canEditMeta = userRole === "admin" || userRole === "template_manager";
  const isReader = isReadOnlyUser(authUser);

  // Inline name/description edit state
  const [editingMeta, setEditingMeta] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  const openMetaEdit = () => {
    setEditName(template?.name || "");
    setEditDescription(template?.description || "");
    setEditingMeta(true);
  };

  const cancelMetaEdit = () => setEditingMeta(false);

  const saveMetaEdit = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) return;
    try {
      setSavingMeta(true);
      await templatesApi.update(id, {
        name: trimmedName,
        description: editDescription.trim() || null,
      });
      setTemplate((prev) => ({
        ...prev,
        name: trimmedName,
        description: editDescription.trim() || null,
      }));
      setEditingMeta(false);
      toast.success("Template updated", "Name and description saved.");
    } catch (e) {
      toast.error(
        "Save failed",
        e?.response?.data?.error || "Could not update template",
      );
    } finally {
      setSavingMeta(false);
    }
  };

  const [assignModal, setAssignModal] = useState({ open: false, type: null }); // type: 'manager' | 'approver'
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loadingAssignable, setLoadingAssignable] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);

  // Custom navigation guard (useBlocker only works with createBrowserRouter, not BrowserRouter)
  const [navBlocked, setNavBlocked] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const location = useLocation();

  // Intercept all <a> clicks when fields are dirty (sidebar, back link, etc.)
  useEffect(() => {
    if (!hasUnsavedFieldChanges) return;

    const handleClick = (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (href && href.startsWith("/") && href !== location.pathname) {
        e.preventDefault();
        e.stopPropagation();
        setPendingNavPath(href);
        setNavBlocked(true);
      }
    };

    document.addEventListener("click", handleClick, true); // capture phase
    return () => document.removeEventListener("click", handleClick, true);
  }, [hasUnsavedFieldChanges, location.pathname]);

  const navigateWithDirtyGuard = (path) => {
    if (hasUnsavedFieldChanges && path !== location.pathname) {
      setPendingNavPath(path);
      setNavBlocked(true);
      return;
    }
    navigate(path);
  };

  // Which connectors each field doesn't reach, so the Fields tab shows the
  // same "not sent" differences as each connector's Map Payload.
  const fieldNotes = useMemo(
    () => fieldDeliveryNotes(linkedWebhooks, template?.fields || []),
    [linkedWebhooks, template?.fields],
  );

  const groupedFields = useMemo(() => {
    const fields = template?.fields || [];
    const map = {};
    fields.forEach((f) => {
      const key = f.category || "other";
      const displayName = f.category_display_name || "Other";
      if (!map[key]) {
        map[key] = { displayName, fields: [] };
      }
      map[key].fields.push(f);
    });
    return map;
  }, [template]);

  const templateFieldById = useMemo(() => {
    const map = {};
    (template?.fields || []).forEach((f) => {
      if (f?.id != null) map[String(f.id)] = f;
    });
    return map;
  }, [template]);

  // A header field with no NS Mapping of its own isn't necessarily
  // unmapped — it may exist only to be read once and copied onto every
  // generated line (e.g. Pricing ID, Valid Period, Reseller on a vendor-bid
  // template). Build a lookup so the header row can show where that value
  // actually posts instead of a misleading "— None —".
  const headerFieldsPostedViaLine = useMemo(() => {
    const map = {};
    (template?.fields || []).forEach((f) => {
      if (f.field_type !== "array_of_objects") return;
      let cols;
      try {
        const raw =
          typeof f.field_values === "string"
            ? JSON.parse(f.field_values)
            : f.field_values;
        cols = Array.isArray(raw) ? raw : null;
      } catch {
        cols = null;
      }
      (cols || []).forEach((col) => {
        if (col.inherited_from_header && col.ns_field) {
          map[col.inherited_from_header] = col.ns_field;
        }
      });
    });
    return map;
  }, [template]);

  // Keep the field selection in sync with what actually exists (drop stale ids)
  useEffect(() => {
    const validIds = new Set((template?.fields || []).map((f) => f.id));
    setSelectedFieldIds((prev) => {
      const next = prev.filter((fid) => validIds.has(fid));
      return next.length === prev.length ? prev : next;
    });
  }, [template]);

  // Clear selection when leaving the Fields tab
  useEffect(() => {
    if (activeTab !== "fields") setSelectedFieldIds([]);
  }, [activeTab]);

  // Point 5: Collect unique category names used in this template's fields for autocomplete
  const existingCategoryNames = useMemo(() => {
    const names = new Set();
    (template?.fields || []).forEach((f) => {
      const name = f.category_display_name || f.category;
      if (name && name !== "other" && name !== "Other") names.add(name);
    });
    return [...names].sort();
  }, [template]);

  const templateCategories = useMemo(() => {
    const usedCategoryIds = new Set(
      (template?.fields || []).map((f) => f.category_id).filter(Boolean),
    );
    // If no fields have categories yet, show all global categories
    if (usedCategoryIds.size === 0) return categories;
    return categories.filter((c) => usedCategoryIds.has(c.id));
  }, [template, categories]);

  const isAdmin = currentUser?.role === "admin";
  const isManager =
    currentUser?.role === "admin" || currentUser?.role === "template_manager";
  const toastError = toast.error;

  const availableWebhooks = useMemo(() => {
    const linkedIds = new Set((linkedWebhooks || []).map((w) => String(w.id)));
    return (allOrgWebhooks || []).filter((w) => !linkedIds.has(String(w.id)));
  }, [allOrgWebhooks, linkedWebhooks]);

  const loadTemplateWebhooks = useCallback(async () => {
    try {
      setLoadingWebhooks(true);
      const linkedPromise = templatesApi.listLinkedWebhooks(id);

      // Avoid calling /auth/me on every refresh; fetch once and cache role.
      let role = currentUser?.role;
      if (!role) {
        const meRes = await authApi.getCurrentUser();
        const me = meRes.data || null;
        setCurrentUser(me);
        role = me?.role;
      }

      const [linkedRes, allRes] = await Promise.all([
        linkedPromise,
        role === "admin" ? api.get("/webhooks") : Promise.resolve(null),
      ]);

      setLinkedWebhooks(linkedRes.data?.webhooks || []);
      setAllOrgWebhooks(allRes?.data?.webhooks || []);
      setSelectedWebhookId("");
    } catch (e) {
      console.error(e);
      toastError(
        "Webhook load failed",
        e?.response?.data?.error || "Unable to load template webhook links",
      );
    } finally {
      setLoadingWebhooks(false);
    }
  }, [id, currentUser?.role, toastError]);

  const loadDeliveries = useCallback(
    async (webhookId) => {
      try {
        setLoadingDeliveriesId(webhookId);
        const res = await api.get(`/webhooks/${webhookId}/deliveries`, {
          params: { limit: 20, template_id: id },
        });
        setWebhookDeliveries((prev) => ({
          ...prev,
          [webhookId]: res.data?.deliveries || [],
        }));
      } catch (e) {
        console.error(e);
        toast.error(
          "Delivery log load failed",
          e?.response?.data?.error || "Unable to load webhook delivery logs",
        );
      } finally {
        setLoadingDeliveriesId(null);
      }
    },
    [toast, id],
  );

  const handleLinkWebhook = async () => {
    if (!selectedWebhookId) return;
    try {
      setLinkingWebhookId(selectedWebhookId);
      const linked = await templatesApi.linkWebhook(id, selectedWebhookId);
      if (linked?.data?.requires_payload_mapping) {
        toast.warning(
          "Map Payload needed",
          "This connector requires a Map Payload. Open Map Payload on it before approving requests, or nothing will be sent.",
        );
      } else {
        toast.success("Webhook linked", "Webhook connected to this template.");
      }
      await loadTemplateWebhooks();
    } catch (e) {
      console.error(e);
      toast.error(
        "Link failed",
        e?.response?.data?.error || "Unable to link webhook to template",
      );
    } finally {
      setLinkingWebhookId(null);
    }
  };

  const handleUnlinkWebhook = async (webhookId) => {
    try {
      setUnlinkingWebhookId(webhookId);
      await templatesApi.unlinkWebhook(id, webhookId);
      toast.success(
        "Webhook disconnected",
        "Webhook removed from this template.",
      );
      setWebhookDeliveries((prev) => {
        const next = { ...prev };
        delete next[webhookId];
        return next;
      });
      if (expandedWebhookId === webhookId) {
        setExpandedWebhookId(null);
      }
      if (expandedDeliveryId) {
        setExpandedDeliveryId(null);
      }
      await loadTemplateWebhooks();
    } catch (e) {
      console.error(e);
      toast.error(
        "Unlink failed",
        e?.response?.data?.error || "Unable to remove webhook link",
      );
    } finally {
      setUnlinkingWebhookId(null);
    }
  };

  const handleToggleDeliveries = async (webhookId) => {
    if (expandedWebhookId === webhookId) {
      setExpandedWebhookId(null);
      return;
    }
    setExpandedWebhookId(webhookId);
    if (!webhookDeliveries[webhookId]) {
      await loadDeliveries(webhookId);
    }
  };

  // ── Output Destinations (FTP/SFTP) template linking (AC4/AC5) ──
  const availableOutputDestinations = useMemo(() => {
    const linkedIds = new Set(
      (linkedOutputDestinations || []).map((d) => String(d.id)),
    );
    return (allOrgOutputDestinations || []).filter(
      (d) => !linkedIds.has(String(d.id)),
    );
  }, [allOrgOutputDestinations, linkedOutputDestinations]);

  const loadTemplateOutputDestinations = useCallback(async () => {
    try {
      setLoadingOutputDestinations(true);
      const linkedPromise = outputDestinationsApi.listForTemplate(id);

      let role = currentUser?.role;
      if (!role) {
        const meRes = await authApi.getCurrentUser();
        const me = meRes.data || null;
        setCurrentUser(me);
        role = me?.role;
      }

      const [linkedRes, allRes] = await Promise.all([
        linkedPromise,
        role === "admin"
          ? outputDestinationsApi.list()
          : Promise.resolve(null),
      ]);

      setLinkedOutputDestinations(linkedRes.data?.destinations || []);
      setAllOrgOutputDestinations(allRes?.data?.destinations || []);
      setSelectedOutputDestinationId("");
    } catch (e) {
      console.error(e);
      toastError(
        "Output destination load failed",
        e?.response?.data?.error ||
          "Unable to load template output destination links",
      );
    } finally {
      setLoadingOutputDestinations(false);
    }
  }, [id, currentUser?.role, toastError]);

  const handleLinkOutputDestination = async () => {
    if (!selectedOutputDestinationId) return;
    try {
      setLinkingOutputDestinationId(selectedOutputDestinationId);
      await outputDestinationsApi.linkToTemplate(
        selectedOutputDestinationId,
        id,
      );
      toast.success(
        "Output destination linked",
        "Destination connected to this template.",
      );
      await loadTemplateOutputDestinations();
    } catch (e) {
      console.error(e);
      toast.error(
        "Link failed",
        e?.response?.data?.error || "Unable to link output destination",
      );
    } finally {
      setLinkingOutputDestinationId(null);
    }
  };

  const handleUnlinkOutputDestination = async (destinationId) => {
    try {
      setUnlinkingOutputDestinationId(destinationId);
      await outputDestinationsApi.unlinkFromTemplate(destinationId, id);
      toast.success(
        "Output destination disconnected",
        "Destination removed from this template.",
      );
      await loadTemplateOutputDestinations();
    } catch (e) {
      console.error(e);
      toast.error(
        "Unlink failed",
        e?.response?.data?.error || "Unable to remove output destination link",
      );
    } finally {
      setUnlinkingOutputDestinationId(null);
    }
  };

  const handleSaveOutputMapping = async () => {
    const rows = outputFieldMappingRows.filter(
      (r) => r.xtract_field && r.csv_column.trim(),
    );
    if (rows.length === 0) {
      setOutputMappingError(
        "Add at least one field mapping row (pick a field and a CSV column name).",
      );
      return;
    }
    const seen = new Set();
    for (const r of rows) {
      const key = r.csv_column.trim().toLowerCase();
      if (seen.has(key)) {
        setOutputMappingError(`Duplicate CSV column '${r.csv_column}'`);
        return;
      }
      seen.add(key);
    }
    setOutputMappingError(null);
    const field_mapping = rows.map((r, idx) => ({
      xtract_field: r.xtract_field,
      csv_column: r.csv_column.trim(),
      order: idx,
    }));
    const filename_pattern = outputFilenamePattern.trim() || undefined;

    setSavingOutputMappingId(mappingModalDestinationId);
    try {
      const existingLink = linkedOutputDestinations.find(
        (d) => d.id === mappingModalDestinationId,
      );
      if (existingLink) {
        await outputDestinationsApi.updateTemplateMapping(
          mappingModalDestinationId,
          id,
          { field_mapping, filename_pattern },
        );
      } else {
        await outputDestinationsApi.linkToTemplate(
          mappingModalDestinationId,
          id,
          { field_mapping, filename_pattern },
        );
      }
      toast.success(
        "Mapping Saved",
        "Field mapping and filename pattern have been updated.",
      );
      setMappingModalDestinationId(null);
      await loadTemplateOutputDestinations();
    } catch (e) {
      console.error(e);
      setOutputMappingError(
        e?.response?.data?.error ||
          "Could not save the field mapping. Please try again.",
      );
    } finally {
      setSavingOutputMappingId(null);
    }
  };

  // Pre-populate the field-mapping modal when opened, from the currently
  // linked destination's saved field_mapping/filename_pattern (if any).
  // If there is no saved mapping yet, default to ALL template fields
  // (csv_column = sanitized field name) instead of a single blank row, so
  // the user prunes/renames rather than manually adding one row per field.
  const sanitizeCsvColumnName = (name) =>
    (name || "").trim().replace(/\s+/g, "_");

  // A Table (Line Items) field declares its sub-columns as a JSON schema in
  // field_values. Two shapes exist in the wild - {key,label,...} written by
  // this page's field editor, and {name,...} written by BC template import -
  // so accept either.
  const parseTableColumns = (field) => {
    if (!field?.field_values) return [];
    try {
      const parsed =
        typeof field.field_values === "string"
          ? JSON.parse(field.field_values)
          : field.field_values;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((c) => ({
          key: (c.key || c.name || "").trim(),
          label: (c.label || c.name || c.key || "").trim(),
        }))
        .filter((c) => c.key);
    } catch {
      return [];
    }
  };

  const isTableField = (f) =>
    f?.field_type === "array_of_objects" || f?.data_type === "array_of_objects";

  // Options offered in the mapping dropdown. A table field is expanded into
  // its sub-columns as dotted paths (line_items.code), because mapping the
  // whole table to one column produces a single CSV cell containing the
  // entire table as JSON - which no CSV importer can consume. A table with
  // no declared column schema is still offered whole, clearly labelled, since
  // we have nothing better to offer for it.
  const outputMappableOptions = useMemo(() => {
    const options = [];
    (template?.fields || []).forEach((f) => {
      if (!isTableField(f)) {
        options.push({ value: f.field_name, label: f.field_name, isLine: false });
        return;
      }
      const cols = parseTableColumns(f);
      if (cols.length === 0) {
        options.push({
          value: f.field_name,
          label: `${f.field_name} (whole table as JSON — not importable)`,
          isLine: false,
          isWholeTable: true,
        });
        return;
      }
      cols.forEach((c) => {
        options.push({
          value: `${f.field_name}.${c.key}`,
          label: `${f.field_name} › ${c.label || c.key}`,
          isLine: true,
        });
      });
    });
    return options;
  }, [template?.fields]);

  // True once the mapping addresses a table's sub-columns: the export then
  // emits one CSV row per line item instead of one row per request.
  const outputMappingIsMultiRow = useMemo(
    () => outputFieldMappingRows.some((r) => (r.xtract_field || "").includes(".")),
    [outputFieldMappingRows],
  );

  const getDefaultOutputFieldMappingRows = () => {
    const fields = template?.fields || [];
    const rows = [];
    fields.filter((f) => !isTableField(f)).forEach((f) => {
      rows.push({
        xtract_field: f.field_name,
        csv_column: sanitizeCsvColumnName(f.field_name),
      });
    });
    // Only the first table field is expanded: the backend rejects a mapping
    // spanning two tables (it would imply a cartesian product of their rows).
    const firstTable = fields.find(isTableField);
    if (firstTable) {
      parseTableColumns(firstTable).forEach((c) => {
        rows.push({
          xtract_field: `${firstTable.field_name}.${c.key}`,
          csv_column: sanitizeCsvColumnName(c.label || c.key),
        });
      });
    }
    return rows;
  };

  useEffect(() => {
    if (!mappingModalDestinationId) return;
    const dest = linkedOutputDestinations.find(
      (d) => d.id === mappingModalDestinationId,
    );
    const existingMapping = dest?.field_mapping;
    if (Array.isArray(existingMapping) && existingMapping.length > 0) {
      setOutputFieldMappingRows(
        [...existingMapping]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((m) => ({
            xtract_field: m.xtract_field,
            csv_column: m.csv_column,
          })),
      );
    } else {
      const defaults = getDefaultOutputFieldMappingRows();
      setOutputFieldMappingRows(
        defaults.length > 0
          ? defaults
          : [{ xtract_field: "", csv_column: "" }],
      );
    }
    setOutputFilenamePattern(
      dest?.filename_pattern || "Xtract_{RequestId}_{Timestamp}.csv",
    );
    setOutputMappingError(null);
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMappingModalDestinationId(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mappingModalDestinationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResetOutputFieldMappingToDefaults = () => {
    const defaults = getDefaultOutputFieldMappingRows();
    setOutputFieldMappingRows(
      defaults.length > 0 ? defaults : [{ xtract_field: "", csv_column: "" }],
    );
    setOutputMappingError(null);
  };

  // Insert a {Token} into the filename pattern input at the current cursor
  // position (falls back to appending at the end if the ref isn't ready).
  const outputFilenamePatternInputRef = useRef(null);
  const insertOutputFilenamePatternToken = (token) => {
    const el = outputFilenamePatternInputRef.current;
    const insertion = `{${token}}`;
    if (!el) {
      setOutputFilenamePattern((prev) => `${prev}${insertion}`);
      return;
    }
    const start = el.selectionStart ?? outputFilenamePattern.length;
    const end = el.selectionEnd ?? outputFilenamePattern.length;
    const next =
      outputFilenamePattern.slice(0, start) +
      insertion +
      outputFilenamePattern.slice(end);
    setOutputFilenamePattern(next);
    // Restore focus + cursor position after the inserted token, on the next tick
    // (state update re-renders the input before we can move the caret).
    requestAnimationFrame(() => {
      el.focus();
      const caretPos = start + insertion.length;
      el.setSelectionRange(caretPos, caretPos);
    });
  };

  // Live preview of the resolved filename using placeholder/current values -
  // purely client-side illustration; the backend (filename_template.py) is
  // the source of truth at actual export time.
  const getOutputFilenamePatternPreview = () => {
    const pattern = outputFilenamePattern.trim();
    if (!pattern) return "";
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const timestampStr = `${dateStr}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}Z`;
    const templateName = sanitizeCsvColumnName(template?.template_name || "Template");
    return pattern
      .replace(/\{RequestId\}/g, "1042")
      .replace(/\{Date\}/g, dateStr)
      .replace(/\{Timestamp\}/g, timestampStr)
      .replace(/\{Template\}/g, templateName);
  };

  const handleRetryDelivery = async (deliveryId, webhookId) => {
    try {
      setRetryingDeliveryId(deliveryId);
      await api.post(`/webhooks/deliveries/${deliveryId}/retry`);
      toast.success(
        "Retry fired",
        "Webhook retry started. Refreshing logs shortly...",
      );
      setTimeout(() => {
        loadDeliveries(webhookId);
      }, 2500);
    } catch (e) {
      console.error(e);
      toast.error(
        "Retry failed",
        e?.response?.data?.error || "Unable to retry delivery",
      );
    } finally {
      setRetryingDeliveryId(null);
    }
  };

  // Safely parse field_values which may arrive as a JSON string or an array
  const parseFieldValues = (fv) => {
    if (!fv) return [];
    if (Array.isArray(fv)) return fv;
    try {
      return JSON.parse(fv);
    } catch {
      return [];
    }
  };

  const handleSaveMapping = async () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonEditorValue);
    } catch (e) {
      setJsonError(e.message);
      return;
    }
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      setJsonError("Template must be a JSON object { ... }");
      return;
    }
    setJsonError(null);
    setMappingServerCheck(null);
    setSavingMappingId(mappingModalWebhookId);
    try {
      await templatesApi.saveWebhookMapping(id, mappingModalWebhookId, {
        type: "json_template",
        format: mappingFormat,
        template: parsed,
        excluded_fields: mappingExcluded,
      });
      setMappingModalWebhookId(null);
      toast.success(
        "Mapping Saved",
        "Payload mapping has been updated for this webhook.",
      );
      // Re-read the links so the card's sync status comes from the server.
      loadTemplateWebhooks();
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 422 && e.response.data?.mapping_check) {
        setMappingServerCheck(e.response.data.mapping_check);
        toast.error(
          "Mapping doesn't match the template",
          "Fix the problems listed under the editor, then save again.",
        );
      } else {
        toast.error(
          "Save Failed",
          e?.response?.data?.error ||
            "Could not save the payload mapping. Please try again.",
        );
      }
    } finally {
      setSavingMappingId(null);
    }
  };

  const handleClearMapping = async () => {
    setSavingMappingId(mappingModalWebhookId);
    try {
      await api.put(
        `/webhooks/template/${id}/${mappingModalWebhookId}/mapping`,
        {},
      );
      setLinkedWebhooks((prev) =>
        prev.map((w) =>
          w.id === mappingModalWebhookId ? { ...w, payload_mapping: null } : w,
        ),
      );
      setMappingModalWebhookId(null);
      toast.success(
        "Mapping Cleared",
        "This webhook will now send the default Xtract payload.",
      );
      loadTemplateWebhooks();
    } catch (e) {
      console.error(e);
      toast.error(
        "Clear Failed",
        e?.response?.data?.error || "Could not clear the payload mapping. Please try again.",
      );
    } finally {
      setSavingMappingId(null);
      setClearMappingPending(false);
    }
  };

  const handleSaveHeaders = async () => {
    const headersObj = {};
    for (const row of headerRows) {
      const k = row.key.trim();
      if (k) headersObj[k] = row.value;
    }
    const toSave = Object.keys(headersObj).length > 0 ? headersObj : null;
    setSavingHeadersId(headersModalWebhookId);
    try {
      await api.put(
        `/webhooks/template/${id}/${headersModalWebhookId}/headers`,
        toSave ? { headers: headersObj } : {},
      );
      setLinkedWebhooks((prev) =>
        prev.map((w) =>
          w.id === headersModalWebhookId ? { ...w, custom_headers: toSave } : w,
        ),
      );
      setHeadersModalWebhookId(null);
      toast.success(
        "Headers Saved",
        "Custom headers have been updated for this webhook.",
      );
    } catch (e) {
      console.error(e);
      toast.error(
        "Save Failed",
        "Could not save the custom headers. Please try again.",
      );
    } finally {
      setSavingHeadersId(null);
    }
  };

  // Pre-populate headers editor and register Escape key when headers modal opens
  useEffect(() => {
    if (!headersModalWebhookId) return;
    const wh = linkedWebhooks.find((w) => w.id === headersModalWebhookId);
    const existing = wh?.custom_headers;
    if (
      existing &&
      typeof existing === "object" &&
      Object.keys(existing).length > 0
    ) {
      setHeaderRows(
        Object.entries(existing).map(([key, value]) => ({
          key,
          value: String(value),
        })),
      );
    } else {
      setHeaderRows([{ key: "", value: "" }]);
    }
    setHeadersFieldSearch("");
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setHeadersModalWebhookId(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [headersModalWebhookId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate JSON editor and register Escape key when mapping modal opens
  useEffect(() => {
    if (!mappingModalWebhookId) return;
    const wh = linkedWebhooks.find((w) => w.id === mappingModalWebhookId);
    const existing = wh?.payload_mapping?.template;
    setMappingFormat(wh?.payload_mapping?.format || "json");
    setJsonEditorValue(existing ? JSON.stringify(existing, null, 2) : "");
    setMappingExcluded(wh?.payload_mapping?.excluded_fields || []);
    setMappingServerCheck(null);
    setJsonError(null);
    setClearMappingPending(false);
    setMappingFieldSearch("");
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMappingModalWebhookId(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mappingModalWebhookId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDeliveryDetails = async (deliveryId) => {
    if (expandedDeliveryId === deliveryId) {
      setExpandedDeliveryId(null);
      return;
    }

    setExpandedDeliveryId(deliveryId);
    if (deliveryDetails[deliveryId]) {
      return;
    }

    try {
      setLoadingDeliveryDetailsId(deliveryId);
      const res = await api.get(`/webhooks/deliveries/${deliveryId}`);
      setDeliveryDetails((prev) => ({
        ...prev,
        [deliveryId]: res.data?.delivery || null,
      }));
    } catch (e) {
      console.error(e);
      toast.error(
        "Delivery detail load failed",
        e?.response?.data?.error || "Unable to load delivery details",
      );
    } finally {
      setLoadingDeliveryDetailsId(null);
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const [templateRes, categoriesRes] = await Promise.all([
        templatesApi.get(id),
        templatesApi.getCategories(),
      ]);
      setTemplate(templateRes.data);
      setCategories(categoriesRes.data?.categories || []);
      setPendingSettings(null);
      setError("");
    } catch (e) {
      console.error(e);
      setError("Failed to load template details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplate();
  }, [id]);

  // Re-read the template without the page-level loading state. Used after a Business
  // Central setting is saved: the full reload swaps the page for a skeleton, which
  // unmounts the settings tab the user is working in.
  const refreshTemplateQuietly = async () => {
    try {
      const res = await templatesApi.get(id);
      setTemplate(res.data);
    } catch (e) {
      console.warn("Could not refresh the template:", e?.message);
    }
  };

  // Sync Boomi posting-rule toggle state from the loaded template. Runs
  // whenever the template reloads (e.g. after save) so the modal reflects
  // the persisted values rather than stale local defaults.
  useEffect(() => {
    if (!template) return;
    setBoomiValidateBeforePosting(
      template.boomi_validate_before_posting !== undefined &&
        template.boomi_validate_before_posting !== null
        ? Boolean(template.boomi_validate_before_posting)
        : true,
    );
    setBoomiBlockOnErrors(
      template.boomi_block_on_errors !== undefined &&
        template.boomi_block_on_errors !== null
        ? Boolean(template.boomi_block_on_errors)
        : true,
    );
    setBoomiTogglesDirty(false);
  }, [template]);

  // Fetch live NS field catalog when template is NS-enabled (fully dynamic, no hardcoding)
  useEffect(() => {
    if (!template) return;
    if (nsFieldCatalogFetched.current) return;
    const isNs =
      template.ns_enabled ||
      template.template_ns_enabled ||
      isNsTemplate;
    if (!isNs) return;
    nsFieldCatalogFetched.current = true;
    const ownNsEntityType = template.ns_entity_type || template.template_ns_entity_type || 'purchase_order';
    nsApi
      // This effect only wants available_fields to label the mapping dropdown, so
      // skip the custom-field probe (a SuiteQL round trip per 25 custom fields)
      // that a real detection pass runs — it would make every page load pay for
      // work this view does not use.
      //
      // ns_entity_type must be passed explicitly (NOT template_id — that would
      // switch the backend into its persist-and-overwrite-rules branch on every
      // page load). The backend's _resolve_ns_entity_type() defaults to
      // 'purchase_order' when the body carries neither template_id nor
      // ns_entity_type, so omitting it here silently served the PO field
      // catalog (with real PO keys like tranId/entity/subsidiary) to Vendor
      // Bid templates' mapping dropdown — the exact dropdown-vs-mapped-rows
      // mismatch this was fixed for.
      .detectFields({ probe_custom_fields: false, ns_entity_type: ownNsEntityType })
      .then((res) => {
        const catalog = res.data?.available_fields || [];
        if (catalog.length > 0) {
          setNsFieldCatalog(catalog);
          setNsFieldCatalogError('');
        } else {
          // catalog_error carries the actual NetSuite/network failure the
          // backend caught (bad credentials, missing role permission, the
          // metadata-catalog endpoint itself failing) — every real account
          // exposes 200+ PO fields, so an empty list is always a failure, never
          // a legitimate response. Prefer that real reason; the generic line
          // only covers the case where the request succeeded with nothing
          // (which the backend should no longer produce, but a response from a
          // pre-fix backend could still).
          setNsFieldCatalogError(
            res.data?.catalog_error
              ? `Could not load NetSuite fields: ${res.data.catalog_error}`
              : 'NetSuite returned no fields — check the connection and role permissions.',
          );
        }
      })
      .catch((err) => {
        // Previously only console.warn'd, so a 403 (a template_manager blocked
        // by an over-strict endpoint) left the mapping dropdown silently empty
        // with no visible cause. Surface it — the dropdown still falls back to
        // raw saved keys, but the user can now tell why the labeled list is gone.
        const status = err?.response?.status;
        setNsFieldCatalogError(
          status === 403
            ? 'You don\'t have permission to load NetSuite fields. Ask an admin or template manager.'
            : err?.response?.data?.error || 'Could not load NetSuite fields. The dropdown will show raw field keys instead.',
        );
      });
  }, [
    template?.id,
    template?.ns_enabled,
    template?.template_ns_enabled,
    template?.template_creation_method,
  ]);

  // Fetch live BC field catalog when template is BC-enabled (mirrors the NS effect above)
  useEffect(() => {
    if (!template || !features.business_central) return
    // Wait for the current user: detect-fields is administrator-only (it samples live
    // Business Central documents), so everyone else is served the cheaper field list.
    if (!currentUser) return
    if (bcFieldCatalogFetched.current === template.id) return
    const isBc = template.bc_enabled || template.template_bc_enabled ||
                 template.template_creation_method === 'bc_purchase_order'
    if (!isBc) return
    bcFieldCatalogFetched.current = template.id
    const request = currentUser.role === 'admin'
      ? bcApi.detectFields(template.id || id).then(res => res.data?.available_fields || [])
      : bcApi.getPOFields(template.id || id).then(res => res.data?.fields || [])
    request
      .then(catalog => {
        if (catalog.length > 0) {
          setBcFieldCatalog(catalog)
        } else {
          console.warn('BC field catalog came back empty — API may require valid credentials')
        }
      })
      .catch(err => {
        console.warn('Failed to load BC field catalog:', err?.response?.status, err?.message)
        // If the catalog fails, the dropdown will still show raw saved keys
      })
  }, [template?.id, template?.bc_enabled, template?.template_bc_enabled, template?.template_creation_method, features.business_central, currentUser?.role])

  // Fetch BC entity types once — a fast DB read, not a live BC call — so the
  // upload panel's "Creating template for the ___" heading has a real display
  // name (Purchase Order / Purchase Invoice / ...) instead of guessing.
  useEffect(() => {
    if (!template || !features.business_central) return
    if (bcEntityTypesFetched.current === template.id) return
    const isBc = template.bc_enabled || template.template_bc_enabled ||
                 template.template_creation_method === 'bc_purchase_order'
    if (!isBc) return
    bcEntityTypesFetched.current = template.id
    bcApi.getEntityTypes()
      .then(res => setBcEntityTypes(res.data?.entity_types || []))
      .catch(() => {})
  }, [template?.id, template?.bc_enabled, template?.template_bc_enabled, template?.template_creation_method, features.business_central])

  // Display name for this template's chosen BC document type, e.g. "Purchase
  // Invoice" — falls back to a readable guess from the key so the heading
  // never shows nothing while bcEntityTypes is still loading.
  // Whether this template is a Business Central template at all — the single
  // source of truth for showing the Business Central tab.
  //
  // This deliberately does NOT test `template_bc_enabled`: that key is only
  // produced by _request_to_dict_internal (for requests). A template loaded
  // through templatesApi.get() → _template_to_dict carries `bc_enabled`
  // instead, so gating the tab on `template_bc_enabled === true` compared
  // against undefined and hid the Business Central tab on every template.
  //
  // Enabled OR created-as-BC both qualify: a BC template whose posting is
  // currently switched off still needs its tab, because that tab is where the
  // toggle to switch it back on lives.
  const isBcTemplate = useMemo(() => !!(
    template?.bc_enabled ||
    template?.template_bc_enabled ||
    template?.creation_method === 'bc_purchase_order' ||
    template?.template_creation_method === 'bc_purchase_order'
  ), [
    template?.bc_enabled, template?.template_bc_enabled,
    template?.creation_method, template?.template_creation_method,
  ])

  // Mirrors isBcTemplate. Used to decide whether NetSuite-specific UI (field-rule
  // freshness) belongs on this template.
  const isNsTemplate = useMemo(() => !!(
    template?.ns_enabled ||
    template?.template_ns_enabled ||
    template?.creation_method === 'ns_purchase_order' ||
    template?.template_creation_method === 'ns_purchase_order'
  ), [
    template?.ns_enabled, template?.template_ns_enabled,
    template?.creation_method, template?.template_creation_method,
  ])

  // Which NetSuite document type this template targets — 'purchase_order' or
  // 'vendor_bid'. Drives every piece of NS-related copy/behaviour below so a
  // Vendor Bid template never shows Purchase Order labels (or vice versa).
  const nsEntityTypeForCopy = useMemo(
    () => (template?.ns_entity_type || template?.template_ns_entity_type || 'purchase_order'),
    [template?.ns_entity_type, template?.template_ns_entity_type],
  )
  const isNsBidTemplateForCopy = isNsTemplate && nsEntityTypeForCopy === 'vendor_bid'

  const bcEntityTypeDisplayName = useMemo(() => {
    const key = template?.bc_entity_type || template?.template_bc_entity_type || 'purchase_order'
    const found = bcEntityTypes.find(t => t.bc_entity_type_key === key)
    if (found?.display_name) return found.display_name
    return key.split('_').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ')
  }, [template?.bc_entity_type, template?.template_bc_entity_type, bcEntityTypes])

  // Fetch live BC *line-item* field catalog (purchaseOrderLine entity)
  useEffect(() => {
    if (!template || !features.business_central) return
    if (bcLineFieldCatalogFetched.current === template.id) return
    const isBc = template.bc_enabled || template.template_bc_enabled ||
                 template.template_creation_method === 'bc_purchase_order'
    if (!isBc) return
    bcLineFieldCatalogFetched.current = template.id
    bcApi.getPOLineFields(template.id || id)
      .then(res => {
        const catalog = res.data?.fields || []
        if (catalog.length > 0) setBcLineFieldCatalog(catalog)
      })
      .catch(err => {
        console.warn('Failed to load BC line field catalog:', err?.response?.status, err?.message)
      })
  }, [template?.id, template?.bc_enabled, template?.template_bc_enabled, template?.template_creation_method, features.business_central])

  // Load currentUser on mount so isAdmin is available for tab visibility
  useEffect(() => {
    if (!currentUser) {
      authApi
        .getCurrentUser()
        .then((res) => {
          setCurrentUser(res.data || null);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (
      !connectorsEnabled &&
      (activeTab === "webhooks" || activeTab === "output_destinations")
    ) {
      setActiveTab("fields");
    }
  }, [connectorsEnabled, activeTab]);

  useEffect(() => {
    if (!connectorsEnabled) return;
    // The fields tab needs the links too: a field change is checked against
    // each connector's Map Payload before it is saved.
    if (activeTab === "webhooks" || activeTab === "settings" || activeTab === "fields") {
      loadTemplateWebhooks();
    }
  }, [connectorsEnabled, activeTab, loadTemplateWebhooks]);

  // A field change elsewhere on the page (imports, AI suggestions, undo) put a
  // connector out of sync — refresh the links so their status badges are current.
  useEffect(() => {
    if (!connectorsEnabled) return;
    const handler = () => loadTemplateWebhooks();
    window.addEventListener("mapping-impact", handler);
    return () => window.removeEventListener("mapping-impact", handler);
  }, [connectorsEnabled, loadTemplateWebhooks]);

  useEffect(() => {
    if (!connectorsEnabled) return;
    if (activeTab === "output_destinations" || activeTab === "settings") {
      loadTemplateOutputDestinations();
    }
  }, [connectorsEnabled, activeTab, loadTemplateOutputDestinations]);

  // Browser close/refresh guard when fields changed but analyzer not rebuilt
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedFieldChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedFieldChanges]);

  // Lets the OpCo WorkspaceSwitcher confirm before a reload drops edits.
  useEffect(() => {
    window.__xtractDirty = Boolean(hasUnsavedFieldChanges);
    return () => { window.__xtractDirty = false; };
  }, [hasUnsavedFieldChanges]);

  // Retention: load policies when tab is active
  const loadRetentionPolicies = useCallback(async () => {
    setRetentionLoading(true);
    try {
      const res = await retentionApi.listPolicies({ template_id: id });
      const policies = res.data.policies || [];
      setRetentionPolicies(policies);
      // Initialize local edits from loaded policies
      const edits = {};
      RETENTION_ENTITY_TYPES.forEach((meta) => {
        const p = policies.find(
          (pol) =>
            pol.entity_type === meta.key &&
            String(pol.template_id) === String(id),
        );
        edits[meta.key] = {
          days: p?.days || meta.defaultDays,
          enabled: p?.enabled || false,
          deleteBlobs: p?.delete_blobs !== false,
          excludeStatuses: p?.exclude_statuses || [],
          templateId: parseInt(id),
          _dirty: false,
        };
      });
      setRetentionLocalEdits(edits);
    } catch (e) {
      console.error("Failed to load retention policies", e);
    } finally {
      setRetentionLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === "retention") {
      loadRetentionPolicies();
    }
  }, [activeTab, loadRetentionPolicies]);

  const loadTemplateLearningRules = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingLearningRules(true);
      setLearningRulesError("");
      const res = await templatesApi.listAiLearningRules(id, {
        include_inactive: true,
      });
      setLearningRules(res.data?.rules || []);
    } catch (e) {
      console.error(e);
      setLearningRules([]);
      setLearningRulesError(
        e?.response?.data?.error || "Failed to load AI learning rules",
      );
    } finally {
      setLoadingLearningRules(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === "learning") {
      loadTemplateLearningRules();
    }
  }, [activeTab, loadTemplateLearningRules]);

  // Update a single entity's local state
  const handleRetentionLocalChange = useCallback((entityKey, changes) => {
    setRetentionLocalEdits((prev) => ({
      ...prev,
      [entityKey]: { ...prev[entityKey], ...changes, _dirty: true },
    }));
  }, []);

  // Check if any edits are dirty
  const retentionHasDirtyEdits = useMemo(
    () => Object.values(retentionLocalEdits).some((e) => e._dirty),
    [retentionLocalEdits],
  );

  const dirtyRetentionEntries = useMemo(
    () => Object.entries(retentionLocalEdits).filter(([, v]) => v._dirty),
    [retentionLocalEdits],
  );

  const enabledDirtyRetentionEntries = useMemo(
    () => dirtyRetentionEntries.filter(([, state]) => state.enabled),
    [dirtyRetentionEntries],
  );

  const totalRetentionImpact = useMemo(
    () =>
      Object.values(retentionConfirmImpact).reduce(
        (sum, count) => sum + (Number.isFinite(count) ? count : 0),
        0,
      ),
    [retentionConfirmImpact],
  );

  useEffect(() => {
    if (!retentionShowConfirm) {
      setRetentionConfirmImpact({});
      return;
    }

    let cancelled = false;
    const loadImpact = async () => {
      if (enabledDirtyRetentionEntries.length === 0) {
        setRetentionConfirmImpact({});
        setRetentionImpactLoading(false);
        return;
      }
      setRetentionImpactLoading(true);
      try {
        const impacts = await Promise.all(
          enabledDirtyRetentionEntries.map(async ([entityKey, state]) => {
            const meta = RETENTION_ENTITY_TYPES.find(
              (m) => m.key === entityKey,
            );
            try {
              const res = await retentionApi.preview({
                entity_type: entityKey,
                template_id: parseInt(id),
                days: state.days,
                exclude_statuses: meta?.hasStatusFilter
                  ? state.excludeStatuses
                  : [],
              });
              return [entityKey, res?.data?.affected_count || 0];
            } catch {
              return [entityKey, 0];
            }
          }),
        );

        if (!cancelled) {
          setRetentionConfirmImpact(Object.fromEntries(impacts));
        }
      } finally {
        if (!cancelled) {
          setRetentionImpactLoading(false);
        }
      }
    };

    loadImpact();
    return () => {
      cancelled = true;
    };
  }, [retentionShowConfirm, enabledDirtyRetentionEntries, id]);

  const handleRetentionSaveClick = () => {
    if (enabledDirtyRetentionEntries.length === 0) {
      // Only disabling/updating OFF policies: save immediately without delete-impact confirmation.
      handleRetentionSaveAll();
      return;
    }
    setRetentionShowConfirm(true);
  };

  // Save ALL dirty policies at once
  const handleRetentionSaveAll = async () => {
    setRetentionShowConfirm(false);
    setRetentionSaving(true);
    try {
      const dirtyEntries = Object.entries(retentionLocalEdits).filter(
        ([, v]) => v._dirty,
      );
      for (const [entityKey, state] of dirtyEntries) {
        const meta = RETENTION_ENTITY_TYPES.find((m) => m.key === entityKey);
        await retentionApi.upsertPolicy({
          entity_type: entityKey,
          template_id: parseInt(id),
          days: state.days,
          enabled: state.enabled,
          delete_blobs: state.deleteBlobs,
          exclude_statuses: meta?.hasStatusFilter
            ? state.excludeStatuses
            : undefined,
        });
      }
      toast.success(
        `${dirtyEntries.length} retention ${dirtyEntries.length === 1 ? "policy" : "policies"} saved`,
      );
      await loadRetentionPolicies();
    } catch (e) {
      toast.error("Failed to save policies");
    } finally {
      setRetentionSaving(false);
    }
  };

  const handleRetentionSave = async (data) => {
    setRetentionSaving(true);
    try {
      await retentionApi.upsertPolicy(data);
      toast.success("Retention policy saved");
      await loadRetentionPolicies();
    } catch (e) {
      toast.error("Failed to save policy");
    } finally {
      setRetentionSaving(false);
    }
  };

  const handleRetentionPreview = async (data) => {
    try {
      const res = await retentionApi.preview(data);
      return res.data.affected_count;
    } catch (e) {
      toast.error("Failed to generate preview");
      return null;
    }
  };

  const handleRetentionPreviewItems = async (data) => {
    try {
      const res = await retentionApi.previewItems(data);
      return res.data;
    } catch (e) {
      toast.error("Failed to load preview items");
      return { items: [], total: 0, has_more: false };
    }
  };

  const handleRetentionBulkApply = async () => {
    setRetentionBulkSaving(true);
    try {
      await retentionApi.bulkApply({
        template_id: parseInt(id),
        days: retentionBulkDays,
        enabled: true,
      });
      toast.success(
        `Applied ${retentionBulkDays}-day retention to all entity types`,
      );
      await loadRetentionPolicies();
    } catch (e) {
      toast.error("Failed to apply bulk retention");
    } finally {
      setRetentionBulkSaving(false);
    }
  };

  const handleRetentionReset = async () => {
    setRetentionBulkSaving(true);
    try {
      await retentionApi.bulkApply({
        template_id: parseInt(id),
        days: retentionBulkDays,
        enabled: false,
      });
      toast.success("All retention policies disabled");
      await loadRetentionPolicies();
    } catch (e) {
      toast.error("Failed to reset retention policies");
    } finally {
      setRetentionBulkSaving(false);
    }
  };

  const handleRetentionRunNow = async () => {
    setRetentionRunNowSaving(true);
    try {
      await retentionApi.runNow();
      toast.success(
        "Retention cleanup started in background for your organisation",
      );
      await loadRetentionPolicies();
    } catch (e) {
      toast.error("Failed to run retention cleanup now");
    } finally {
      setRetentionRunNowSaving(false);
    }
  };

  const getRetentionPolicy = (entityType) =>
    retentionPolicies.find(
      (p) =>
        p.entity_type === entityType && String(p.template_id) === String(id),
    ) || null;

  const handleSaveField = async (payload) => {
    // Field keys can't be renamed, so only the type, the table columns and
    // adding a field can change what a Map Payload can reference.
    const current = template?.fields || [];
    const proposed = {
      ...(payload.field_name ? { field_name: payload.field_name } : {}),
      ...(payload.field_type ? { field_type: payload.field_type } : {}),
      ...(payload.field_values !== undefined ? { field_values: payload.field_values } : {}),
    };
    const nextFields = editingField
      ? current.map((f) =>
          f.id === editingField.id ? { ...f, ...proposed, field_name: f.field_name } : f,
        )
      : [...current, { ...proposed, is_active: true }];
    const note = mappingImpactNote(nextFields);
    if (note) {
      const go = await showConfirm({
        title: editingField ? "Save this field change?" : "Add this field?",
        message: note.trim(),
        confirmLabel: editingField ? "Save anyway" : "Add anyway",
        variant: "danger",
      });
      if (!go) return;
    }
    try {
      setSavingField(true);
      if (editingField) {
        await templatesApi.updateField(id, editingField.id, payload);
        toast.success("Field updated", "Template field updated successfully.");
      } else {
        await templatesApi.addField(id, payload);
        toast.success("Field added", "Template field added successfully.");
      }
      setFieldModalOpen(false);
      setEditingField(null);
      setFieldsDirty(true);
      await loadTemplate();
    } catch (e) {
      console.error(e);
      toast.error(
        "Save failed",
        e?.response?.data?.error || "Unable to save field",
      );
    } finally {
      setSavingField(false);
    }
  };

  // E-mail fields aren't read by AI, so adding them doesn't need an analyzer rebuild.
  const handleAddEmailFields = async (sources) => {
    try {
      setAddingEmailFields(true);
      const { data } = await templatesApi.addEmailFields(id, sources);
      const count = data?.added?.length || 0;
      toast.success(
        count === 1 ? "Field added" : "Fields added",
        `${count} field${count === 1 ? "" : "s"} added in the E-mail section.`,
      );
      setEmailPickerOpen(false);
      await loadTemplate();
    } catch (e) {
      console.error(e);
      toast.error("Add failed", e?.response?.data?.error || "Unable to add the fields");
    } finally {
      setAddingEmailFields(false);
    }
  };

  // Mirrors the backend's own gate (_can_manage_templates): admins and template
  // managers only. Without this the button would render for everyone and simply
  // 403 on click.
  const canManageTemplates = ['admin', 'template_manager'].includes(currentUser?.role)
  const [deletingTemplate, setDeletingTemplate] = useState(false)

  const handleDeleteTemplate = async () => {
    if (isReader || deletingTemplate) return
    const label = template?.name || 'this template'
    const confirmed = await showConfirm({
      title: `Delete "${label}"?`,
      message:
        'The template and its fields will be removed, and its trained analyzer will be ' +
        'deleted from Azure — that part cannot be undone. Requests already created from ' +
        'this template are kept, but it will no longer be available for new ones.',
      confirmLabel: 'Delete template',
      variant: 'danger',
    })
    if (!confirmed) return

    setDeletingTemplate(true)
    try {
      await templatesApi.delete(id)
      toast.success('Template deleted', `"${label}" has been deleted.`)
      navigate('/templates')
    } catch (e) {
      console.error('Delete template failed:', e)
      toast.error(
        'Delete failed',
        e?.response?.data?.error || 'Unable to delete this template.'
      )
      setDeletingTemplate(false)
    }
  }

  const handleDeleteField = async (field) => {
    const label = field?.display_name || field?.field_name || "this field";
    const confirmed = await showConfirm({
      title: `Delete "${label}"?`,
      message:
        "This field will be removed from the template. You can undo this straight after." +
        mappingImpactNote((template?.fields || []).filter((f) => f.id !== field.id)),
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      await templatesApi.deleteField(id, field.id);
      setSelectedFieldIds((prev) => prev.filter((fid) => fid !== field.id));
      setFieldsDirty(true);
      await loadTemplate();
      toast.addToast({
        type: "success",
        title: "Field removed",
        message: `"${label}" was deleted.`,
        duration: 9000,
        action: {
          label: "Undo",
          onClick: () => handleUndoDeleteFields([field]),
        },
      });
    } catch (e) {
      console.error(e);
      toast.error(
        "Delete failed",
        e?.response?.data?.error || "Unable to delete field",
      );
    }
  };

  // Restore soft-deleted fields (used by the Undo action)
  const handleUndoDeleteFields = async (fields) => {
    if (!fields?.length) return;
    try {
      await Promise.all(
        fields.map((f) =>
          templatesApi.updateField(id, f.id, { is_active: true }),
        ),
      );
      setFieldsDirty(true);
      await loadTemplate();
      toast.success(
        "Restored",
        fields.length === 1
          ? `"${fields[0].display_name || fields[0].field_name}" was restored.`
          : `${fields.length} fields were restored.`,
      );
    } catch (e) {
      console.error(e);
      toast.error(
        "Undo failed",
        e?.response?.data?.error || "Could not restore the field(s).",
      );
    }
  };

  // Bulk-delete the currently selected fields
  const handleBulkDeleteFields = async () => {
    const allFields = template?.fields || [];
    const fields = allFields.filter((f) => selectedFieldIds.includes(f.id));
    if (!fields.length) return;
    const confirmed = await showConfirm({
      title: `Delete ${fields.length} ${fields.length === 1 ? "field" : "fields"}?`,
      message:
        "The selected fields will be removed from the template. You can undo this straight after." +
        mappingImpactNote(allFields.filter((f) => !selectedFieldIds.includes(f.id))),
      confirmLabel: `Delete ${fields.length}`,
      variant: "danger",
    });
    if (!confirmed) return;
    setBulkDeleting(true);
    try {
      await Promise.all(fields.map((f) => templatesApi.deleteField(id, f.id)));
      setSelectedFieldIds([]);
      setFieldsDirty(true);
      await loadTemplate();
      toast.addToast({
        type: "success",
        title: `${fields.length} ${fields.length === 1 ? "field" : "fields"} removed`,
        message: "The selected fields were deleted.",
        duration: 9000,
        action: {
          label: "Undo",
          onClick: () => handleUndoDeleteFields(fields),
        },
      });
    } catch (e) {
      console.error(e);
      toast.error(
        "Delete failed",
        e?.response?.data?.error || "Unable to delete the selected fields",
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleFieldSelected = (fieldId) => {
    setSelectedFieldIds((prev) =>
      prev.includes(fieldId)
        ? prev.filter((f) => f !== fieldId)
        : [...prev, fieldId],
    );
  };

  const toggleGroupSelected = (groupFieldIds, allSelected) => {
    setSelectedFieldIds((prev) => {
      if (allSelected) return prev.filter((f) => !groupFieldIds.includes(f));
      const set = new Set(prev);
      groupFieldIds.forEach((f) => set.add(f));
      return [...set];
    });
  };

  const clearFieldSelection = () => setSelectedFieldIds([]);

  const openFieldEditor = (field) => {
    if (isReader) return;
    setEditingField(field);
    setFieldModalOpen(true);
  };

  const handleDeleteAllFields = async () => {
    const confirmed = await showConfirm({
      title: `Delete all ${template?.fields?.length || 0} fields?`,
      message:
        "This will remove every field from this template. This action cannot be undone." +
        mappingImpactNote([]),
      confirmLabel: "Delete All",
      variant: "danger",
    });
    if (!confirmed) return;
    try {
      const res = await templatesApi.deleteAllFields(id);
      toast.success(
        "All fields deleted",
        res.data?.message || "All fields removed.",
      );
      setFieldsDirty(true);
      await loadTemplate();
    } catch (e) {
      console.error(e);
      toast.error(
        "Delete failed",
        e?.response?.data?.error || "Unable to delete fields",
      );
    }
  };

  const handleImportFields = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      setImporting(true);
      const text = await file.text();
      const json = JSON.parse(text);

      // Support multiple JSON formats:
      // 1. Azure CU format: { fieldSchema: { fields: { FieldName: { type, description } } } }
      // 2. Flat dict: { fields: { FieldName: { type, description } } }
      // 3. Direct dict: { FieldName: { type, description } }
      let fieldsDict = null;
      if (
        json.fieldSchema?.fields &&
        typeof json.fieldSchema.fields === "object"
      ) {
        fieldsDict = json.fieldSchema.fields;
      } else if (
        json.fields &&
        typeof json.fields === "object" &&
        !Array.isArray(json.fields)
      ) {
        fieldsDict = json.fields;
      } else if (
        typeof json === "object" &&
        !Array.isArray(json) &&
        !json.fieldSchema
      ) {
        // Check if top-level keys look like field definitions (have type/description)
        const firstVal = Object.values(json)[0];
        if (
          firstVal &&
          typeof firstVal === "object" &&
          ("type" in firstVal || "description" in firstVal)
        ) {
          fieldsDict = json;
        }
      }

      if (!fieldsDict || Object.keys(fieldsDict).length === 0) {
        toast.error(
          "Invalid format",
          "JSON must contain field definitions. Supported formats: Azure CU analyzer JSON, or a { fields: { ... } } dictionary.",
        );
        return;
      }

      // Ask whether to clear existing fields
      const fieldCount = Object.keys(fieldsDict).length;
      const existingCount = template?.fields?.length || 0;
      const clearExisting =
        existingCount > 0 &&
        (await showConfirm({
          title: `Replace existing fields?`,
          message: `This will import ${fieldCount} fields from "${file.name}". You currently have ${existingCount} fields. Choose Replace to clear existing fields first, or Cancel to add alongside them.`,
          confirmLabel: "Replace All",
          variant: "danger",
        }));

      const res = await templatesApi.importFields(id, {
        fields: fieldsDict,
        clear_existing: clearExisting,
      });

      const data = res.data;
      toast.success(
        "Fields imported",
        `${data.added} added, ${data.reactivated} reactivated, ${data.skipped} skipped`,
      );

      // Save the import source as template metadata
      try {
        const existingDocs = template.source_documents
          ? (() => {
              try {
                return JSON.parse(template.source_documents);
              } catch {
                return [];
              }
            })()
          : [];
        await templatesApi.update(id, {
          name: template.name,
          description: template.description,
          source_documents: JSON.stringify([...existingDocs, file.name]),
          creation_method: template.creation_method || "json_import",
        });
      } catch (metaErr) {
        console.warn("Failed to save import metadata:", metaErr);
      }

      setFieldsDirty(true);
      await loadTemplate();
    } catch (err) {
      console.error("Import error:", err);
      if (err instanceof SyntaxError) {
        toast.error("Invalid JSON", "The file could not be parsed as JSON.");
      } else {
        toast.error(
          "Import failed",
          err?.response?.data?.error || "Unable to import fields",
        );
      }
    } finally {
      setImporting(false);
    }
  };

  const handleBuildAnalyzer = async () => {
    try {
      setBuildingAnalyzer(true);

      await templatesApi.buildAnalyzer(id, {
        name: `${template.name} Analyzer`,
        description: `Custom analyzer for template ${template.name}`,
        default_method: "extract",
      });
      // Re-fetch template so useEffect sees fresh analyzer.updated_at > all field.updated_at
      await loadTemplate();
      toast.success(
        "Saved",
        "Changes saved and linked to this template.",
      );
    } catch (e) {
      console.error(e);
      toast.error(
        "Save failed",
        e?.response?.data?.error || "Unable to save changes",
      );
    } finally {
      setSavingFieldToggles(false);
      setBuildingAnalyzer(false);
    }
  };

  // Add new BC columns detected by schema drift check
  const handleApplyBcDrift = async () => {
    if (!bcDrift?.new_columns?.length) return;
    const keys = bcDrift.new_columns.map((c) => c.key);
    try {
      setBcDriftApplying(true);
      await bcApi.applyDriftColumns(id, keys);
      toast.success(
        "BC fields added",
        `${keys.length} new BC column${keys.length !== 1 ? "s" : ""} added to this template. Review the BC mapping before approving.`,
      );
      setBcDrift(null); // dismiss banner
      setFieldsDirty(true);
      await loadTemplate();
    } catch (e) {
      toast.error(
        "Failed",
        e?.response?.data?.error || "Could not add BC fields.",
      );
    } finally {
      setBcDriftApplying(false);
    }
  };

  // ===== AI Suggest Handlers =====

  // ===== Template Assignment Handlers =====
  const openAssignModal = async (type) => {
    setAssignModal({ open: true, type });
    setLoadingAssignable(true);
    try {
      const roleFilter =
        type === "manager"
          ? "template_manager,admin"
          : "reviewer,admin,template_manager";
      const res = await templatesApi.getAssignableUsers(id, roleFilter);
      setAssignableUsers(res.data.users || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoadingAssignable(false);
    }
  };

  const handleAssign = async (userId) => {
    setSavingAssignment(true);
    try {
      const payload =
        assignModal.type === "manager"
          ? { manager_id: userId }
          : { approver_id: userId };
      const res = await templatesApi.updateAssignments(id, payload);
      setTemplate(res.data);
      toast.success(
        `${assignModal.type === "manager" ? "Manager" : "Approver"} updated`,
      );
      setAssignModal({ open: false, type: null });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to update assignment");
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleUnassign = async (type) => {
    setSavingAssignment(true);
    try {
      const payload =
        type === "manager" ? { manager_id: null } : { approver_id: null };
      const res = await templatesApi.updateAssignments(id, payload);
      setTemplate(res.data);
      toast.success(`${type === "manager" ? "Manager" : "Approver"} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to remove assignment");
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleAiFileSelect = (e) => {
    const isNs = isNsTemplate;
    if (!isNs && aiFiles.length >= 10) {
      toast.warning(
        "Limit reached",
        "You can only upload up to 10 documents. Please remove a document first.",
      );
      e.target.value = "";
      return;
    }
    const incoming = Array.from(e.target.files || []);
    const pdfs = incoming.filter((f) => f.type === "application/pdf");
    if (pdfs.length === 0) {
      toast.error("Invalid file", "Please select PDF files only");
      return;
    }
    if (isNs) {
      // NS: single file only — replace any existing
      setAiFiles([pdfs[0]]);
    } else {
      const remaining = 10 - aiFiles.length;
      const kept = pdfs.slice(0, remaining);
      if (pdfs.length > remaining) {
        toast.warning(
          "Limit reached",
          `Only ${remaining} more document(s) allowed. ${pdfs.length - remaining} file(s) were not added.`,
        );
      }
      setAiFiles((prev) => [...prev, ...kept]);
    }
    setAiResult(null);
    setAiSelected({});
    e.target.value = ""; // reset so same file can be re-selected
  };

  const handleAiFileDrop = (e) => {
    e.preventDefault();
    const isNs = isNsTemplate;
    if (!isNs && aiFiles.length >= 10) {
      toast.warning(
        "Limit reached",
        "You can only upload up to 10 documents. Please remove a document first.",
      );
      return;
    }
    const incoming = Array.from(e.dataTransfer.files || []);
    const pdfs = incoming.filter((f) => f.type === "application/pdf");
    if (pdfs.length === 0) {
      toast.error("Invalid file", "Please drop PDF files only");
      return;
    }
    if (isNs) {
      setAiFiles([pdfs[0]]);
    } else {
      const remaining = 10 - aiFiles.length;
      const kept = pdfs.slice(0, remaining);
      if (pdfs.length > remaining) {
        toast.warning(
          "Limit reached",
          `Only ${remaining} more document(s) allowed. ${pdfs.length - remaining} file(s) were not added.`,
        );
      }
      setAiFiles((prev) => [...prev, ...kept]);
    }
    setAiResult(null);
    setAiSelected({});
  };

  // Ticked by default: every new suggestion except optional extras (footer
  // details, secondary tables) and what the template already has.
  const defaultAiSelection = (fields) => {
    const selected = {};
    (fields || []).forEach((f) => {
      selected[f.field_name] =
        f.placement?.action !== "exists" && !isOptionalSuggestion(f);
    });
    return selected;
  };

  // Poll a field-suggestion job until completed/failed
  const pollSuggestionJob = useCallback(
    (jobId) => {
      if (aiPollRef.current) clearInterval(aiPollRef.current);
      setAiJobId(jobId);
      setAiCurrentJobId(jobId);
      setAiSuggesting(true);

      const check = async () => {
        try {
          const res = await jobsApi.getStatus(jobId);
          const job = res.data;
          if (job.status === "completed") {
            clearInterval(aiPollRef.current);
            aiPollRef.current = null;
            setAiSuggesting(false);
            setAiJobId(null);
            setAiJobProgress(null);

            const data =
              typeof job.result_data === "string"
                ? JSON.parse(job.result_data)
                : job.result_data;
            setAiApplyingAnswers(false);
            aiRefineParentRef.current = null;
            if (data && data.fields && data.fields.length > 0) {
              setAiResult(data);
              setAiSelected(defaultAiSelection(data.fields));
              setAiExpanded({});
              toast.success(
                data.parent_job_id ? "Suggestions updated" : "Fields suggested",
                data.parent_job_id
                  ? "The suggestions now follow your answers."
                  : `AI identified ${data.total_fields} fields from ${data.file_count || 1} document(s). Review and accept.`,
              );
            } else {
              toast.error(
                "AI suggestion completed",
                "No fields were found in the uploaded documents.",
              );
            }
          } else if (job.status === "failed") {
            clearInterval(aiPollRef.current);
            aiPollRef.current = null;
            setAiSuggesting(false);
            setAiApplyingAnswers(false);
            setAiJobId(null);
            setAiJobProgress(null);
            if (aiRefineParentRef.current) {
              // Keep working with the suggestion that is still on screen.
              setAiCurrentJobId(aiRefineParentRef.current);
              aiRefineParentRef.current = null;
            }
            toast.error(
              "AI suggestion failed",
              job.error_message || "Background processing failed",
            );
          } else {
            // Still running - update progress display
            setAiJobProgress({
              percent: job.progress_percent || 0,
              message: job.progress_message || "Processing...",
            });
          }
        } catch (e) {
          console.error("Poll error", e);
          clearInterval(aiPollRef.current);
          aiPollRef.current = null;
          setAiSuggesting(false);
          setAiApplyingAnswers(false);
          setAiJobId(null);
          if (aiRefineParentRef.current) {
            setAiCurrentJobId(aiRefineParentRef.current);
            aiRefineParentRef.current = null;
          }
        }
      };

      // Fire immediately, then every 4s
      check();
      aiPollRef.current = setInterval(check, 4000);
    },
    [toast],
  );

  // Cleanup polling on unmount
  useEffect(
    () => () => {
      if (aiPollRef.current) clearInterval(aiPollRef.current);
    },
    [],
  );

  // On mount (or when template loads): check for active/completed field_suggestion jobs
  useEffect(() => {
    if (!id || !template) return;
    const params = new URLSearchParams(location.search);
    const suggestionJobParam = params.get("suggestion_job");

    const checkExistingJobs = async () => {
      try {
        // Fetch most recent field_suggestion jobs for this template
        const res = await jobsApi.list({
          entity_type: "template",
          entity_id: id,
          job_type: "field_suggestion",
          per_page: 1,
        });
        const jobs = res.data?.jobs || [];
        if (jobs.length === 0) return;

        const latestJob = jobs[0];

        // If query param specifies a job, prioritize that one
        const targetJob = suggestionJobParam
          ? jobs.find((j) => String(j.id) === suggestionJobParam) || latestJob
          : latestJob;

        // Once the user has accepted (fully or partially) or dismissed a
        // job's suggestions, it's marked "consumed" server-side and must
        // never reappear on subsequent template opens - regardless of
        // whether every suggested field actually got added.
        if (targetJob.result_consumed) return;

        if (targetJob.status === "completed" && targetJob.result_data) {
          const data =
            typeof targetJob.result_data === "string"
              ? JSON.parse(targetJob.result_data)
              : targetJob.result_data;
          if (data && data.fields && data.fields.length > 0 && !aiResult) {
            // Only show if at least some suggested fields don't exist yet
            const existingNames = new Set(
              (template?.fields || []).map((f) => f.field_name),
            );
            const newFields = data.fields.filter((f) =>
              f.placement
                ? f.placement.action !== "exists"
                : !existingNames.has(f.field_name),
            );
            if (newFields.length > 0) {
              setShowAiSuggest(true);
              setAiResult(data);
              setAiCurrentJobId(targetJob.id);
              setAiSelected(defaultAiSelection(data.fields));
            }
          }
        } else if (
          targetJob.status === "running" ||
          targetJob.status === "pending"
        ) {
          setShowAiSuggest(true);
          pollSuggestionJob(targetJob.id);
        }
      } catch (e) {
        console.error("Failed to check existing AI jobs", e);
      }
    };

    checkExistingJobs();
  }, [id, template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // NetSuite setup gate: step 1 (fetch data + validations) must be satisfied
  // before the field-mapping panel is offered. Mapping a document against an
  // unknown field set is how a template ends up mapping to fields this account
  // does not use. Set by NSTemplateSetup, including when a previous run (or the
  // background detection at creation) already satisfied it.
  const [nsSetupReady, setNsSetupReady] = useState(false);

  // Auto-open AI Suggest panel for NS templates on first load ONLY if no fields exist yet
  const nsAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (!template) return;
    if (nsAutoOpenedRef.current) return;
    if (
      isNsTemplate &&
      !showAiSuggest &&
      !aiResult &&
      !aiJobId &&
      (template.fields || []).length === 0
    ) {
      nsAutoOpenedRef.current = true;
      setShowAiSuggest(true);
    }
  }, [template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAiSuggest = async () => {
    if (aiFiles.length === 0) return;
    // Which NetSuite document type this template targets — read off the saved
    // template, never assumed. Everything below (which endpoint fires, which
    // category/label the mapped fields get, the line-items schema) follows
    // this instead of unconditionally treating every NS template as a PO.
    const nsEntityType =
      template?.ns_entity_type || template?.template_ns_entity_type || "purchase_order";
    const isNsBidTemplate = isNsTemplate && nsEntityType === "vendor_bid";
    try {
      setAiSuggesting(true);
      setAiResult(null);

      if (isNsBidTemplate) {
        // Vendor Bid: its own endpoint call and its own field/category shape —
        // never the Purchase Order transform below. Passing template_id lets
        // the backend resolve the document type and dispatch to Bid's own
        // detection + AI mapping instead of defaulting to PO.
        const formData = new FormData();
        formData.append("file", aiFiles[0]);
        formData.append("template_id", template.id || id);
        formData.append("ns_entity_type", "vendor_bid");
        const res = await nsApi.extractFromDocument(formData);
        const data = res.data;
        const mappings = data.mappings || [];
        const alreadyHasLineItems = (template?.fields || []).some(
          (f) => f.field_name === "line_items",
        );
        const fields = bidFieldsFromMappings(mappings, {
          hasLineItems: alreadyHasLineItems,
        });
        const result = {
          fields,
          total_fields: fields.length,
          document_type: "NetSuite Special Pricing",
          document_summary: `Extracted ${fields.length} fields from PDF with NS Special Pricing mapping`,
          source_file: aiFiles[0].name,
          pages_analyzed: data.pdf_text_length ? Math.ceil(data.pdf_text_length / 3000) : 1,
          total_pages: data.pdf_text_length ? Math.ceil(data.pdf_text_length / 3000) : 1,
          ns_entity_type: "vendor_bid",
          ns_available_fields: data.available_fields || [],
          // Labels read but deliberately not mapped, kept so the review screen
          // can show the Distributor/Competitor rows were seen and skipped.
          ns_excluded_labels: excludedBidLabels(mappings),
          ns_catalog_source: data.catalog_source || null,
          ns_ai_error: data.ai_error || null,
          ns_mandatory_fields: data.mandatory_header_fields || data.mandatory_fields || [
            "custrecord_jcs_sp_supplier",
            "custrecord_jcs_sp_end_customer",
          ],
        };
        setAiResult(result);
        const selected = {};
        fields.forEach((f) => {
          selected[f.field_name] = true;
        });
        setAiSelected(selected);
        if (data.ai_error) {
          toast.warning(
            "Mapped from the known Samsung layout",
            `The AI step was unavailable (${data.ai_error}) — ${fields.length} fields were mapped from the known Special Pricing layout instead. Review and accept.`,
          );
        } else {
          toast.success(
            "Bid fields mapped",
            `AI identified ${fields.length} fields and mapped them to NetSuite Special Pricing keys. Review and accept.`,
          );
        }
      } else if (isNsTemplate) {
        // NS extract-from-document for Purchase Order and admin-registered
        // record types. The template's own type is sent: an explicit value
        // wins on the backend, so hardcoding purchase_order here mapped every
        // generic template's PDF against Purchase Order fields.
        const formData = new FormData();
        formData.append("file", aiFiles[0]);
        formData.append("template_id", template.id || id);
        formData.append("ns_entity_type", nsEntityType);
        const res = await nsApi.extractFromDocument(formData);
        const data = res.data;
        // Transform NS mappings into the standard AI result format
        const mappings = data.mappings || [];
        const seenKeys = new Set();
        const fields = [];
        for (const m of mappings) {
          const key =
            m.suggested_ns_key ||
            m.label?.toLowerCase().replace(/[^a-z0-9]+/g, "_") ||
            `field_${Math.random().toString(36).slice(2, 6)}`;
          if (seenKeys.has(key)) continue; // skip duplicate ns keys
          seenKeys.add(key);
          const nsHint =
            m.suggested_ns_key && NS_FIELD_HINTS[m.suggested_ns_key];
          fields.push({
            field_name: key,
            display_name: m.label,
            field_type: "text",
            category: "NetSuite PO",
            sample_value: m.sample_value,
            confidence: m.confidence,
            ns_key: m.suggested_ns_key,
            reason: typeof m.reason === "string" ? m.reason : "",
            description: nsHint
              ? `${nsHint} (appears on the document as "${m.label}")`
              : m.suggested_ns_key
                ? `Maps to NetSuite field: ${m.suggested_ns_key}. Appears on the document as "${m.label}".`
                : `No NetSuite mapping found. Appears on the document as "${m.label}".`,
          });
        }
        // extract-from-document only maps header key/value fields — it never
        // returns a line-items row, so the PO table would otherwise never get
        // a template field (and never get extracted at request time). Add one,
        // mirroring the BC purchase-order flow, unless it already exists.
        const alreadyHasLineItems = (template?.fields || []).some(
          (f) => f.field_name === "line_items",
        );
        if (!alreadyHasLineItems) {
          fields.push({
            field_name: "line_items",
            display_name: "Line Items",
            field_type: "array_of_objects",
            category: "NetSuite PO",
            description:
              "Purchase order line items (item code, description, quantity, unit price, UOM)",
            columns: [
              {
                name: "item_code",
                type: "text",
                description: "Item/product code as it appears on the document",
              },
              {
                name: "description",
                type: "text",
                description: "Item description",
              },
              {
                name: "quantity",
                type: "number",
                description: "Quantity ordered",
              },
              {
                name: "unit_price",
                type: "currency",
                description: "Unit price per item",
              },
              {
                name: "uom",
                type: "text",
                description: "Unit of measure (e.g. EA, BOX)",
              },
            ],
          });
        }
        const result = {
          fields,
          total_fields: fields.length,
          document_type: "NetSuite Purchase Order",
          document_summary: `Extracted ${fields.length} fields from PDF with NS mapping`,
          source_file: aiFiles[0].name,
          pages_analyzed: data.pdf_text_length
            ? Math.ceil(data.pdf_text_length / 3000)
            : 1,
          total_pages: data.pdf_text_length
            ? Math.ceil(data.pdf_text_length / 3000)
            : 1,
          ns_available_fields: data.available_fields || [],
          ns_mandatory_fields: data.mandatory_fields || [
            "entity",
            "tranDate",
            "subsidiary",
          ],
        };
        setAiResult(result);
        const selected = {};
        fields.forEach((f) => {
          selected[f.field_name] = true;
        });
        setAiSelected(selected);
        toast.success(
          "NS fields mapped",
          `AI identified ${fields.length} fields and mapped them to NetSuite PO keys. Review and accept.`,
        );
        setAiSuggesting(false);
      } else {
        const res = await templatesApi.suggestFields(id, aiFiles, aiPrompt, {
          restrict: aiRestrict,
          detailLevel: aiDetailLevel,
        });
        const data = res.data;
        // Async: response is 202 with job_id
        if (data.job_id) {
          toast.success(
            "Processing started",
            "AI is analyzing your documents in the background. You can continue working.",
          );
          pollSuggestionJob(data.job_id);
        } else {
          // Fallback: sync response (backward compat)
          setAiResult(data);
          const selected = {};
          (data.fields || []).forEach((f) => {
            selected[f.field_name] = true;
          });
          setAiSelected(selected);
          toast.success(
            "Fields suggested",
            `AI identified ${data.total_fields} fields. Review and accept the ones you want.`,
          );
          setAiSuggesting(false);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "AI suggestion failed",
        e?.response?.data?.error ||
          e?.response?.data?.details ||
          "Unable to analyze document",
      );
      setAiSuggesting(false);
    }
  };

  const handleAcceptSuggested = async () => {
    if (!aiResult?.fields) return;
    const incompleteManual = aiResult.fields.some(
      (f) => f._manual && aiSelected[f.field_name] && !f.display_name.trim(),
    );
    if (incompleteManual) {
      toast.error(
        "Missing field name",
        "Give every manually added field a display name before accepting.",
      );
      return;
    }
    const fieldsToAdd = aiResult.fields.filter((f) => aiSelected[f.field_name]);
    if (fieldsToAdd.length === 0) {
      toast.error("No fields selected", "Select at least one field to add");
      return;
    }

    setAiSaving(true);
    let added = 0;
    let failed = 0;
    // Tracks {field_name, reason} for each field that failed to save (not
    // counting silently-skipped 409 duplicates), so the real backend reason
    // (e.g. "field name exceeds 255 characters: '...'") can be shown to the
    // user instead of only logged to the console.
    const failures = [];
    const existingNames = new Set(
      (template?.fields || []).map((f) => f.field_name),
    );

    // The DB column (template_field_field_name / template_field_display_name)
    // is VARCHAR(255). AI-suggested names are model-generated and the user
    // never authored them, so - unlike a human typing a name on purpose -
    // there's no value in dead-ending the user on a name they didn't write.
    // Truncate to fit and warn, with a cheap post-truncation collision guard
    // (append a numeric suffix) since two different long names could share
    // the same first 255 characters. Manual field creation (this same
    // endpoint) intentionally does NOT get this treatment - see backend
    // create_template_field(), which still rejects with a clear message for
    // any caller that bypasses this truncation (defense in depth).
    const MAX_FIELD_NAME_LENGTH = 255;
    const fitField = (name, usedNames) => {
      if (name.length <= MAX_FIELD_NAME_LENGTH) return { value: name, truncated: false };
      let candidate = name.slice(0, MAX_FIELD_NAME_LENGTH);
      if (!usedNames.has(candidate)) return { value: candidate, truncated: true };
      // Collision after truncation (rare) - make room for a short numeric
      // suffix so the result still fits within the column limit.
      for (let i = 2; i < 1000; i++) {
        const suffix = `_${i}`;
        const shortened = name.slice(0, MAX_FIELD_NAME_LENGTH - suffix.length) + suffix;
        if (!usedNames.has(shortened)) return { value: shortened, truncated: true };
      }
      return { value: candidate, truncated: true }; // extremely unlikely fallback
    };

    // Suggestions from the schema designer say where each one goes (a new
    // field, a new table, new columns for a table the template has, or
    // nothing because it's already there); planSuggestionChanges turns them
    // into calls. NetSuite / Business Central mappings have no placement and
    // keep the loop below.
    let addedColumns = 0;
    const placementAware = fieldsToAdd.some((f) => f.placement);
    if (placementAware) {
      const plan = planSuggestionChanges(fieldsToAdd, template?.fields || []);
      const clashes = plan.skipped.filter((s) => s.reason.startsWith("a field with this name"));
      if (clashes.length) {
        toast.warning(
          "Some suggestions were skipped",
          `A field with the same name already exists: ${clashes.map((s) => s.field_name).join(", ")}.`,
        );
      }
      for (const c of plan.creates) {
        try {
          await templatesApi.addField(id, c.payload);
          added++;
        } catch (e) {
          if (e?.response?.status === 409) continue;
          failures.push({
            field_name: c.field_name,
            reason: e?.response?.data?.error || e?.message || "Unknown error",
          });
          failed++;
        }
      }
      for (const a of plan.appends) {
        try {
          // The server appends to the stored columns, so the table's own
          // columns (and their mappings) are never re-sent or lost.
          await templatesApi.updateField(id, a.field_id, { append_columns: a.columns });
          addedColumns += a.columns.length;
        } catch (e) {
          failures.push({
            field_name: a.field_name,
            reason: e?.response?.data?.error || e?.message || "Unknown error",
          });
          failed++;
        }
      }
    }

    for (const field of placementAware ? [] : fieldsToAdd) {
      if (existingNames.has(field.field_name)) {
        // Skip duplicates
        continue;
      }
      const nameResult = fitField(field.field_name, existingNames);
      const displayResult = fitField(
        field.display_name || field.field_name,
        new Set(), // display_name has no uniqueness constraint - just fit the length
      );
      const finalFieldName = nameResult.value;
      const finalDisplayName = displayResult.value;
      try {
        await templatesApi.addField(id, {
          display_name: finalDisplayName,
          field_name: finalFieldName,
          field_type:
            field.field_type === "currency"
              ? "number"
              : field.field_type || "text",
          category: field.category || "",
          description: field.description || "",
          // Pass NS field mapping so it actually saves to template_field_ns_field
          ns_field: field.ns_key || null,
          // Pass column schema for array_of_objects (table) fields
          ...(field.field_type === "array_of_objects" && field.columns
            ? {
                columns: field.columns,
                field_values: JSON.stringify(field.columns),
              }
            : {}),
        });
        added++;
        existingNames.add(finalFieldName);
        if (nameResult.truncated || displayResult.truncated) {
          toast.warning(
            "Field name shortened to fit",
            `"${field.field_name}" was too long and was shortened to "${finalFieldName}".`,
          );
        }
      } catch (e) {
        // 409 = duplicate field name - skip silently
        if (e?.response?.status === 409) {
          existingNames.add(finalFieldName);
          continue;
        }
        const reason =
          e?.response?.data?.error || e?.message || "Unknown error";
        console.error(`Failed to add field ${finalFieldName}:`, e);
        failures.push({ field_name: finalFieldName, reason });
        failed++;
      }
    }

    setAiSaving(false);

    if (added + addedColumns > 0) {
      setFieldsDirty(true);
      const parts = [];
      if (added > 0) parts.push(`${added} new field${added !== 1 ? "s" : ""}`);
      if (addedColumns > 0)
        parts.push(`${addedColumns} column${addedColumns !== 1 ? "s" : ""} to existing tables`);
      toast.success(
        "Fields added",
        `Added ${parts.join(" and ")}.${failed > 0 ? ` ${failed} failed.` : ""}`,
      );
    } else if (failed > 0) {
      toast.error("Failed", `All ${failed} fields failed to save.`);
    } else {
      toast.info(
        "No new fields",
        "All mapped fields already exist in this template.",
      );
    }

    // Surface exactly which field(s) failed and why, instead of silently
    // swallowing it to the console - this was the reported "1 of 54 AI
    // fields fails to create" symptom with no visible cause.
    if (failures.length > 0) {
      failures.forEach(({ field_name, reason }) => {
        toast.error(`Field "${field_name}" not saved`, reason);
      });
    }

    // Save template metadata (source documents, prompt, method) BEFORE
    // reloading the template so the refreshed state includes this data.
    // Do NOT overwrite creation_method for NS/BC templates — only set for generic templates
    try {
      const metaPayload = {
        name: template.name,
        description: template.description,
        source_documents: JSON.stringify(aiFiles.map((f) => f.name)),
        creation_prompt: aiPrompt || null,
      };
      if (
        !isNsTemplate &&
        template.template_creation_method !== "bc_purchase_order"
      ) {
        metaPayload.creation_method = "ai_suggest";
      }
      await templatesApi.update(id, metaPayload);
    } catch (metaErr) {
      console.warn("Failed to save template metadata:", metaErr);
    }

    // Reload template (now includes saved source_documents + creation_method)
    await loadTemplate();

    // Mark the job consumed so the panel doesn't reappear on reopen -
    // regardless of whether ALL, SOME (partial accept, including any
    // per-field failures above), or the fallback sync path (no job id)
    // was used. Best-effort: if this call fails, the panel may reappear
    // once more, but the accept itself has already succeeded.
    if (aiCurrentJobId) {
      try {
        await jobsApi.consume(aiCurrentJobId);
      } catch (consumeErr) {
        console.warn("Failed to mark suggestion job as consumed:", consumeErr);
      }
    }

    // Clear suggestion state
    setAiResult(null);
    setAiFiles([]);
    setAiPrompt("");
    setShowAiSuggest(false);
    setAiCurrentJobId(null);
  };

  // Discard must also tell the backend the suggestion was dismissed. Clearing
  // React state alone left the field_suggestion job unconsumed, so the restore
  // effect showed the same suggestions again every time the page was opened.
  // Leaving the page WITHOUT discarding still keeps them (the job stays
  // unconsumed), which is the intended behaviour.
  const handleDiscardSuggested = () => {
    if (aiCurrentJobId) {
      jobsApi
        .consume(aiCurrentJobId)
        .catch((e) => console.warn("Failed to mark suggestion job as dismissed:", e));
    }
    setAiCurrentJobId(null);
    setAiResult(null);
    setAiSelected({});
    setAiFiles([]);
  };

  const [seedingNsFields, setSeedingNsFields] = useState(false);
  const handleSeedStandardNsFields = async () => {
    setSeedingNsFields(true);
    const existingNames = new Set(
      (template?.fields || []).map((f) => f.field_name),
    );
    const isBidTemplate =
      (template?.ns_entity_type || template?.template_ns_entity_type) === "vendor_bid";
    const standardFields = isBidTemplate ? STANDARD_NS_BID_FIELDS : STANDARD_NS_PO_FIELDS;
    let added = 0,
      failed = 0;
    for (const field of standardFields) {
      if (existingNames.has(field.field_name)) continue;
      try {
        await templatesApi.addField(id, {
          display_name: field.display_name,
          field_name: field.field_name,
          field_type: field.field_type,
          category: field.category,
          description: field.description,
          ns_field: field.ns_key || null,
          ...(field.field_type === "array_of_objects" && field.columns
            ? {
                columns: field.columns,
                field_values: JSON.stringify(field.columns),
              }
            : {}),
        });
        added++;
      } catch (e) {
        if (e?.response?.status === 409) continue;
        console.error(
          `Failed to add standard NS field ${field.field_name}:`,
          e,
        );
        failed++;
      }
    }
    setSeedingNsFields(false);
    if (added > 0) {
      setFieldsDirty(true);
      toast.success(
        isBidTemplate ? "Standard Special Pricing fields added" : "Standard NetSuite PO fields added",
        `Added ${added} field${added !== 1 ? "s" : ""}.${failed > 0 ? ` ${failed} failed.` : ""}`,
      );
      await loadTemplate();
    } else if (failed > 0) {
      toast.error("Failed", `All ${failed} fields failed to save.`);
    } else {
      toast.info(
        "Already present",
        isBidTemplate
          ? "All standard Special Pricing fields already exist in this template."
          : "All standard NetSuite PO fields already exist in this template.",
      );
    }
  };

  // Lets the user add a field the AI missed directly into the review list
  // (next to "Accept Mapped NS Fields"), so it's created together with the
  // rest of the batch on accept instead of requiring a separate round trip.
  const manualFieldCounter = useRef(0);
  const handleAddManualField = () => {
    manualFieldCounter.current += 1;
    const manualId = `manual_${manualFieldCounter.current}_${Math.random().toString(36).slice(2, 6)}`;
    const newField = {
      field_name: manualId,
      display_name: "",
      field_type: "text",
      category:
        isNsTemplate
          ? (template?.ns_entity_type || template?.template_ns_entity_type) === "vendor_bid"
            ? "Bid Details"
            : "NetSuite PO"
          : "",
      description: "",
      sample_value: "",
      confidence: null,
      ns_key: null,
      _manual: true,
      _manualId: manualId,
      _editing: true,
    };
    setAiResult((prev) =>
      prev
        ? {
            ...prev,
            fields: [...(prev.fields || []), newField],
            total_fields: (prev.total_fields || 0) + 1,
          }
        : prev,
    );
    setAiSelected((prev) => ({ ...prev, [manualId]: true }));
  };

  // Only what the user can see and add: not hidden optional extras, not
  // what the template already has.
  const toggleSelectAll = () => {
    if (!aiResult?.fields) return;
    const selectable = aiResult.fields.filter(
      (f) =>
        f.placement?.action !== "exists" &&
        (aiShowOptional || !isOptionalSuggestion(f)),
    );
    const allSelected = selectable.every((f) => aiSelected[f.field_name]);
    setAiSelected((prev) => {
      const next = { ...prev };
      selectable.forEach((f) => {
        next[f.field_name] = !allSelected;
      });
      return next;
    });
  };

  // Re-run the suggestion with the user's answers to its questions; the
  // documents aren't read again (see templates_api.answer_suggestion_questions).
  const handleApplyAnswers = async (answers) => {
    if (!aiCurrentJobId) return;
    try {
      setAiApplyingAnswers(true);
      const res = await templatesApi.answerSuggestionQuestions(aiCurrentJobId, answers);
      aiRefineParentRef.current = aiCurrentJobId;
      pollSuggestionJob(res.data.job_id);
    } catch (e) {
      setAiApplyingAnswers(false);
      toast.error(
        "Could not update the suggestions",
        e?.response?.data?.error || "Please try again.",
      );
    }
  };

  const updateAiColumn = (fieldName, key, patch) =>
    setAiResult((prev) =>
      prev
        ? {
            ...prev,
            fields: prev.fields.map((f) =>
              f.field_name === fieldName
                ? {
                    ...f,
                    columns: (f.columns || []).map((c) =>
                      c.key === key ? { ...c, ...patch } : c,
                    ),
                  }
                : f,
            ),
          }
        : prev,
    );

  // Optional extras go last, behind a toggle, so the main suggestions aren't
  // buried under footer details.
  const aiAllFields = (aiResult?.fields || []).map((field, idx) => ({ field, idx }));
  const aiOptionalFields = aiAllFields.filter(({ field }) => isOptionalSuggestion(field));
  const aiVisibleFields = [
    ...aiAllFields.filter(({ field }) => !isOptionalSuggestion(field)),
    ...(aiOptionalFields.length ? [{ separator: true }] : []),
    ...(aiShowOptional ? aiOptionalFields : []),
  ];

  const aiLearningEnabled = !!template?.ai_learning_enabled;

  // Settings tab: Duplicate Handling / Duplicate Webhook / Mandate Review /
  // External Review / Connector Delivery Mode are staged locally and only
  // sent to the server via handleSaveSettings — see the "Save Changes" bar
  // rendered after these controls.
  const savedSettings = {
    allow_reprocessing: template?.allow_reprocessing !== false,
    duplicate_webhook_enabled: !!template?.duplicate_webhook_enabled,
    mandate_review: !!template?.mandate_review,
    external_review: !!template?.external_review,
    webhook_update_mode: template?.webhook_update_mode || "append",
    date_order: template?.date_order || "day_first",
  };
  const effectiveSettings = pendingSettings || savedSettings;
  const settingsHasDirtyEdits =
    pendingSettings !== null &&
    Object.keys(savedSettings).some(
      (key) => pendingSettings[key] !== savedSettings[key],
    );

  const updatePendingSetting = (field, value) => {
    setPendingSettings((prev) => ({
      ...(prev || savedSettings),
      [field]: value,
    }));
  };

  const handleToggleDuplicatePolicy = () => {
    updatePendingSetting(
      "allow_reprocessing",
      !effectiveSettings.allow_reprocessing,
    );
  };

  const handleToggleDuplicateWebhook = () => {
    updatePendingSetting(
      "duplicate_webhook_enabled",
      !effectiveSettings.duplicate_webhook_enabled,
    );
  };

  const handleToggleMandateReview = () => {
    updatePendingSetting("mandate_review", !effectiveSettings.mandate_review);
  };

  const handleToggleExternalReview = () => {
    updatePendingSetting(
      "external_review",
      !effectiveSettings.external_review,
    );
  };

  const handleChangeWebhookUpdateMode = (nextMode) => {
    updatePendingSetting("webhook_update_mode", nextMode);
  };

  const handleChangeDateOrder = (nextOrder) => {
    updatePendingSetting("date_order", nextOrder);
  };

  const handleDiscardSettings = () => setPendingSettings(null);

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await templatesApi.update(id, {
        name: template.name,
        description: template.description,
        ...effectiveSettings,
      });
      setTemplate((prev) => ({ ...prev, ...effectiveSettings }));
      setPendingSettings(null);
      toast.success(
        "Settings updated",
        "Template settings have been saved.",
      );
    } catch (e) {
      console.error(e);
      toast.error(
        "Update failed",
        e?.response?.data?.error || "Could not update template settings",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToggleAiLearning = async () => {
    const prevAiLearningEnabled = aiLearningEnabled;
    const nextAiLearningEnabled = !prevAiLearningEnabled;
    try {
      setTemplate((prev) => ({
        ...prev,
        ai_learning_enabled: nextAiLearningEnabled,
      }));
      setSavingAiLearningToggle(true);
      await templatesApi.update(id, {
        name: template.name,
        description: template.description,
        ai_learning_enabled: nextAiLearningEnabled,
      });
      toast.success(
        "AI learning updated",
        nextAiLearningEnabled
          ? "Template AI learning is ON. Saved corrections can now be learned and applied."
          : "Template AI learning is OFF. New learning rules will not be saved or applied.",
      );
      if (activeTab === "learning") {
        loadTemplateLearningRules();
      }
    } catch (e) {
      setTemplate((prev) => ({
        ...prev,
        ai_learning_enabled: prevAiLearningEnabled,
      }));
      console.error(e);
      toast.error(
        "Update failed",
        e?.response?.data?.error || "Could not update AI learning setting",
      );
    } finally {
      setSavingAiLearningToggle(false);
    }
  };

  // ===== End AI Suggest Handlers =====

  if (loading) {
    return (
      <TemplateDetailSkeleton />
    );
  }

  if (error || !template) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error || "Template not found"}
        </div>
        <div className="mt-4">
          <button
            onClick={() => navigateWithDirtyGuard("/templates")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link
            to="/templates"
            onClick={(e) => {
              if (!hasUnsavedFieldChanges) return;
              e.preventDefault();
              navigateWithDirtyGuard("/templates");
            }}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Templates
          </Link>

          {editingMeta ? (
            <div className="space-y-2 mt-1">
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveMetaEdit();
                  if (e.key === "Escape") cancelMetaEdit();
                }}
                className="text-2xl font-bold w-full px-2 py-1 rounded-lg border border-navy-400 dark:border-navy-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/30"
              />
              <input
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") cancelMetaEdit();
                }}
                placeholder="Add a description (optional)"
                className="text-sm w-full px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-navy-500/20"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={saveMetaEdit}
                  disabled={savingMeta || !editName.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-navy-800 text-white text-xs font-medium disabled:opacity-50"
                >
                  {savingMeta ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                  Save
                </button>
                <button
                  onClick={cancelMetaEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="group">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {template.name}
                </h1>
                <TemplateOpCoChip template={template} />
                <TemplateOpCoActions template={template} onChanged={loadTemplate} />
                {canEditMeta && (
                  <button
                    onClick={openMetaEdit}
                    title="Edit template name"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {template.description || "No description"}
              </p>
            </div>
          )}
        </div>

        {activeTab === "fields" && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                if (isReader) return;
                const closing = showAiSuggest;
                // Case (b): user is explicitly dismissing/closing the panel
                // without accepting anything. Mark the job consumed so it
                // doesn't reappear on next template open.
                if (closing && aiResult && aiCurrentJobId) {
                  jobsApi.consume(aiCurrentJobId).catch((e) =>
                    console.warn("Failed to mark suggestion job as consumed:", e),
                  );
                  setAiCurrentJobId(null);
                }
                setShowAiSuggest(!showAiSuggest);
              }}
              disabled={isReader}
              title={isReader ? READ_ONLY_MESSAGE : undefined}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isReader
                  ? "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  : showAiSuggest
                    ? "bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700"
                    : aiResult
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : aiJobId
                        ? "bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700"
                        : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
            >
              {aiJobId && <Loader2 className="h-4 w-4 animate-spin" />}
              {!aiJobId && <Sparkles className="h-4 w-4" />}
              {showAiSuggest
                ? "Close"
                : aiResult
                  ? `Review ${aiResult.total_fields} AI Fields`
                  : aiJobId
                    ? "AI Processing..."
                    : isNsTemplate
                      ? "Upload NetSuite PDF"
                      : isBcTemplate
                        ? `Upload ${bcEntityTypeDisplayName} PDF`
                        : "AI Suggest Fields"}
            </button>
            <div className="relative">
              <button
                onClick={() => {
                  if (isReader) return;
                  setAddFieldMenuOpen((prev) => !prev);
                }}
                disabled={isReader}
                title={isReader ? READ_ONLY_MESSAGE : undefined}
                aria-haspopup="menu"
                aria-expanded={addFieldMenuOpen}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
                Add Field
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </button>
              {addFieldMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAddFieldMenuOpen(false)} />
                  <div
                    role="menu"
                    className="absolute right-0 z-50 mt-1.5 w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-1"
                  >
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => {
                        setAddFieldMenuOpen(false);
                        setEditingField(null);
                        setFieldModalOpen(true);
                      }}
                      className="w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <FileText className="h-4 w-4 mt-0.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      <span>
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Read by AI</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">From the attachments or the e-mail text, guided by a description</span>
                      </span>
                    </button>
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => {
                        setAddFieldMenuOpen(false);
                        setEmailPickerOpen(true);
                      }}
                      className="w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <Mail className="h-4 w-4 mt-0.5 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                      <span>
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">E-mail details</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">Copied from the e-mail&apos;s sender, subject, date… not its text. Nothing to set up</span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
            <input
              type="file"
              accept=".json"
              ref={importInputRef}
              onChange={handleImportFields}
              className="hidden"
            />
            <button
              onClick={() => !isReader && importInputRef.current?.click()}
              disabled={isReader || importing}
              title={isReader ? READ_ONLY_MESSAGE : undefined}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Import Fields
            </button>
            {features.netsuite && template?.template_ns_enabled === true && (
              <>
                <button
                  onClick={async () => {
                    setExportingJson(true);
                    try {
                      await exportTemplateJson(template.id || id, template.name);
                    } catch (err) {
                      toast.error("Export failed", err?.response?.data?.error || err.message);
                    } finally {
                      setExportingJson(false);
                    }
                  }}
                  disabled={exportingJson}
                  title="Download this template as a JSON file you can edit and import again"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportingJson ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export JSON
                </button>
                <button
                  onClick={() => !isReader && setJsonModalOpen(true)}
                  disabled={isReader}
                  title={isReader ? READ_ONLY_MESSAGE : "Replace this template's record type, mappings and line items from a JSON file"}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileJson className="h-4 w-4" />
                  Update from JSON
                </button>
              </>
            )}
            {(template?.fields || []).length > 0 && (
              <button
                onClick={() => !isReader && handleDeleteAllFields()}
                disabled={isReader}
                title={isReader ? READ_ONLY_MESSAGE : undefined}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-4 w-4" />
                Delete All Fields
              </button>
            )}
            <button
              onClick={() => !isReader && handleBuildAnalyzer()}
              disabled={
                isReader ||
                buildingAnalyzer ||
                savingFieldToggles ||
                !hasUnsavedFieldChanges ||
                (template?.fields || []).length === 0
              }
              title={
                isReader
                  ? READ_ONLY_MESSAGE
                  : !hasUnsavedFieldChanges
                    ? "No field changes to save"
                    : undefined
              }
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {buildingAnalyzer ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Bot className="h-4 w-4" />
              )}
              Save
            </button>
          </div>
        )}
      </div>

      {/* Template Assignments (always visible) */}
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <FolderCog className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
          <span className="text-gray-500 dark:text-gray-400 text-xs">
            Manager:
          </span>
          {template?.manager_name ? (
            <span className="font-medium text-gray-900 dark:text-gray-100 text-xs">
              {template.manager_name}
            </span>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
              Unassigned
            </span>
          )}
          {userRole === "admin" && (
            <button
              onClick={() => openAssignModal("manager")}
              className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline ml-1"
            >
              Change
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <UserCheck className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
          <span className="text-gray-500 dark:text-gray-400 text-xs">
            Approver:
          </span>
          {template?.approver_name ? (
            <span className="font-medium text-gray-900 dark:text-gray-100 text-xs">
              {template.approver_name}
            </span>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
              Unassigned
            </span>
          )}
          {(userRole === "admin" ||
            (userRole === "template_manager" &&
              template?.manager_id === authUser?.id)) && (
            <button
              onClick={() => openAssignModal("approver")}
              className="text-[11px] text-green-600 dark:text-green-400 hover:underline ml-1"
            >
              Change
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1.5 inline-flex gap-1">
        {/* Fields tab — always visible */}
        <button
          onClick={() => setActiveTab("fields")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === "fields"
              ? "bg-navy-800 text-white"
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          Fields
        </button>
        {/* Webhooks tab — HIDDEN on BC templates */}
        {connectorsEnabled &&
          template?.template_creation_method !== "bc_purchase_order" && (
          <button
            onClick={() => setActiveTab("webhooks")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "webhooks"
                ? "bg-navy-800 text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
          >
            Connectors
          </button>
        )}
        {/* Output Destinations tab — FTP/SFTP CSV export links (AC4/AC5), HIDDEN on BC templates */}
        {connectorsEnabled &&
          template?.template_creation_method !== "bc_purchase_order" && (
          <button
            onClick={() => setActiveTab("output_destinations")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "output_destinations"
                ? "bg-navy-800 text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
          >
            Output Destinations
          </button>
        )}
        <button
          onClick={() => setActiveTab("settings")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === "settings"
              ? "bg-navy-800 text-white"
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => setActiveTab("learning")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === "learning"
              ? "bg-navy-800 text-white"
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          AI Learning
        </button>
        {(isAdmin || isReader) && (
          <button
            onClick={() => setActiveTab("retention")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "retention"
                ? "bg-navy-800 text-white"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
          >
            Retention
          </button>
        )}
        {/* BC tab — shown on any Business Central template (see isBcTemplate) */}
        {features.business_central && isBcTemplate && (
            <button
              onClick={() => setActiveTab("bc")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "bc"
                  ? "bg-navy-800 text-white"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              Business Central
            </button>
          )}
        {/* NS tab — ONLY on templates with creation_method = ns_purchase_order AND ns_enabled */}
        {features.netsuite &&
          template?.template_ns_enabled === true &&
          isNsTemplate && (
            <button
              onClick={() => setActiveTab("ns")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "ns"
                  ? "bg-purple-700 text-white"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              NetSuite
            </button>
          )}
      </div>

      {/* Template Info Panel */}
      {activeTab === "settings" && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Created by */}
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-md bg-blue-50 dark:bg-blue-900/30">
                <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Created by
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                  {template.created_by_name || "Unknown"}
                </p>
              </div>
            </div>

            {/* Created date */}
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-md bg-green-50 dark:bg-green-900/30">
                <Calendar className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Created
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                  {template.created_at ? formatDate(template.created_at) : "-"}
                </p>
              </div>
            </div>

            {/* Last updated */}
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-md bg-amber-50 dark:bg-amber-900/30">
                <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Last updated
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                  {template.updated_at ? formatDate(template.updated_at) : "-"}
                  {template.modified_by_name && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                      by {template.modified_by_name}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Creation method */}
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-md bg-purple-50 dark:bg-purple-900/30">
                <Sparkles className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Method
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                  {{
                    ai_suggest: "AI Suggested",
                    plugin_auto: "Plugin (Auto-created)",
                    json_import: "Imported from JSON",
                    from_template: "Created from Existing Template",
                    manual: "Created Manually",
                  }[template.creation_method] ||
                    template.creation_method ||
                    "Created Manually"}
                </p>
              </div>
            </div>

            {/* Created from existing template */}
            {template.source_template_id && (
              <div className="flex items-start gap-2.5">
                <div className="p-1.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30">
                  <FileStack className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Created from
                  </p>
                  <Link
                    to={`/templates/${template.source_template_id}`}
                    className="inline-flex items-center gap-1.5 mt-0.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-900/60 transition-colors"
                  >
                    <FileStack className="h-3.5 w-3.5" />
                    {template.source_template_name ||
                      `Template #${template.source_template_id}`}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Source documents - prominent display */}
          {(() => {
            let docs = [];
            try {
              if (template.source_documents) {
                const parsed = JSON.parse(template.source_documents);
                if (Array.isArray(parsed)) docs = parsed;
              }
            } catch {
              /* ignore */
            }

            return (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <FileText className="h-3 w-3 text-blue-500 dark:text-blue-400" />
                  <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                    Source Documents
                  </p>
                </div>
                {docs.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {docs.map((doc, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/25 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50"
                      >
                        <FileText className="h-3 w-3 shrink-0" />
                        {doc}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                    No source documents - template was created manually
                  </p>
                )}
              </div>
            );
          })()}

          {/* Prompt used */}
          {template.creation_prompt && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2 mb-1.5">
                <MessageSquare className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  AI Prompt Used
                </p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                "{template.creation_prompt}"
              </p>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Duplicate Handling
                </span>
                <div className="relative group flex-shrink-0">
                  <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-64 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    Turn off to prevent re-uploading the same file hash for this
                    template via the plugin.
                    <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
              </div>
              <Toggle
                checked={effectiveSettings.allow_reprocessing}
                onChange={handleToggleDuplicatePolicy}
                disabled={savingSettings || isReader}
              />
            </div>

            {/* Sub-toggle: only visible when Duplicate Handling is ON */}
            {effectiveSettings.allow_reprocessing && (
              <div className="mt-3 ml-1 pl-4 border-l-2 border-green-200 dark:border-green-800/60">
                <div className="flex items-center justify-between gap-3 py-0.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Trigger Webhook for Duplicates
                    </span>
                    <div className="relative group flex-shrink-0">
                      <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-64 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        When a duplicate PDF is re-uploaded via the plugin, this
                        controls whether the webhook (CSV export) fires for the
                        duplicate data.
                        <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                      </div>
                    </div>
                  </div>
                  <Toggle
                    checked={effectiveSettings.duplicate_webhook_enabled}
                    onChange={handleToggleDuplicateWebhook}
                    disabled={savingSettings || isReader}
                    size="sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Mandate Review Before Export */}
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Mandate Review Before Export
                </span>
                <div className="relative group flex-shrink-0">
                  <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-72 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    When enabled, requests go to the queue for manual review -
                    user must publish to trigger CSV export. When disabled,
                    extraction auto-approves and fires the webhook immediately.
                    <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
              </div>
              <Toggle
                checked={effectiveSettings.mandate_review}
                onChange={handleToggleMandateReview}
                disabled={savingSettings || isReader}
              />
            </div>
          </div>

          {/* Who reviews - Xtract or a connected application */}
          {effectiveSettings.mandate_review && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Review In Connected App
                  </span>
                  <div className="relative group flex-shrink-0">
                    <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-72 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      When enabled, requests are reviewed and published from the
                      application that submitted them, and stay out of Xtract's
                      approvals queue so two people cannot publish the same
                      request. Administrators can still publish here if that
                      application is unavailable.
                      <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                    </div>
                  </div>
                </div>
                <Toggle
                  checked={effectiveSettings.external_review}
                  onChange={handleToggleExternalReview}
                  disabled={savingSettings || isReader}
                />
              </div>
            </div>
          )}

          {/* Connector Delivery Mode */}
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Connector Delivery Mode
                </span>
                <div className="relative group flex-shrink-0">
                  <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-72 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    Upsert: sends the same request ID each time so receivers can
                    update the existing row. Append: fires a new payload on
                    every save, inserting a new row each time.
                    <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
              </div>
              <select
                value={effectiveSettings.webhook_update_mode}
                onChange={(e) => handleChangeWebhookUpdateMode(e.target.value)}
                disabled={savingSettings || isReader}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              >
                <option value="append">Append</option>
                <option value="upsert">Upsert</option>
              </select>
            </div>
          </div>

          {/* Dates on Documents - how dates are READ (GitHub #780) */}
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Dates on Documents
                </span>
                <div className="relative group flex-shrink-0">
                  <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-72 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    How this template's documents write dates, so 12/02/2026 is
                    read correctly. If a document shows otherwise (a date like
                    01/25/2026 can only be month first), its dates are read its
                    way and the request is flagged for review. How dates are
                    written out is each date field's Output Format.
                    <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
              </div>
              <select
                value={effectiveSettings.date_order}
                onChange={(e) => handleChangeDateOrder(e.target.value)}
                disabled={savingSettings || isReader}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              >
                {DATE_ORDERS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Save Changes bar */}
          {settingsHasDirtyEdits && (
            <div className="mt-4 flex items-center justify-between rounded-xl border border-[var(--token-primary,#1946FA)]/20 bg-[var(--token-primary,#1946FA)]/5 px-5 py-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  You have unsaved changes
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDiscardSettings}
                  disabled={isReader || savingSettings}
                  title={isReader ? READ_ONLY_MESSAGE : undefined}
                  className="btn btn-secondary text-xs !px-3 !py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Discard
                </button>
                <button
                  onClick={handleSaveSettings}
                  disabled={isReader || savingSettings}
                  title={isReader ? READ_ONLY_MESSAGE : undefined}
                  className="btn btn-primary text-xs !px-4 !py-1.5 gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingSettings ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assignment Modal */}
      {assignModal.open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ width: "100vw", height: "100vh" }}
          >
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setAssignModal({ open: false, type: null })}
            />
            <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-5">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                {assignModal.type === "manager"
                  ? "Assign Template Manager"
                  : "Assign Approver"}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Select a user to assign as{" "}
                {assignModal.type === "manager"
                  ? "template manager"
                  : "approver"}{" "}
                for this template.
              </p>
              {loadingAssignable ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {assignableUsers.length === 0 && (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                      No eligible users found
                    </p>
                  )}
                  {assignableUsers.map((u) => {
                    const isCurrentAssignee =
                      assignModal.type === "manager"
                        ? u.id === template?.manager_id
                        : u.id === template?.approver_id;
                    return (
                      <button
                        key={u.id}
                        onClick={() => handleAssign(u.id)}
                        disabled={savingAssignment || isCurrentAssignee}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                          isCurrentAssignee
                            ? "bg-gray-100 dark:bg-gray-800 opacity-50 cursor-default"
                            : "hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
                          {(u.username || u.email || "?")[0].toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {u.username || u.email}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {u.email}
                          </p>
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 capitalize">
                          {u.role}
                        </span>
                        {isCurrentAssignee && (
                          <span className="text-[10px] text-gray-400 uppercase">
                            Current
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                {(assignModal.type === "manager"
                  ? template?.manager_id
                  : template?.approver_id) && (
                  <button
                    onClick={() => {
                      handleUnassign(assignModal.type);
                      setAssignModal({ open: false, type: null });
                    }}
                    className="text-xs text-red-500 hover:text-red-600"
                  >
                    Remove current
                  </button>
                )}
                <button
                  onClick={() => setAssignModal({ open: false, type: null })}
                  className="ml-auto text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {activeTab === "settings" && (
        <TemplateAccess
          templateId={id}
          templateName={template?.name}
          inline
          readOnly={isReader}
          managerId={template?.manager_id}
          approverId={template?.approver_id}
        />
      )}

      {/* Danger zone — deliberately at the BOTTOM of Settings, not in the header
          toolbar. The toolbar already holds "Delete All Fields"; putting "Delete
          Template" beside it makes a one-character misread destroy the whole
          template. Deleting also removes the Azure CU analyzer, which is not
          recoverable, so it gets its own section and its own confirmation. */}
      {activeTab === 'settings' && canManageTemplates && (
        <div className="mt-6 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20 p-5">
          <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">Danger zone</h3>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Delete this template</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Removes the template and its {template?.fields?.length ?? 0} field
                {(template?.fields?.length ?? 0) === 1 ? '' : 's'}, and deletes its trained
                analyzer from Azure. Requests already created from it are not deleted.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDeleteTemplate}
              disabled={isReader || deletingTemplate}
              title={isReader ? READ_ONLY_MESSAGE : 'Delete this template'}
              className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg flex-shrink-0 transition-colors ${
                isReader || deletingTemplate
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {deletingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deletingTemplate ? 'Deleting…' : 'Delete template'}
            </button>
          </div>
        </div>
      )}

      {activeTab === "learning" && (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Template AI Learning Rules
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Rules are attached to template fields and applied in future
                  extractions.
                </p>
              </div>
              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  Template AI Learning
                </span>
                <div className="relative group flex-shrink-0">
                  <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 cursor-default" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 w-72 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-relaxed shadow-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    Enables template-level learning from user corrections. Rules
                    are stored per field for this template and applied after
                    validation in future processing.
                    <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                  </div>
                </div>
                <Toggle
                  checked={aiLearningEnabled}
                  onChange={handleToggleAiLearning}
                  disabled={isReader || savingAiLearningToggle}
                  size="sm"
                />
              </div>
            </div>
            <button
              onClick={loadTemplateLearningRules}
              disabled={loadingLearningRules || savingAiLearningToggle}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {loadingLearningRules ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Refresh
            </button>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
            {loadingLearningRules ? (
              <div className="py-10 text-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Loading learning rules...
                </p>
              </div>
            ) : learningRulesError ? (
              <div className="rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {learningRulesError}
                </p>
              </div>
            ) : learningRules.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No AI learning rules found for this template.
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Save requests with AI learning enabled to populate this list.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Field
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Category
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Learning Instruction
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Last Corrected Value
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {learningRules.map((rule) => {
                      const fieldMeta =
                        templateFieldById[String(rule.template_field_id)] ||
                        null;
                      const fieldLabel =
                        fieldMeta?.display_name ||
                        rule.display_name ||
                        fieldMeta?.field_name ||
                        rule.field_name ||
                        `Field #${rule.template_field_id}`;
                      const fieldKey =
                        fieldMeta?.field_name || rule.field_name || "-";
                      const category =
                        fieldMeta?.category_display_name ||
                        fieldMeta?.category ||
                        "Uncategorized";

                      return (
                        <tr
                          key={rule.id}
                          className="border-t border-gray-100 dark:border-gray-800 align-top"
                        >
                          <td className="px-3 py-2.5">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {fieldLabel}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
                              {fieldKey}
                            </p>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300">
                            {category}
                          </td>
                          <td className="px-3 py-2.5">
                            <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words max-w-[34rem]">
                              {rule.instruction || "-"}
                            </p>
                          </td>
                          <td className="px-3 py-2.5">
                            <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words max-w-[16rem]">
                              {rule.last_corrected_value || "-"}
                            </p>
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                rule.is_active
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                              }`}
                            >
                              {rule.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {rule.updated_at
                              ? formatDate(rule.updated_at)
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Retention Tab */}
      {activeTab === "retention" && (
        <div className="space-y-5">
          {/* Sub-tabs: Policies / History */}
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg w-fit">
            {[
              { key: "policies", label: "Policies" },
              { key: "recycle_bin", label: "Recycle Bin" },
              { key: "history", label: "History" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setRetentionSubTab(tab.key)}
                className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  retentionSubTab === tab.key
                    ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Policies Sub-tab */}
          {retentionSubTab === "policies" && (
            <div className="space-y-5">
              {/* Info banner */}
              <div className="flex items-start gap-3 rounded-xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-800 bg-gray-50/50 dark:bg-gray-800/30 px-4 py-3">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  Retention policies auto-delete old data hourly. Items are
                  moved to the{" "}
                  <strong className="text-gray-700 dark:text-gray-300">
                    recycle bin
                  </strong>{" "}
                  for 7 days before permanent deletion.
                </div>
              </div>

              {/* Quick Setup bar */}
              <div className="flex items-center justify-between rounded-xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-800 bg-white dark:bg-gray-900 px-5 py-3">
                <div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    Quick Setup
                  </span>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Apply the same retention period to all data types
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={1}
                      max={9999}
                      value={retentionBulkDays}
                      onChange={(e) =>
                        setRetentionBulkDays(
                          Math.max(1, parseInt(e.target.value) || 1),
                        )
                      }
                      disabled={isReader}
                      className="w-[64px] px-2 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-center focus:ring-2 focus:ring-[var(--token-primary,#1946FA)]/20 focus:border-[var(--token-primary,#1946FA)] disabled:opacity-40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="text-[11px] text-gray-400">days</span>
                  </div>
                  <button
                    onClick={handleRetentionBulkApply}
                    disabled={isReader || retentionBulkSaving}
                    title={isReader ? READ_ONLY_MESSAGE : undefined}
                    className="btn btn-primary text-xs !px-3 !py-1.5 gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {retentionBulkSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Zap className="h-3.5 w-3.5" />
                    )}
                    Apply All
                  </button>
                  <button
                    onClick={handleRetentionReset}
                    disabled={isReader || retentionBulkSaving}
                    title={isReader ? READ_ONLY_MESSAGE : undefined}
                    className="btn btn-secondary text-xs !px-3 !py-1.5 gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Reset
                  </button>
                  <button
                    onClick={handleRetentionRunNow}
                    disabled={
                      isReader || retentionBulkSaving || retentionRunNowSaving
                    }
                    title={isReader ? READ_ONLY_MESSAGE : undefined}
                    className="btn btn-secondary text-xs !px-3 !py-1.5 gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {retentionRunNowSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Activity className="h-3.5 w-3.5" />
                    )}
                    Run Cleanup Now
                  </button>
                </div>
              </div>

              {retentionLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-12 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse"
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-5">
                  {RETENTION_GROUPS.map((group) => {
                    const groupEntities = RETENTION_ENTITY_TYPES.filter(
                      (e) =>
                        group.keys.includes(e.key) &&
                        (connectorsEnabled || e.key !== "webhook_deliveries"),
                    );
                    return (
                      <div key={group.title}>
                        {/* Group label */}
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {group.title}
                          </h3>
                          <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                        </div>
                        {/* Grouped items */}
                        <div className="space-y-3">
                          {groupEntities.map((entityMeta) => (
                            <RetentionPolicyRow
                              key={entityMeta.key}
                              entityMeta={entityMeta}
                              policy={getRetentionPolicy(entityMeta.key)}
                              localState={
                                retentionLocalEdits[entityMeta.key] || {
                                  days: entityMeta.defaultDays,
                                  enabled: false,
                                  deleteBlobs: true,
                                  excludeStatuses: [],
                                  templateId: parseInt(id),
                                  _dirty: false,
                                }
                              }
                              onLocalChange={handleRetentionLocalChange}
                              onPreview={handleRetentionPreview}
                              onPreviewItems={handleRetentionPreviewItems}
                              readOnly={isReader}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Save Changes bar */}
              {retentionHasDirtyEdits && (
                <div className="flex items-center justify-between rounded-xl border border-[var(--token-primary,#1946FA)]/20 bg-[var(--token-primary,#1946FA)]/5 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      You have unsaved changes (
                      {
                        Object.values(retentionLocalEdits).filter(
                          (e) => e._dirty,
                        ).length
                      }{" "}
                      {Object.values(retentionLocalEdits).filter(
                        (e) => e._dirty,
                      ).length === 1
                        ? "policy"
                        : "policies"}
                      )
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={loadRetentionPolicies}
                      disabled={isReader}
                      title={isReader ? READ_ONLY_MESSAGE : undefined}
                      className="btn btn-secondary text-xs !px-3 !py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Discard
                    </button>
                    <button
                      onClick={handleRetentionSaveClick}
                      disabled={isReader || retentionSaving}
                      title={isReader ? READ_ONLY_MESSAGE : undefined}
                      className="btn btn-primary text-xs !px-4 !py-1.5 gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {retentionSaving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              {/* How it works */}
              <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  <strong>How it works:</strong> The retention worker runs
                  hourly in the background. It checks each enabled policy, finds
                  data older than the configured days, and moves them to the
                  recycle bin. Items remain recoverable for 7 days before
                  permanent deletion. Items with protected statuses (pending, in
                  progress) are never deleted.
                </div>
              </div>

              {/* Data Storage Location Disclosure */}
              <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/10">
                <button
                  type="button"
                  onClick={() => setStorageDisclosureOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left"
                >
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-500 shrink-0" />
                    <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Data Storage Location Disclosure
                    </span>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 text-blue-500 transition-transform duration-200 ${storageDisclosureOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {storageDisclosureOpen && (
                  <div className="px-5 pb-4 space-y-3 text-xs text-blue-900 dark:text-blue-200 leading-relaxed border-t border-blue-200 dark:border-blue-800/50 pt-3">
                    <ul className="space-y-2.5 list-none">
                      <li>
                        <p className="font-medium text-blue-800 dark:text-blue-200 mb-0.5">
                          Storage Location
                        </p>
                        <p>
                          All retained data (documents, emails, processing
                          results, and associated metadata) is stored in{" "}
                          <strong>Microsoft Azure, UK South region</strong>.
                          Blob storage for uploaded files and extracted content
                          is co-located in the same region.
                        </p>
                      </li>
                      <li>
                        <p className="font-medium text-blue-800 dark:text-blue-200 mb-0.5">
                          Retention Duration
                        </p>
                        <p>
                          Data is retained according to the policies configured
                          above. Default retention periods range from 30 to 730
                          days depending on data type. Policies are fully
                          configurable per template.
                        </p>
                      </li>
                      <li>
                        <p className="font-medium text-blue-800 dark:text-blue-200 mb-0.5">
                          Data Lifecycle
                        </p>
                        <p>
                          When data exceeds its retention period, it is moved to
                          a <strong>recycle bin</strong> for a 7-day grace
                          period before permanent, irreversible deletion. Blob
                          storage files (if enabled) are deleted alongside
                          records.
                        </p>
                      </li>
                      <li>
                        <p className="font-medium text-blue-800 dark:text-blue-200 mb-0.5">
                          Compliance &amp; Security
                        </p>
                        <p>
                          Data is encrypted at rest (AES-256) and in transit
                          (TLS 1.2+). Storage complies with UK data residency
                          requirements. Audit logs of all deletions are retained
                          for accountability.
                        </p>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Sub-tab */}
          {retentionSubTab === "recycle_bin" && (
            <div className="space-y-5">
              <RetentionRecycleBinSection templateId={parseInt(id)} />
            </div>
          )}

          {retentionSubTab === "history" && (
            <div className="space-y-5">
              <RetentionDeletionLog templateId={parseInt(id)} />
            </div>
          )}

          {/* Confirmation modal */}
          {retentionShowConfirm &&
            createPortal(
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
                onClick={() => setRetentionShowConfirm(false)}
              >
                <div
                  className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg mx-4 p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start gap-3 mb-5">
                    <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 mt-0.5">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                        Save Retention Policies
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {dirtyRetentionEntries.length}{" "}
                        {dirtyRetentionEntries.length === 1
                          ? "policy"
                          : "policies"}{" "}
                        will be updated.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        {retentionImpactLoading
                          ? "Checking affected records..."
                          : `${totalRetentionImpact.toLocaleString()} records currently match these rules.`}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2.5 mb-5 max-h-[300px] overflow-y-auto pr-1">
                    {dirtyRetentionEntries.map(([key, state]) => {
                      const meta = RETENTION_ENTITY_TYPES.find(
                        (m) => m.key === key,
                      );
                      if (!meta) return null;
                      const affected = retentionConfirmImpact[key] || 0;
                      const impactLabel =
                        RETENTION_IMPACT_LABELS[key] ||
                        meta.label.toLowerCase();
                      return (
                        <div
                          key={key}
                          className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 px-3.5 py-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {meta.label}
                              </span>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {!state.enabled
                                  ? "Policy is OFF. No items will be deleted."
                                  : retentionImpactLoading
                                    ? "Calculating affected records..."
                                    : affected > 0
                                      ? `${affected.toLocaleString()} ${impactLabel} ${affected === 1 ? "is" : "are"} going to be deleted`
                                      : `Currently 0 ${impactLabel}. Future ${impactLabel} older than ${state.days} days will be deleted.`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2.5 flex-shrink-0">
                              <span className="text-xs font-medium text-gray-500">
                                {state.days} days
                              </span>
                              <span
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                  state.enabled
                                    ? "bg-[var(--token-primary,#1946FA)]/10 text-[var(--token-primary,#1946FA)]"
                                    : "bg-gray-200 dark:bg-gray-700 text-gray-500"
                                }`}
                              >
                                {state.enabled ? "ON" : "OFF"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 p-3.5 mb-5">
                    <p className="text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
                      <RotateCcw className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Deleted items can be{" "}
                        <strong>restored within 7 days</strong> from the Recycle
                        Bin tab.
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-3 justify-end">
                    <button
                      onClick={() => setRetentionShowConfirm(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRetentionSaveAll}
                      disabled={retentionSaving}
                      className="btn btn-primary gap-1.5 disabled:opacity-50"
                    >
                      {retentionSaving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                      Confirm & Save
                    </button>
                  </div>
                </div>
              </div>,
              document.body,
            )}
        </div>
      )}
      {features.business_central && activeTab === 'bc' && isBcTemplate && (
        <div className="space-y-4">
          {/* Sub-tabs inside the Business Central tab. Operations and
              configuration are different jobs done at different times — a
              reviewer checking "did today's documents post?" should not have to
              scroll past sync settings, and the settings cards should not grow
              an operations feed underneath them. */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1.5 inline-flex gap-1">
            <button
              onClick={() => setBcSubTab('dashboard')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                bcSubTab === 'dashboard'
                  ? 'bg-navy-800 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setBcSubTab('settings')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                bcSubTab === 'settings'
                  ? 'bg-navy-800 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Settings
            </button>
          </div>

          {bcSubTab === 'dashboard'
            ? <TemplateBCDashboard templateId={id} templateName={template?.name} />
            : <TemplateBCSettings templateId={id} templateName={template?.name} onSaved={refreshTemplateQuietly} />}
        </div>
      )}
      {features.netsuite &&
        activeTab === "ns" &&
        template?.template_ns_enabled === true &&
        isNsTemplate && (
          <div className="space-y-4">
            {/* Sub-tabs inside the NetSuite tab — same split as Business
                Central's: operations (dashboard) vs configuration (settings),
                so a reviewer checking "did today's documents post?" doesn't
                have to scroll past field mapping/lookup settings. */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1.5 inline-flex gap-1">
              <button
                onClick={() => setNsSubTab('dashboard')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  nsSubTab === 'dashboard'
                    ? 'bg-navy-800 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setNsSubTab('settings')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  nsSubTab === 'settings'
                    ? 'bg-navy-800 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                Settings
              </button>
              <button
                onClick={() => setNsSubTab('mappings')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  nsSubTab === 'mappings'
                    ? 'bg-navy-800 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                Name mappings
              </button>
            </div>

            {nsSubTab === 'dashboard'
              ? <TemplateNSDashboard templateId={id} templateName={template?.name} nsEntityType={nsEntityTypeForCopy} />
              : nsSubTab === 'mappings'
              ? <NSNameMappings />
              : <TemplateNSSettings templateId={id} templateName={template?.name} nsEntityType={nsEntityTypeForCopy} />}
          </div>
        )}

      {activeTab === "activity" && (
        <TemplateActivity templateId={id} templateName={template?.name} />
      )}

      {/* BC schema drift banner - new columns in BC not yet in this template */}
      {features.business_central &&
        activeTab === "fields" &&
        bcDrift?.has_drift && (
          <div className="rounded-lg border border-indigo-200 dark:border-indigo-800/40 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Building2 className="h-4 w-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
              <div>
                <p className="text-sm text-indigo-800 dark:text-indigo-200">
                  <span className="font-semibold">
                    {bcDrift.new_columns.length} new BC column
                    {bcDrift.new_columns.length !== 1 ? "s" : ""} detected
                  </span>{" "}
                  in Business Central that aren't in this template yet.
                </p>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                  {bcDrift.new_columns
                    .slice(0, 4)
                    .map((c) => c.label)
                    .join(", ")}
                  {bcDrift.new_columns.length > 4
                    ? ` +${bcDrift.new_columns.length - 4} more`
                    : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setBcDrift(null)}
                className="px-2.5 py-1.5 rounded-lg border border-indigo-300 dark:border-indigo-700 text-xs text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/40"
              >
                Dismiss
              </button>
              <button
                onClick={handleApplyBcDrift}
                disabled={bcDriftApplying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium whitespace-nowrap disabled:opacity-50"
              >
                {bcDriftApplying ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Building2 className="h-3.5 w-3.5" />
                )}
                Add to Template
              </button>
            </div>
          </div>
        )}

      {/* Template Connectors */}
      {connectorsEnabled && activeTab === "webhooks" && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Template Connectors
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Route approved output from this template to linked connector
                endpoints.
              </p>
            </div>
            {(isManager || isReader) && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedWebhookId}
                  onChange={(e) => setSelectedWebhookId(e.target.value)}
                  disabled={isReader}
                  className="px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select connector to connect</option>
                  {availableWebhooks.map((wh) => (
                    <option key={wh.id} value={wh.id}>
                      {wh.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleLinkWebhook}
                  disabled={
                    isReader || !selectedWebhookId || linkingWebhookId !== null
                  }
                  title={isReader ? READ_ONLY_MESSAGE : undefined}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {linkingWebhookId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                  Connect
                </button>
              </div>
            )}
          </div>

          {!isManager && !isReader && (
            <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Connectors are managed by your admin. You can view linked endpoints
              and delivery logs.
            </div>
          )}

          {isManager &&
            !loadingWebhooks &&
            linkedWebhooks.length > 0 &&
            availableWebhooks.length === 0 && (
              <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                All available connector endpoints are already connected to this
                template.
              </div>
            )}

          {loadingWebhooks ? (
            <div className="py-10 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loading linked connectors...
              </p>
            </div>
          ) : linkedWebhooks.length === 0 ? (
            <div className="py-10 text-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
              <Webhook className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                No connectors connected to this template.
              </p>
              {isAdmin && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Connect one from the dropdown above.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {linkedWebhooks.map((wh) => {
                const deliveries = webhookDeliveries[wh.id] || [];
                const isExpanded = expandedWebhookId === wh.id;
                return (
                  <div
                    key={wh.id}
                    className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/40 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Webhook className="h-4 w-4 text-teal-500" />
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {wh.name}
                          </p>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${wh.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                          >
                            {wh.is_active ? "Active" : "Disabled"}
                          </span>
                          {wh.payload_mapping && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              Mapped
                            </span>
                          )}
                          {wh.custom_headers &&
                            Object.keys(wh.custom_headers).length > 0 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                                Headers
                              </span>
                            )}
                          {wh.require_payload_mapping && !wh.payload_mapping && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                              Map Payload required
                            </span>
                          )}
                          {wh.mapping_check?.status === "error" && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                              Out of sync
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate mt-1">
                          {wh.url}
                        </p>
                        {wh.require_payload_mapping && !wh.payload_mapping && (
                          <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                            This connector requires a Map Payload, so nothing is sent for this
                            template until one is set. Open Map Payload.
                          </p>
                        )}
                        {wh.mapping_check?.status === "error" && (
                          <div className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                            <p>
                              The Map Payload doesn't match this template's fields —{" "}
                              publishing to this connector is blocked until it is updated:
                            </p>
                            <ul className="mt-0.5 list-disc pl-4">
                              {wh.mapping_check.errors.slice(0, 3).map((e) => (
                                <li key={`${e.code}:${e.field}`}>{e.message}</li>
                              ))}
                              {wh.mapping_check.errors.length > 3 && (
                                <li>and {wh.mapping_check.errors.length - 3} more — open Map Payload</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => loadDeliveries(wh.id)}
                          disabled={loadingDeliveriesId === wh.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs disabled:opacity-50"
                          title="Refresh deliveries"
                        >
                          {loadingDeliveriesId === wh.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          Refresh
                        </button>
                        <button
                          onClick={() => handleToggleDeliveries(wh.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          Deliveries
                        </button>
                        {isManager && (
                          <button
                            onClick={() => setMappingModalWebhookId(wh.id)}
                            disabled={isReader}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Braces className="h-3.5 w-3.5" />
                            Map Payload
                          </button>
                        )}
                        {isManager && (
                          <button
                            onClick={() => setHeadersModalWebhookId(wh.id)}
                            disabled={isReader}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Hash className="h-3.5 w-3.5" />
                            Headers
                          </button>
                        )}
                        {(isManager || isReader) && (
                          <button
                            onClick={() => setWebhookPendingDisconnect(wh)}
                            disabled={isReader || unlinkingWebhookId === wh.id}
                            title={isReader ? READ_ONLY_MESSAGE : undefined}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {unlinkingWebhookId === wh.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Unlink className="h-3.5 w-3.5" />
                            )}
                            Disconnect
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 py-3">
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Activity className="h-4 w-4 text-gray-400" />
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Delivery Logs
                              </h4>
                              <span className="text-[11px] text-gray-400 font-medium">
                                ({deliveries.length})
                              </span>
                            </div>
                            <button
                              onClick={() => loadDeliveries(wh.id)}
                              disabled={loadingDeliveriesId === wh.id}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                              title="Refresh"
                            >
                              <RefreshCw
                                className={`h-4 w-4 ${loadingDeliveriesId === wh.id ? "animate-spin" : ""}`}
                              />
                            </button>
                          </div>

                          {deliveries.length === 0 ? (
                            <div className="py-10 text-center text-gray-500 dark:text-gray-400 text-sm">
                              No deliveries yet.
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <div className="min-w-[980px]">
                                <div className="grid grid-cols-[1fr_1.2fr_0.6fr_1fr_1.5fr_0.8fr_0.8fr_auto] gap-3 px-4 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/70 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800">
                                  <div>Status</div>
                                  <div>Event</div>
                                  <div>HTTP</div>
                                  <div>Request</div>
                                  <div>Timestamp</div>
                                  <div>Duration</div>
                                  <div>Download</div>
                                  <div className="w-4"></div>
                                </div>

                                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                  {deliveries.map((d) => {
                                    const detailsExpanded =
                                      expandedDeliveryId === d.id;
                                    const full = deliveryDetails[d.id];

                                    let downloadUrl = null;
                                    if (d.response_body) {
                                      try {
                                        const parsed =
                                          typeof d.response_body === "string"
                                            ? JSON.parse(d.response_body)
                                            : d.response_body;
                                        downloadUrl =
                                          parsed?.download_url ||
                                          parsed?.url ||
                                          parsed?.file_url ||
                                          parsed?.csv_url ||
                                          parsed?.link ||
                                          null;
                                        if (
                                          downloadUrl &&
                                          !downloadUrl.startsWith("http")
                                        )
                                          downloadUrl = null;
                                      } catch {
                                        const urlMatch = String(
                                          d.response_body,
                                        ).match(/https?:\/\/[^\s"'<>]+/);
                                        if (urlMatch) downloadUrl = urlMatch[0];
                                      }
                                    }

                                    return (
                                      <React.Fragment key={d.id}>
                                        <div className="grid grid-cols-[1fr_1.2fr_0.6fr_1fr_1.5fr_0.8fr_0.8fr_auto] gap-3 px-4 py-3.5 items-center hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                          <div>
                                            <span
                                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${d.status === "success" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : d.status === "failed" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"}`}
                                            >
                                              {d.status === "success" && (
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                              )}
                                              {d.status}
                                            </span>
                                          </div>

                                          <div className="text-xs font-mono text-gray-700 dark:text-gray-300">
                                            {d.event}
                                          </div>

                                          <div>
                                            {d.http_status ? (
                                              <span
                                                className={`text-xs font-semibold font-mono ${d.http_status >= 200 && d.http_status < 300 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
                                              >
                                                {d.http_status}
                                              </span>
                                            ) : (
                                              <span className="text-xs text-gray-400">
                                                -
                                              </span>
                                            )}
                                          </div>

                                          <div>
                                            {d.request_id > 0 ? (
                                              <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                                <Hash className="h-3 w-3 text-gray-400" />
                                                {d.request_id}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 font-medium">
                                                test
                                              </span>
                                            )}
                                          </div>

                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatDateTimeShort(d.created_at)}
                                          </div>

                                          <div className="text-xs text-gray-400 font-mono">
                                            {formatDeliveryDuration(
                                              d.created_at,
                                              d.completed_at,
                                            )}
                                          </div>

                                          <div>
                                            {downloadUrl ? (
                                              <a
                                                href={downloadUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-slate-800 hover:bg-slate-700 text-white transition-colors"
                                              >
                                                <FileDown className="h-3 w-3" />
                                                CSV
                                              </a>
                                            ) : (
                                              <span className="text-xs text-gray-300 dark:text-gray-600">
                                                -
                                              </span>
                                            )}
                                          </div>

                                          <div>
                                            {d.status === "failed" ? (
                                              <button
                                                onClick={() =>
                                                  toggleDeliveryDetails(d.id)
                                                }
                                                className="text-gray-400 hover:text-gray-600"
                                                title="View error"
                                              >
                                                {loadingDeliveryDetailsId ===
                                                d.id ? (
                                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : detailsExpanded ? (
                                                  <ChevronDown className="h-3.5 w-3.5" />
                                                ) : (
                                                  <ChevronRight className="h-3.5 w-3.5" />
                                                )}
                                              </button>
                                            ) : (
                                              <span className="text-gray-300 dark:text-gray-600">
                                                <ChevronRight className="h-3.5 w-3.5" />
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {detailsExpanded &&
                                          d.status === "failed" && (
                                            <div className="px-4 pb-4 bg-red-50/40 dark:bg-red-900/10 border-t border-red-100 dark:border-red-900/30">
                                              <div className="pt-3">
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">
                                                  Error
                                                </p>
                                                <pre className="text-xs whitespace-pre-wrap break-words text-red-700 dark:text-red-300">
                                                  {full?.error_message ||
                                                    d.error_message ||
                                                    "No additional error details available."}
                                                </pre>
                                                {isAdmin && (
                                                  <div className="mt-2">
                                                    <button
                                                      onClick={() =>
                                                        handleRetryDelivery(
                                                          d.id,
                                                          wh.id,
                                                        )
                                                      }
                                                      disabled={
                                                        retryingDeliveryId ===
                                                        d.id
                                                      }
                                                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs"
                                                    >
                                                      {retryingDeliveryId ===
                                                      d.id ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                      ) : (
                                                        <RefreshCw className="h-3 w-3" />
                                                      )}
                                                      Retry
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Template Output Destinations (FTP/SFTP CSV export links) */}
      {connectorsEnabled && activeTab === "output_destinations" && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Output Destinations
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Automatically export approved requests for this template as
                CSV files to linked FTP/SFTP destinations.
              </p>
            </div>
            {(isAdmin || isReader) && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedOutputDestinationId}
                  onChange={(e) =>
                    setSelectedOutputDestinationId(e.target.value)
                  }
                  disabled={isReader}
                  className="px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {allOrgOutputDestinations.length === 0
                      ? "No destinations created yet"
                      : "Select destination to connect"}
                  </option>
                  {availableOutputDestinations.map((dest) => (
                    <option key={dest.id} value={dest.id}>
                      {dest.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleLinkOutputDestination}
                  disabled={
                    isReader ||
                    !selectedOutputDestinationId ||
                    linkingOutputDestinationId !== null
                  }
                  title={isReader ? READ_ONLY_MESSAGE : undefined}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {linkingOutputDestinationId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                  Connect
                </button>
              </div>
            )}
          </div>

          {!isAdmin && !isReader && (
            <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Output destinations are managed by your admin. You can view
              linked destinations here.
            </div>
          )}

          {isAdmin &&
            !loadingOutputDestinations &&
            linkedOutputDestinations.length > 0 &&
            availableOutputDestinations.length === 0 && (
              <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                All available output destinations are already connected to
                this template.
              </div>
            )}

          {loadingOutputDestinations ? (
            <div className="py-10 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Loading linked output destinations...
              </p>
            </div>
          ) : linkedOutputDestinations.length === 0 ? (
            <div className="py-10 text-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
              <Server className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                No output destinations connected to this template.
              </p>
              {/* When the org has no destinations at all, "connect one from
                  the dropdown" is impossible advice - the dropdown is empty.
                  Point at where destinations are actually created instead. */}
              {isAdmin &&
                (allOrgOutputDestinations.length === 0 ? (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      No output destinations have been created yet.
                    </p>
                    <Link
                      reloadDocument
                      to="/output-destinations"
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 mt-2"
                    >
                      Create one in Output Destinations
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Connect one from the dropdown above.
                  </p>
                ))}
            </div>
          ) : (
            <div className="space-y-3">
              {linkedOutputDestinations.map((dest) => {
                const config = dest.config || {};
                const mapping = Array.isArray(dest.field_mapping)
                  ? [...dest.field_mapping].sort(
                      (a, b) => (a.order ?? 0) - (b.order ?? 0),
                    )
                  : [];
                const hasMapping = mapping.length > 0;
                const isLineColumn = (m) =>
                  (m.xtract_field || "").includes(".");
                const isMultiRow = mapping.some(isLineColumn);
                const health = dest.export_health;
                const isExpanded = expandedOutputDestinationId === dest.id;
                return (
                  <div
                    key={dest.id}
                    className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden"
                  >
                    {/* Collapsed card is a single header strip, matching the
                        Webhooks card exactly. Everything else sits behind the
                        "CSV Output" toggle - an earlier version stacked the
                        mapping and health panels here permanently, which on a
                        53-field template produced an unreadable wall. */}
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/40 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Server className="h-4 w-4 text-teal-500 shrink-0" />
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {dest.name}
                          </p>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${dest.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                          >
                            {dest.is_active ? "Active" : "Disabled"}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 uppercase">
                            {config.protocol}
                          </span>
                          {/* A count, not the columns themselves. The full
                              list is one click away and reads vertically. */}
                          {hasMapping ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                              {mapping.length} column
                              {mapping.length === 1 ? "" : "s"}
                              {isMultiRow ? " · per line item" : ""}
                            </span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 inline-flex items-center gap-1">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Not mapped
                            </span>
                          )}
                          {health?.last_status && (
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                health.last_status === "success"
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                  : health.last_status === "failed"
                                    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                              }`}
                            >
                              Last:{" "}
                              {health.last_status === "success"
                                ? "Success"
                                : health.last_status === "failed"
                                  ? "Failed"
                                  : "Pending"}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate mt-1">
                          {config.username}@{config.host}:{config.port}
                          {config.remote_path}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() =>
                            setExpandedOutputDestinationId(
                              isExpanded ? null : dest.id,
                            )
                          }
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          CSV Output
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() =>
                              setMappingModalDestinationId(dest.id)
                            }
                            disabled={isReader}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Braces className="h-3.5 w-3.5" />
                            Map Fields
                          </button>
                        )}
                        {(isAdmin || isReader) && (
                          <button
                            onClick={() =>
                              setOutputDestinationPendingDisconnect(dest)
                            }
                            disabled={
                              isReader ||
                              unlinkingOutputDestinationId === dest.id
                            }
                            title={isReader ? READ_ONLY_MESSAGE : undefined}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {unlinkingOutputDestinationId === dest.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Unlink className="h-3.5 w-3.5" />
                            )}
                            Disconnect
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 py-3">
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                CSV Output
                              </h4>
                              <span className="text-[11px] text-gray-400 font-medium">
                                ({mapping.length})
                              </span>
                            </div>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                isMultiRow
                                  ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                              }`}
                            >
                              {isMultiRow
                                ? "one row per line item"
                                : "one row per request"}
                            </span>
                          </div>

                          {/* Two facts a template manager needs before the
                              column list: what the file is called, and
                              whether the last one arrived. */}
                          <dl className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs border-b border-gray-100 dark:border-gray-800">
                            <div className="min-w-0">
                              <dt className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                Filename pattern
                              </dt>
                              <dd className="font-mono text-gray-700 dark:text-gray-300 truncate">
                                {dest.filename_pattern || "—"}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                Last export
                              </dt>
                              <dd className="text-gray-700 dark:text-gray-300 truncate">
                                {health?.last_at ? (
                                  <>
                                    {formatDateTimeShort(health.last_at)}
                                    {health.failed_count > 0 && (
                                      <span className="text-red-600 dark:text-red-400 ml-1.5">
                                        ({health.failed_count} of{" "}
                                        {health.total_count} failed)
                                      </span>
                                    )}
                                    {/* Deep-links to this destination's own
                                        delivery log, not the bare list. */}
                                    <Link
                                      reloadDocument
                                      to={`/output-destinations?destination=${dest.id}`}
                                      className="text-blue-600 hover:text-blue-800 ml-1.5"
                                    >
                                      history →
                                    </Link>
                                  </>
                                ) : (
                                  "Never exported"
                                )}
                              </dd>
                            </div>
                          </dl>

                          {!hasMapping ? (
                            <div className="px-4 py-6 text-center">
                              <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-1.5" />
                              <p className="text-xs text-amber-700 dark:text-amber-400">
                                No field mapping configured — exports to this
                                destination will fail until you set one.
                              </p>
                              {isAdmin && (
                                <button
                                  onClick={() =>
                                    setMappingModalDestinationId(dest.id)
                                  }
                                  className="mt-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs"
                                >
                                  <Braces className="h-3.5 w-3.5" />
                                  Map Fields
                                </button>
                              )}
                            </div>
                          ) : (
                            /* A vertical, scrollable table with a sticky
                               header. 53 columns read as 53 short rows here;
                               the previous horizontal strip forced the whole
                               page sideways and could not be scanned. */
                            <div className="max-h-72 overflow-y-auto">
                              <div className="grid grid-cols-[2.5rem_1fr_1fr_5rem] gap-3 px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/70 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 sticky top-0 z-10">
                                <div>#</div>
                                <div>Template Field</div>
                                <div>CSV Column</div>
                                <div>Source</div>
                              </div>
                              {mapping.map((m, i) => {
                                const isLine = isLineColumn(m);
                                return (
                                  <div
                                    key={`${m.xtract_field}-${i}`}
                                    className="grid grid-cols-[2.5rem_1fr_1fr_5rem] gap-3 px-4 py-2 text-xs border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-gray-50/60 dark:hover:bg-gray-800/20"
                                  >
                                    <div className="text-gray-400 tabular-nums">
                                      {i + 1}
                                    </div>
                                    <div
                                      className="font-mono text-gray-600 dark:text-gray-400 truncate"
                                      title={m.xtract_field}
                                    >
                                      {m.xtract_field}
                                    </div>
                                    <div
                                      className="font-mono text-gray-900 dark:text-gray-100 truncate"
                                      title={m.csv_column}
                                    >
                                      {m.csv_column}
                                    </div>
                                    <div>
                                      <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                          isLine
                                            ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                                            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                                        }`}
                                      >
                                        {isLine ? "line" : "header"}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {isMultiRow && hasMapping && (
                            <p className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100 dark:border-gray-800">
                              <span className="text-violet-600 dark:text-violet-400 font-medium">
                                line
                              </span>{" "}
                              columns come from the line-items table — one CSV
                              row each.{" "}
                              <span className="font-medium">header</span>{" "}
                              columns repeat on every row.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== NETSUITE TWO-STEP SETUP (step 1 gates the mapping panel) ===== */}
      {activeTab === "fields" &&
        isNsTemplate && (
          <NSTemplateSetup
            templateId={template.id}
            nsEntityType={nsEntityTypeForCopy}
            defaultSubsidiary={
              template.ns_default_subsidiary_id ||
              template.template_ns_default_subsidiary_id
            }
            onReady={() => setNsSetupReady(true)}
            onSkip={() => setNsSetupReady(true)}
          />
        )}

      {/* ===== AI SUGGEST PANEL ===== */}
      {activeTab === "fields" && showAiSuggest &&
        (!isNsTemplate || nsSetupReady) && (
        <div className="rounded-xl border-2 border-purple-200 dark:border-purple-800/50 bg-gradient-to-b from-purple-50 to-white dark:from-purple-900/10 dark:to-gray-900 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {isNsTemplate
                ? nsEntityTypeForCopy === "vendor_bid"
                  ? "NetSuite Special Pricing AI Field Mapping"
                  : "NetSuite AI Field Mapping"
                : "AI Field Suggestion"}
            </h3>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
              {isNsTemplate
                ? nsEntityTypeForCopy === "vendor_bid"
                  ? "Upload your NetSuite PDF → AI maps fields to NS Special Pricing keys"
                  : "Upload your NetSuite PDF → AI maps fields to NS Purchase Order keys"
                : "Upload sample PDFs → AI generates template fields"}
            </span>
          </div>

          {/* Point 3: Disclaimer about uploading same-type documents */}
          <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
            <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {isNsTemplate ? (
                nsEntityTypeForCopy === "vendor_bid" ? (
                  <>
                    Upload a <strong>NetSuite Special Pricing PDF</strong> (a
                    manufacturer's special-pricing document). The AI will
                    extract fields and automatically map them to NetSuite
                    Special Pricing field keys (e.g. supplier, reseller, end
                    customer, valid period). You can review and adjust
                    mappings before accepting.
                  </>
                ) : (
                  <>
                    Upload a <strong>NetSuite Purchase Order PDF</strong> (or
                    similar PO document). The AI will extract fields and
                    automatically map them to NetSuite PO field keys (e.g. entity,
                    tranDate, subsidiary). You can review and adjust mappings
                    before accepting.
                  </>
                )
              ) : (
                <>
                  Upload up to <strong>10 documents of the same type</strong>{" "}
                  (e.g., all insurance policies, all prospectuses). The AI will
                  analyze them together to discover the most comprehensive set
                  of extractable fields. Mixing different document types may
                  produce inaccurate results.
                </>
              )}
            </p>
          </div>

          {!aiResult ? (
            /* STEP 1: Upload + Prompt */
            <div className="space-y-3">
              {/* Dropzone */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleAiFileDrop}
                onClick={() => {
                  if (aiFiles.length >= 10) {
                    toast.warning(
                      "Limit reached",
                      "You can only upload up to 10 documents. Please remove a document first.",
                    );
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  aiFiles.length >= 10
                    ? "border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed opacity-75"
                    : aiFiles.length > 0
                      ? "border-purple-400 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-600 cursor-pointer"
                      : "border-gray-300 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-600 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 cursor-pointer"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple={
                    !isNsTemplate
                  }
                  onChange={handleAiFileSelect}
                  className="hidden"
                />
                {aiFiles.length > 0 ? (
                  <div className="space-y-2">
                    {aiFiles.map((file, idx) => (
                      <div
                        key={`${file.name}-${idx}`}
                        className="flex items-center justify-center gap-3"
                      >
                        <FileText className="h-5 w-5 text-purple-500 flex-shrink-0" />
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAiFiles((prev) =>
                              prev.filter((_, i) => i !== idx),
                            );
                            setAiResult(null);
                          }}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex-shrink-0"
                        >
                          <X className="h-4 w-4 text-gray-400" />
                        </button>
                      </div>
                    ))}
                    {!isNsTemplate &&
                      (aiFiles.length < 10 ? (
                        <p className="text-xs text-purple-500 dark:text-purple-400 mt-2">
                          + Drop or click to add more ({10 - aiFiles.length}{" "}
                          remaining)
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                          ⚠ Maximum 10 documents reached - remove one to add
                          another
                        </p>
                      ))}
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {isNsTemplate ? (
                        <>
                          Drop your{" "}
                          <span className="text-purple-600 dark:text-purple-400 font-medium">
                            NetSuite PDF
                          </span>{" "}
                          here or{" "}
                          <span className="text-purple-600 dark:text-purple-400 font-medium">
                            browse
                          </span>
                        </>
                      ) : (
                        <>
                          Drop sample PDFs here or{" "}
                          <span className="text-purple-600 dark:text-purple-400 font-medium">
                            browse
                          </span>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {isNsTemplate
                        ? isNsBidTemplateForCopy
                          ? "Upload a NetSuite Special Pricing PDF - AI will map fields to NS keys automatically"
                          : "Upload a NetSuite Purchase Order PDF - AI will map fields to NS keys automatically"
                        : "Upload up to 10 PDFs - the AI will analyze them and suggest extractable fields"}
                    </p>
                  </>
                )}
              </div>

              {/* Optional prompt — hidden for NS templates */}
              {!isNsTemplate && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                    <MessageSquare className="h-3 w-3" />
                    Optional: Tell the AI what fields you're looking for
                  </label>
                  <textarea
                    rows={2}
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                    placeholder={
                      (template?.fields || []).length
                        ? "e.g. add the manufacturer part number for each line"
                        : "e.g. order number, order date, delivery address and the product lines"
                    }
                  />
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-purple-600"
                        checked={aiRestrict}
                        onChange={(e) => setAiRestrict(e.target.checked)}
                      />
                      Only the fields I list
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                      Detail
                      <select
                        value={aiDetailLevel}
                        onChange={(e) => setAiDetailLevel(e.target.value)}
                        disabled={aiRestrict}
                        className="text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-1.5 py-1 disabled:opacity-50"
                      >
                        <option value="essentials">Essentials</option>
                        <option value="standard">Standard</option>
                        <option value="everything">Everything</option>
                      </select>
                    </label>
                  </div>
                </div>
              )}

              {/* Suggest button */}
              <button
                onClick={handleAiSuggest}
                disabled={aiFiles.length === 0 || aiSuggesting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium shadow-sm disabled:opacity-50 hover:bg-indigo-700 enabled:hover:-translate-y-0.5 enabled:hover:shadow-lg enabled:active:translate-y-0 enabled:active:scale-[0.98] transition-all duration-200"
              >
                {aiSuggesting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {aiJobId
                      ? "Processing in background..."
                      : isNsTemplate
                        ? "Mapping NS fields..."
                        : "Analyzing documents..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {isNsTemplate
                      ? "Map NetSuite Fields"
                      : "Suggest Fields"}
                  </>
                )}
              </button>

              {/* While the AI reads the documents: the Xtract mark (documents
                  becoming records is what it depicts), the job's own progress
                  message, and a progress bar once the background job reports. */}
              {aiSuggesting && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mt-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-8 flex flex-col items-center text-center"
                >
                  <ExtractLoader size={56} />
                  <p className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {isNsTemplate ? "Mapping NetSuite fields" : "Reading your documents"}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 min-h-[1rem]">
                    {aiJobProgress?.message ||
                      (isNsTemplate
                        ? "Matching what's on the document to NetSuite fields…"
                        : "Finding the fields and tables worth extracting…")}
                  </p>
                  {aiJobId && (
                    <div className="mt-4 w-full max-w-xs">
                      <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700 ease-out"
                          style={{
                            width: `${Math.max(aiJobProgress?.percent || 5, 5)}%`,
                            background: "linear-gradient(90deg, var(--ai-gradient-from), var(--ai-gradient-to))",
                          }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                        You can navigate away - you'll be notified when ready.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* STEP 2: Review suggested fields */
            <div className="space-y-3">
              {/* Document info header */}
              <div className="flex items-start justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3.5 shadow-sm">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {aiResult.document_type}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {aiResult.document_summary}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="truncate max-w-[16rem]">
                        {aiResult.source_files?.join(", ") ||
                          aiResult.source_file}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">
                        •
                      </span>
                      <span>
                        {aiResult.pages_analyzed}/{aiResult.total_pages} pages
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">
                        •
                      </span>
                      <span>{aiResult.total_fields} fields suggested</span>
                    </p>
                    {/* Name the source of the mapping rather than implying the
                        AI produced it when it was unavailable. */}
                    {aiResult.ns_ai_error && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1.5 flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                        <span>Mapped from the known Special Pricing layout — the AI step was unavailable ({aiResult.ns_ai_error})</span>
                      </p>
                    )}
                    {aiResult.ns_catalog_source === "known_layout" && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1.5 flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                        <span>NetSuite's own field list could not be read, so the known Special Pricing layout was used. Account-specific custom fields are not listed.</span>
                      </p>
                    )}
                    {/* Shown, not silently dropped, so the user can see the
                        Distributor / Competitor rows were read and skipped on
                        purpose rather than missed. */}
                    {aiResult.ns_excluded_labels?.length > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        Read but not mapped (not Special Pricing fields):{" "}
                        {aiResult.ns_excluded_labels
                          .map((e) => e.label)
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setAiResult(null);
                    setAiFiles([]);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 whitespace-nowrap px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors flex-shrink-0"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Try another
                </button>
              </div>

              {aiResult.intent?.requested?.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-1.5">
                  <span>{aiResult.intent.restrict ? "Only looking for:" : "Also looking for:"}</span>
                  {aiResult.intent.requested.map((r) => (
                    <span
                      key={r.name}
                      title={r.hint || undefined}
                      className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                    >
                      {r.name}
                    </span>
                  ))}
                </p>
              )}

              <SuggestionQuestions
                questions={aiResult.questions}
                onApply={handleApplyAnswers}
                applying={aiApplyingAnswers}
              />

              {/* Select all / none */}
              <div className="flex items-center justify-between">
                <button
                  onClick={toggleSelectAll}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium"
                >
                  {aiResult.fields
                    ?.filter(
                      (f) =>
                        f.placement?.action !== "exists" &&
                        (aiShowOptional || !isOptionalSuggestion(f)),
                    )
                    .every((f) => aiSelected[f.field_name])
                    ? "Deselect all"
                    : "Select all"}
                </button>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2.5 py-1">
                  {Object.values(aiSelected).filter(Boolean).length} of{" "}
                  {aiResult.fields?.length} selected
                </span>
              </div>

              {/* Suggested fields table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[28rem] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-20 shadow-sm">
                    <tr>
                      <th className="px-3 py-2.5 text-left w-8 bg-gray-50 dark:bg-gray-800"></th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                        Field
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                        Type
                      </th>
                      {isNsTemplate && (
                        <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-purple-500 dark:text-purple-400 bg-gray-50 dark:bg-gray-800">
                          Maps to NS
                        </th>
                      )}
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                        Description
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                        Sample
                      </th>
                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 w-20 bg-gray-50 dark:bg-gray-800">
                        Confidence
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiVisibleFields.map((entry) => {
                      if (entry.separator) {
                        return (
                          <tr key="__optional" className="border-t border-gray-100 dark:border-gray-800">
                            <td colSpan={isNsTemplate ? 7 : 6} className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => setAiShowOptional((v) => !v)}
                                className="text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium"
                              >
                                {aiShowOptional ? "Hide" : "Show"} {aiOptionalFields.length} optional suggestion
                                {aiOptionalFields.length !== 1 ? "s" : ""} (not ticked: footer details, secondary tables)
                              </button>
                            </td>
                          </tr>
                        );
                      }
                      const { field } = entry;
                      const selected = !!aiSelected[field.field_name];
                      const existingNames = new Set(
                        (template?.fields || []).map((f) => f.field_name),
                      );
                      const isDuplicate = field.placement
                        ? field.placement.action === "exists"
                        : existingNames.has(field.field_name);
                      const isMapped = !!field.ns_key;
                      const accentClass = isDuplicate
                        ? "border-l-gray-200 dark:border-l-gray-700"
                        : isMapped
                          ? "border-l-emerald-400 dark:border-l-emerald-600"
                          : "border-l-amber-300 dark:border-l-amber-700";
                      return (
                        <React.Fragment key={field._manualId || field.field_name}>
                        <tr
                          onClick={() =>
                            !isDuplicate &&
                            setAiSelected((prev) => ({
                              ...prev,
                              [field.field_name]: !prev[field.field_name],
                            }))
                          }
                          className={`border-t border-gray-100 dark:border-gray-800 border-l-[3px] cursor-pointer transition-colors ${accentClass} ${
                            isDuplicate
                              ? "opacity-40 cursor-not-allowed"
                              : selected
                                ? "bg-purple-50 dark:bg-purple-900/10"
                                : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          }`}
                          title={
                            isDuplicate
                              ? "Already exists in template"
                              : undefined
                          }
                        >
                          <td className="px-3 py-2.5">
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                isDuplicate
                                  ? "border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
                                  : selected
                                    ? "border-purple-500 bg-purple-500"
                                    : "border-gray-300 dark:border-gray-600"
                              }`}
                            >
                              {selected && !isDuplicate && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                              {isDuplicate && (
                                <Check className="h-3 w-3 text-gray-400" />
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 group">
                            {field._manual && field._editing ? (
                              <div
                                className="flex items-start gap-1.5 max-w-[13rem]"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                  <input
                                    autoFocus
                                    type="text"
                                    value={field.display_name}
                                    placeholder="Display name…"
                                    style={{
                                      fontSize: "12px",
                                      lineHeight: "1rem",
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Enter" &&
                                        field.display_name.trim()
                                      ) {
                                        setAiResult((prev) =>
                                          prev
                                            ? {
                                                ...prev,
                                                fields: prev.fields.map((f) =>
                                                  f._manualId ===
                                                  field._manualId
                                                    ? { ...f, _editing: false }
                                                    : f,
                                                ),
                                              }
                                            : prev,
                                        );
                                      }
                                    }}
                                    onChange={(e) => {
                                      const displayName = e.target.value;
                                      const slug =
                                        displayName
                                          .toLowerCase()
                                          .trim()
                                          .replace(/[^a-z0-9]+/g, "_")
                                          .replace(/^_+|_+$/g, "") ||
                                        field._manualId;
                                      const oldName = field.field_name;
                                      setAiResult((prev) => {
                                        if (!prev) return prev;
                                        return {
                                          ...prev,
                                          fields: prev.fields.map((f) =>
                                            f._manualId === field._manualId
                                              ? {
                                                  ...f,
                                                  display_name: displayName,
                                                  field_name: slug,
                                                }
                                              : f,
                                          ),
                                        };
                                      });
                                      // aiSelected is keyed by field_name — carry the selection flag
                                      // over to the new key so the checkbox doesn't appear to reset
                                      // as the slug changes with every keystroke.
                                      if (slug !== oldName) {
                                        setAiSelected((prev) => {
                                          const next = { ...prev };
                                          const was = next[oldName];
                                          delete next[oldName];
                                          next[slug] =
                                            was !== undefined ? was : true;
                                          return next;
                                        });
                                      }
                                    }}
                                    className="w-full h-6 px-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400"
                                  />
                                  <p className="text-[10px] text-gray-400 font-mono px-0.5 truncate">
                                    {field.field_name || "field_name"}
                                  </p>
                                </div>
                                <button
                                  onClick={() => {
                                    if (!field.display_name.trim()) return;
                                    setAiResult((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            fields: prev.fields.map((f) =>
                                              f._manualId === field._manualId
                                                ? { ...f, _editing: false }
                                                : f,
                                            ),
                                          }
                                        : prev,
                                    );
                                  }}
                                  disabled={!field.display_name.trim()}
                                  title="Save field name"
                                  className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : field._manual ? (
                              <div className="flex items-center gap-1.5">
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                    {field.display_name}
                                  </p>
                                  <p className="text-[10px] text-gray-400 font-mono">
                                    {field.field_name}
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAiResult((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            fields: prev.fields.map((f) =>
                                              f._manualId === field._manualId
                                                ? { ...f, _editing: true }
                                                : f,
                                            ),
                                          }
                                        : prev,
                                    );
                                  }}
                                  title="Edit field name"
                                  className="opacity-0 group-hover:opacity-100 flex-shrink-0 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-opacity"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">
                                  {field.display_name}
                                </p>
                                <p className="text-[10px] text-gray-400 font-mono">
                                  {field.field_name}
                                </p>
                                <div className="mt-1 flex flex-wrap items-center gap-1">
                                  <PlacementBadge item={field} />
                                  {field.field_type === "array_of_objects" &&
                                    field.columns?.length > 0 &&
                                    field.placement && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAiExpanded((prev) => ({
                                            ...prev,
                                            [field.field_name]: !prev[field.field_name],
                                          }));
                                        }}
                                        className="text-[10px] text-purple-600 dark:text-purple-400 hover:underline"
                                      >
                                        {aiExpanded[field.field_name]
                                          ? "Hide columns"
                                          : `Show ${field.columns.length} column${field.columns.length !== 1 ? "s" : ""}`}
                                      </button>
                                    )}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                field.field_type === "date"
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                  : field.field_type === "number" ||
                                      field.field_type === "currency"
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                    : field.field_type === "percentage"
                                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                      : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {field.field_type}
                            </span>
                          </td>
                          {isNsTemplate && (
                            <td
                              className="px-3 py-2.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <NsFieldSearchDropdown
                                value={field.ns_key || ""}
                                loadError={nsFieldCatalogError}
                                onChange={(newKey) => {
                                  setAiResult((prev) => {
                                    if (!prev) return prev;
                                    return {
                                      ...prev,
                                      fields: prev.fields.map((f) =>
                                        f.field_name === field.field_name
                                          ? { ...f, ns_key: newKey || null }
                                          : f,
                                      ),
                                    };
                                  });
                                }}
                                catalog={
                                  aiResult.ns_available_fields || nsFieldCatalog
                                }
                                compact
                                placeholder="— unmapped —"
                              />
                            </td>
                          )}
                          <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 max-w-xs">
                            <p
                              className="line-clamp-2"
                              title={
                                typeof field.description === "string"
                                  ? field.description
                                  : undefined
                              }
                            >
                              {field.description ? (
                                typeof field.description === "string" ? (
                                  field.description
                                ) : Array.isArray(field.description) ? (
                                  `[${field.description.length} row${field.description.length !== 1 ? "s" : ""}]`
                                ) : (
                                  JSON.stringify(field.description)
                                )
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600 italic">
                                  —
                                </span>
                              )}
                            </p>
                            {/* Why the AI chose this mapping — only sent by
                                the NetSuite mapping endpoints. */}
                            {field.reason && (
                              <p
                                className="mt-1 text-[11px] italic text-purple-600/80 dark:text-purple-300/80 line-clamp-2"
                                title={field.reason}
                              >
                                Why: {field.reason}
                              </p>
                            )}
                          </td>
                          <td
                            className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 max-w-[8rem] truncate"
                            title={
                              formatSampleValue(field.sample_value) || undefined
                            }
                          >
                            {formatSampleValue(field.sample_value) || (
                              <span className="text-gray-300 dark:text-gray-600 italic">
                                —
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs">
                            {isKnownLayoutMapping(field) ? (
                              // A pinned mapping is not a 100%-confident guess,
                              // it is not a guess at all — showing a confidence
                              // bar for it would invite the user to second-guess
                              // the one thing that cannot be wrong.
                              <span
                                title="Pinned to the known Samsung Special Pricing layout, not inferred by the AI."
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 whitespace-nowrap"
                              >
                                Samsung layout
                              </span>
                            ) : field.confidence ? (
                              <div className="flex items-center gap-1.5 w-20">
                                <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      field.confidence >= 0.9
                                        ? "bg-emerald-500"
                                        : field.confidence >= 0.6
                                          ? "bg-amber-500"
                                          : "bg-red-400"
                                    }`}
                                    style={{
                                      width: `${Math.round(field.confidence * 100)}%`,
                                    }}
                                  />
                                </div>
                                <span
                                  className={`font-medium text-[10px] tabular-nums ${
                                    field.confidence >= 0.9
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : field.confidence >= 0.6
                                        ? "text-amber-600 dark:text-amber-400"
                                        : "text-red-500 dark:text-red-400"
                                  }`}
                                >
                                  {Math.round(field.confidence * 100)}%
                                </span>
                              </div>
                            ) : (
                              ""
                            )}
                          </td>
                        </tr>
                        {aiExpanded[field.field_name] &&
                          field.field_type === "array_of_objects" && (
                            <tr className="bg-gray-50/60 dark:bg-gray-800/30">
                              <td></td>
                              <td colSpan={isNsTemplate ? 6 : 5} className="px-3 pb-3">
                                <TableSuggestionDetail
                                  item={field}
                                  onChangeColumn={(key, patch) =>
                                    updateAiColumn(field.field_name, key, patch)
                                  }
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Notes */}
              {aiResult.notes && (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                  {aiResult.notes}
                </p>
              )}

              {/* Accept button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleAcceptSuggested}
                  disabled={
                    aiSaving ||
                    Object.values(aiSelected).filter(Boolean).length === 0
                  }
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium shadow-sm disabled:opacity-50 hover:bg-indigo-700 enabled:hover:-translate-y-0.5 enabled:hover:shadow-lg enabled:active:translate-y-0 enabled:active:scale-[0.98] transition-all duration-200"
                >
                  {aiSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {isNsTemplate
                        ? `Mapping ${Object.values(aiSelected).filter(Boolean).length} NS fields...`
                        : `Adding ${Object.values(aiSelected).filter(Boolean).length} fields...`}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      {isNsTemplate
                        ? `Accept ${Object.values(aiSelected).filter(Boolean).length} Mapped NS Fields`
                        : `Accept ${Object.values(aiSelected).filter(Boolean).length} Selected Fields`}
                    </>
                  )}
                </button>
                <button
                  onClick={handleAddManualField}
                  title="Add a field the AI missed to this batch"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 bg-white dark:bg-gray-800 text-sm font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add Field
                </button>
                <button
                  onClick={handleDiscardSuggested}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Discard
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* ===== END AI SUGGEST PANEL ===== */}

      {/* Analyzer status inline bar */}
      {activeTab === "fields" && (
        <>
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Analyzer:
                </span>
                {template?.analyzer?.is_active ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 font-medium">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Not built
                  </span>
                )}
              </div>
              {template?.analyzer?.azure_analyzer_id && (
                <span className="text-xs font-mono text-gray-400 dark:text-gray-500 hidden sm:inline">
                  {template.analyzer.azure_analyzer_id}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {(template?.fields || []).length} fields
            </span>
          </div>
          {/* How current this template's NetSuite field rules are. Detection runs
              automatically (on creation, on vendor confirmation, and nightly on
              drift), so showing when it last ran is what makes that trustworthy
              rather than invisible. */}
          {isNsTemplate && (
            <NSFieldFreshness
              templateId={template?.id || id}
              nsEntityType={nsEntityTypeForCopy}
              onRefreshed={() => loadTemplate()}
            />
          )}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Fields
              </h2>
              {(hasPendingRequired || hasPendingMandatory) && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving…
                </span>
              )}
            </div>

            {/* Bulk selection action bar */}
            {!isReader && selectedFieldIds.length > 0 && (
              <div className="flex items-center justify-between rounded-xl border border-navy-200 dark:border-navy-800/50 bg-navy-50 dark:bg-navy-900/20 px-4 py-2.5 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-navy-600 dark:text-navy-400" />
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {selectedFieldIds.length}{" "}
                    {selectedFieldIds.length === 1 ? "field" : "fields"}{" "}
                    selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearFieldSelection}
                    className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-gray-800/60 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleBulkDeleteFields}
                    disabled={bulkDeleting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bulkDeleting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Delete selected
                  </button>
                </div>
              </div>
            )}

            {(template?.fields || []).length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No fields yet. Add your first field to start building the
                  analyzer.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {isNsTemplate &&
                  (() => {
                    const isBidTemplate =
                      (template?.ns_entity_type || template?.template_ns_entity_type) === "vendor_bid";
                    const groupDisplayName = isBidTemplate ? "Bid Details" : "NetSuite PO";
                    // groupedFields is keyed by the category's slug (e.g. "netsuite_po"),
                    // not its display name — compare against displayName instead of
                    // indexing by the pretty string, which can never match a slug key.
                    const hasGroup = Object.values(groupedFields).some(
                      (g) => g.displayName === groupDisplayName,
                    );
                    if (hasGroup) return null;
                    return (
                      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10 p-4 flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold text-purple-800 dark:text-purple-300">
                            {isBidTemplate ? "No Special Pricing fields yet" : "No NetSuite PO fields yet"}
                          </p>
                          <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                            {isBidTemplate
                              ? 'This template has NetSuite integration enabled but none of the standard Special Pricing fields (Project Name, Bid Number, Supplier, Reseller, End Customer, Expected Close Date, Bid Line Items) have been added. Either run "Upload NetSuite PDF" to map them from a sample bid document, or add the standard set now.'
                              : 'This template has NetSuite integration enabled but none of the standard PO fields (Date, Order No, Supplier, Deliver To, Currency, digiDirect, Line Items) have been added. Either run "Upload NetSuite PDF" to map them from a sample document, or add the standard set now.'}
                          </p>
                        </div>
                        <button
                          onClick={handleSeedStandardNsFields}
                          disabled={seedingNsFields || isReader}
                          title={isReader ? READ_ONLY_MESSAGE : undefined}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          {seedingNsFields ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                          {isBidTemplate ? "Add Standard Special Pricing Fields" : "Add Standard NetSuite PO Fields"}
                        </button>
                      </div>
                    );
                  })()}
                {Object.entries(groupedFields).map(([categoryKey, group]) => {
                  const groupFieldIds = (group.fields || []).map((f) => f.id);
                  const allSelected =
                    groupFieldIds.length > 0 &&
                    groupFieldIds.every((id) => selectedFieldIds.includes(id));
                  const someSelected =
                    !allSelected &&
                    groupFieldIds.some((id) => selectedFieldIds.includes(id));
                  return (
                    <div key={categoryKey}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                        {group.displayName}
                      </p>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800/60">
                            <tr>
                              {!isReader && (
                                <th className="px-3 py-2 w-10 text-center">
                                  <input
                                    type="checkbox"
                                    aria-label={`Select all fields in ${group.displayName}`}
                                    checked={allSelected}
                                    ref={(el) => {
                                      if (el) el.indeterminate = someSelected;
                                    }}
                                    onChange={() =>
                                      toggleGroupSelected(
                                        groupFieldIds,
                                        allSelected,
                                      )
                                    }
                                    className="h-[18px] w-[18px] shrink-0 appearance-none rounded-full border-2 border-gray-300 dark:border-gray-600 cursor-pointer align-middle transition-colors relative after:content-[''] after:absolute after:inset-0 after:m-auto after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:opacity-0 checked:border-navy-600 checked:bg-navy-600 checked:after:opacity-100 indeterminate:border-navy-600 indeterminate:bg-navy-600 indeterminate:after:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400/40"
                                  />
                                </th>
                              )}
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                Display
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                Field Name
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                Type
                              </th>
                              <th className="px-3 py-2 text-center font-medium text-gray-500 dark:text-gray-400">
                                Required
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                Description
                              </th>
                              <th className="px-3 py-2 text-center font-medium text-gray-500 dark:text-gray-400">
                                User Input
                              </th>
                              {isBcTemplate && (
                                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                  BC Column
                                </th>
                              )}
                              {isNsTemplate && (
                                <th className="px-3 py-2 text-left font-medium text-purple-500 dark:text-purple-400">
                                  NS Mapping
                                </th>
                              )}
                              <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.fields.map((field) => {
                              const isSelected = selectedFieldIds.includes(
                                field.id,
                              );
                              // A line-items table used to collapse into one
                              // row with no visible mapping at all — every
                              // column's NetSuite field lived only inside
                              // field_values, never shown. Parsed here so the
                              // sub-row below can display it directly.
                              let lineColumns = null;
                              if (field.field_type === "array_of_objects") {
                                try {
                                  const raw =
                                    typeof field.field_values === "string"
                                      ? JSON.parse(field.field_values)
                                      : field.field_values;
                                  if (Array.isArray(raw)) lineColumns = raw;
                                } catch {
                                  lineColumns = null;
                                }
                              }
                              const colCount =
                                (isReader ? 0 : 1) +
                                6 +
                                (isBcTemplate ? 1 : 0) +
                                (isNsTemplate ? 1 : 0) +
                                1;
                              return (
                                <React.Fragment key={field.id}>
                                <tr
                                  onClick={() => openFieldEditor(field)}
                                  className={`border-t border-gray-100 dark:border-gray-800 transition-colors duration-150 ${
                                    isSelected
                                      ? "bg-navy-50/60 dark:bg-navy-900/20"
                                      : ""
                                  } ${isReader ? "" : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"}`}
                                  title={
                                    isReader ? undefined : "Click to edit field"
                                  }
                                >
                                  {!isReader && (
                                    <td
                                      className="px-3 py-2 text-center"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        aria-label={`Select ${field.display_name}`}
                                        checked={isSelected}
                                        onChange={() =>
                                          toggleFieldSelected(field.id)
                                        }
                                        className="h-[18px] w-[18px] shrink-0 appearance-none rounded-full border-2 border-gray-300 dark:border-gray-600 cursor-pointer align-middle transition-colors relative after:content-[''] after:absolute after:inset-0 after:m-auto after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:opacity-0 checked:border-navy-600 checked:bg-navy-600 checked:after:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400/40"
                                      />
                                    </td>
                                  )}
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                    {field.display_name}
                                    {isEmailSourced(field) && (
                                      <span
                                        className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 align-middle"
                                        title={`Filled from the e-mail (${emailFieldLabel(field.value_source) || "e-mail detail"}); not read from the document`}
                                      >
                                        <Mail className="h-3 w-3" />
                                        From e-mail
                                      </span>
                                    )}
                                    {isTemplateDefault(field) && (
                                      <span
                                        className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 align-middle"
                                        title="Fixed value from this template's NetSuite default; not read from the document"
                                      >
                                        Fixed value
                                      </span>
                                    )}
                                    {(fieldNotes[field.field_name] || []).map((n) => {
                                      const what = n.columns.length
                                        ? `${n.columns.join(", ")} column${n.columns.length === 1 ? "" : "s"}`
                                        : "";
                                      return n.kind === "excluded" ? (
                                        <span
                                          key={`${n.webhook_id}:${n.kind}`}
                                          className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 align-middle"
                                          title={`Marked "not sent" in ${n.name}'s Map Payload${what ? ` (${what})` : ""}. Change it on the Connectors tab.`}
                                        >
                                          <Unlink className="h-3 w-3" />
                                          {what ? `${what} not sent` : "Not sent"} to {n.name}
                                        </span>
                                      ) : (
                                        <span
                                          key={`${n.webhook_id}:${n.kind}`}
                                          className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 align-middle"
                                          title={`${n.name}'s Map Payload doesn't use ${what ? `the ${what} of ` : ""}this field and doesn't mark it "not sent", so publishing to ${n.name} is blocked. Map it or mark it on the Connectors tab.`}
                                        >
                                          <AlertCircle className="h-3 w-3" />
                                          {what ? `${what} not` : "Not"} in {n.name}'s Map Payload
                                        </span>
                                      );
                                    })}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                                    {field.field_name}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                    {field.field_type}
                                  </td>
                                  <td
                                    className="px-3 py-2 text-center"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {(() => {
                                      const isMandatory =
                                        field.id in pendingMandatory
                                          ? pendingMandatory[field.id]
                                          : !!field.extraction_is_required;
                                      return (
                                        <Toggle
                                          checked={isMandatory}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            if (isReader || field.id in pendingMandatory) return;
                                            saveFieldToggle(field, "extraction_is_required", setPendingMandatory);
                                          }}
                                          title={
                                            isMandatory
                                              ? "Mandatory for extraction - click to make optional"
                                              : "Optional - click to make mandatory"
                                          }
                                        />
                                      );
                                    })()}
                                  </td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-xl">
                                    {field.description || "-"}
                                  </td>
                                  <td
                                    className="px-3 py-2 text-center"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {(() => {
                                      const isReq =
                                        field.id in pendingRequired
                                          ? pendingRequired[field.id]
                                          : !!field.is_required;
                                      return (
                                        <Toggle
                                          checked={isReq && !isEmailSourced(field)}
                                          disabled={isEmailSourced(field)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            if (isReader || field.id in pendingRequired) return;
                                            saveFieldToggle(field, "is_required", setPendingRequired);
                                          }}
                                          title={
                                            isEmailSourced(field)
                                              ? "Filled from the e-mail, so never asked for in the plugin"
                                              : isReq
                                                ? "User must fill in plugin - click to disable"
                                                : "Optional - click to require user input"
                                          }
                                        />
                                      );
                                    })()}
                                  </td>
                                  {isBcTemplate && (
                                    <td
                                      className="px-3 py-2"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {field.field_type === 'array_of_objects' ? (
                                        <span className="text-[10px] text-gray-400 italic">line items — edit the field to map table columns</span>
                                      ) : (field.bc_role === 'line' || bcEntityCatalog[field.field_name]?.is_line_amount) ? (
                                        // Becomes a document line, so there is no header field to point at.
                                        // Not an unmapped field. bc_role comes down on the template payload
                                        // itself; the catalog lookup is only a fallback for an older backend.
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">
                                          <ArrowRight className="h-3 w-3" />
                                          Invoice line
                                          {(field.bc_gl_account_number || bcEntityCatalog[field.field_name]?.gl_account_number)
                                            ? ` · G/L ${field.bc_gl_account_number || bcEntityCatalog[field.field_name].gl_account_number}` : ''}
                                        </span>
                                      ) : (field.bc_role === 'validation' || bcEntityCatalog[field.field_name]?.review_only) ? (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">
                                          <ShieldCheck className="h-3 w-3" />
                                          Validation only
                                        </span>
                                      ) : field.bc_field ? (
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <span className="inline-flex px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 text-[10px] font-mono">
                                            {field.bc_field}
                                          </span>
                                          {field.is_required && (
                                            <span className="inline-flex px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-[10px] font-medium">
                                              mandatory
                                            </span>
                                          )}
                                          {field.extraction_is_required && (
                                            <span className="inline-flex px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-[10px] font-medium">
                                              extractable
                                            </span>
                                          )}
                                          {field.review_only && (
                                            <span className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-[10px] font-medium">
                                              review only
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-300 dark:text-gray-600 text-xs italic">
                                          —
                                        </span>
                                      )}
                                    </td>
                                  )}
                                  {isNsTemplate && (
                                    <td
                                      className="px-3 py-2"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {field.field_type ===
                                      "array_of_objects" ? (
                                        (() => {
                                          const postedCols = (
                                            lineColumns || []
                                          ).filter((c) => c.posted !== false);
                                          const mappedCount = postedCols.filter(
                                            (c) => c.ns_field,
                                          ).length;
                                          const unmappedCount =
                                            postedCols.length - mappedCount;
                                          return (
                                            <span
                                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                                                postedCols.length === 0
                                                  ? "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                                                  : unmappedCount > 0
                                                    ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                                    : "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                              }`}
                                              title="Line item columns are mapped in the sub-table below"
                                            >
                                              <Rows className="h-3 w-3" />
                                              {postedCols.length === 0
                                                ? "table — no columns yet"
                                                : `${mappedCount}/${postedCols.length} line columns mapped below`}
                                            </span>
                                          );
                                        })()
                                      ) : !field.ns_field &&
                                        headerFieldsPostedViaLine[
                                          field.field_name
                                        ] ? (
                                        <div className="flex items-center gap-1 text-[11px] text-purple-600 dark:text-purple-400">
                                          <CornerDownRight className="h-3 w-3 flex-shrink-0" />
                                          <span>
                                            read once, posted per line as{" "}
                                            <span className="font-mono">
                                              {
                                                headerFieldsPostedViaLine[
                                                  field.field_name
                                                ]
                                              }
                                            </span>
                                          </span>
                                        </div>
                                      ) : (
                                        <NsFieldSearchDropdown
                                          value={field.ns_field || ""}
                                          loadError={nsFieldCatalogError}
                                          disabled={isReader}
                                          onChange={async (newVal) => {
                                            if (isReader) return;
                                            try {
                                              await templatesApi.updateField(
                                                id,
                                                field.id,
                                                { ns_field: newVal || null },
                                              );
                                              await loadTemplate();
                                            } catch (err) {
                                              console.error(
                                                "NS mapping update failed:",
                                                err,
                                              );
                                              toast.error(
                                                "NS mapping update failed",
                                                err?.response?.data?.error ||
                                                  "Could not save the NetSuite field mapping",
                                              );
                                            }
                                          }}
                                          catalog={nsFieldCatalog}
                                          compact
                                          placeholder="— None —"
                                        />
                                      )}
                                      {field.ns_field &&
                                        nsFieldCatalog.some(
                                          (f) =>
                                            f.key === field.ns_field &&
                                            f.mandatory,
                                        ) && (
                                          <span className="inline-flex mt-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-[10px] font-medium">
                                            required
                                          </span>
                                        )}
                                    </td>
                                  )}
                                  <td
                                    className="px-3 py-2"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="flex items-center justify-end gap-1">
                                      <button
                                        onClick={() => openFieldEditor(field)}
                                        disabled={isReader}
                                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={
                                          isReader ? READ_ONLY_MESSAGE : "Edit"
                                        }
                                      >
                                        <Pencil className="h-4 w-4 text-gray-500" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          !isReader && handleDeleteField(field)
                                        }
                                        disabled={isReader}
                                        className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={
                                          isReader
                                            ? READ_ONLY_MESSAGE
                                            : "Delete"
                                        }
                                      >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                {/* NetSuite templates: where each line column is posted. */}
                                {lineColumns && lineColumns.length > 0 && isNsTemplate && (
                                  <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40">
                                    <td colSpan={colCount} className="px-3 py-3">
                                      <div className="flex items-center justify-between mb-2 pl-1">
                                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                          <Rows className="h-3 w-3" />
                                          {lineColumns.length} line column
                                          {lineColumns.length === 1 ? "" : "s"} — one row per
                                          product, each posted to the field below
                                        </div>
                                        {(() => {
                                          const postedCols = lineColumns.filter(
                                            (c) => c.posted !== false,
                                          );
                                          const mappedCount = postedCols.filter(
                                            (c) => c.ns_field,
                                          ).length;
                                          const unmappedCount =
                                            postedCols.length - mappedCount;
                                          return (
                                            <span
                                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                unmappedCount > 0
                                                  ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                                  : "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                              }`}
                                            >
                                              {unmappedCount > 0 ? (
                                                <AlertCircle className="h-3 w-3" />
                                              ) : (
                                                <CheckCircle2 className="h-3 w-3" />
                                              )}
                                              {mappedCount}/{postedCols.length} mapped
                                            </span>
                                          );
                                        })()}
                                      </div>
                                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden bg-white dark:bg-gray-900">
                                        {lineColumns.map((col) => {
                                          const isUnposted = col.posted === false;
                                          const isUnmapped =
                                            !isUnposted && !col.ns_field;
                                          const isRequired = col.required === true;
                                          return (
                                            <div
                                              key={col.key || col.name}
                                              className={`flex items-center gap-3 px-3 py-1.5 text-xs transition-colors ${
                                                isUnmapped
                                                  ? "bg-amber-50/40 dark:bg-amber-900/10"
                                                  : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                                              }`}
                                            >
                                              <div className="w-1/4 min-w-0 flex items-center gap-1.5">
                                                <span className="truncate text-gray-700 dark:text-gray-300 font-medium">
                                                  {col.label || col.key}
                                                </span>
                                                {isRequired && (
                                                  <span
                                                    className="text-red-500 shrink-0"
                                                    title="Required"
                                                  >
                                                    *
                                                  </span>
                                                )}
                                              </div>
                                              <span className="w-1/6 min-w-0 truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">
                                                {col.key || col.name}
                                              </span>
                                              <span className="w-16 shrink-0 inline-flex px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[10px] font-medium text-center justify-center">
                                                {col.type || "text"}
                                              </span>
                                              <div className="flex-1 min-w-0 flex items-center">
                                                {isUnposted ? (
                                                  <span className="inline-flex items-center gap-1 text-[11px] italic text-gray-400 dark:text-gray-500">
                                                    <Unlink className="h-3 w-3" />
                                                    not posted — used to resolve another field
                                                  </span>
                                                ) : col.ns_field ? (
                                                  <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                                    <Link2 className="h-3 w-3 shrink-0" />
                                                    <span className="font-mono text-[11px]">
                                                      {col.ns_field}
                                                    </span>
                                                  </span>
                                                ) : (
                                                  <span className="inline-flex items-center gap-1 text-[11px] italic text-amber-600 dark:text-amber-400">
                                                    <AlertCircle className="h-3 w-3" />
                                                    unmapped
                                                  </span>
                                                )}
                                                {col.inherited_from_header && (
                                                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] font-medium shrink-0">
                                                    <CornerDownRight className="h-3 w-3" />
                                                    from header
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      <EmailFieldsPicker
        open={emailPickerOpen}
        onClose={() => setEmailPickerOpen(false)}
        onAdd={handleAddEmailFields}
        saving={addingEmailFields}
        existingSources={
          new Set(
            (template?.fields || [])
              .filter((f) => isEmailSourced(f))
              .map((f) => f.value_source),
          )
        }
      />
      <FieldModal
        open={fieldModalOpen}
        onClose={() => {
          setFieldModalOpen(false);
          setEditingField(null);
        }}
        onSave={handleSaveField}
        categories={templateCategories}
        existingCategoryNames={existingCategoryNames}
        initialField={editingField}
        saving={savingField}
        nsFieldCatalogError={nsFieldCatalogError}
        isNsTemplate={
          !!template?.ns_enabled ||
          isNsTemplate
        }
        nsFieldCatalog={nsFieldCatalog}
        isBcTemplate={isBcTemplate}
        bcFieldCatalog={bcFieldCatalog}
        bcLineFieldCatalog={bcLineFieldCatalog}
      />

      {/* Custom Headers Modal */}
      {headersModalWebhookId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setHeadersModalWebhookId(null)}
            />
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl h-[70vh] max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Custom Headers
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Added to every request sent to this webhook. Values support{" "}
                    <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">
                      {"{{field_name}}"}
                    </code>{" "}
                    substitution. Per-link headers override webhook-level
                    headers.
                  </p>
                </div>
                <button
                  onClick={() => setHeadersModalWebhookId(null)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body — two-column */}
              <div className="flex flex-1 overflow-hidden">
                {/* Left: field reference panel */}
                <div className="w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto p-4">
                  <input
                    type="text"
                    value={headersFieldSearch}
                    onChange={(e) => setHeadersFieldSearch(e.target.value)}
                    placeholder="Search fields..."
                    className="w-full px-2.5 py-1.5 mb-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-navy-500/40 focus:border-navy-500"
                  />
                  {(() => {
                    const fuzzy = (str, q) => {
                      if (!q) return true;
                      const s = str.toLowerCase(),
                        query = q.toLowerCase();
                      let si = 0;
                      for (let qi = 0; qi < query.length; qi++) {
                        si = s.indexOf(query[qi], si);
                        if (si === -1) return false;
                        si++;
                      }
                      return true;
                    };
                    const q = headersFieldSearch.trim();
                    // Headers only support scalar fields (arrays can't be header values)
                    const scalarFields = (template?.fields || [])
                      .filter(
                        (f) =>
                          (f.field_type || f.data_type) !==
                            "array_of_objects" && fuzzy(f.field_name, q),
                      )
                      .sort((a, b) => a.field_name.localeCompare(b.field_name));
                    const REQUEST_FIELDS = [
                      {
                        key: "request_id",
                        description: "The unique ID of this request",
                      },
                    ];
                    const filteredRequestFields = REQUEST_FIELDS.filter((f) =>
                      fuzzy(f.key, q),
                    );
                    const noResults =
                      scalarFields.length === 0 &&
                      filteredRequestFields.length === 0;
                    return noResults && q ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                        No fields match "{q}".
                      </p>
                    ) : (
                      <>
                        {scalarFields.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                              Template Fields
                            </p>
                            {scalarFields.map((f) => (
                              <button
                                key={f.field_name}
                                onClick={() =>
                                  navigator.clipboard.writeText(
                                    `{{${f.field_name}}}`,
                                  )
                                }
                                title={f.description || undefined}
                                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 group mb-0.5 flex items-center"
                              >
                                <code className="text-xs font-mono text-gray-700 dark:text-gray-300 flex-1 truncate">{`{{${f.field_name}}}`}</code>
                                <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" />
                              </button>
                            ))}
                          </>
                        )}
                        {filteredRequestFields.length > 0 && (
                          <div
                            className={
                              scalarFields.length > 0
                                ? "mt-3 pt-3 border-t border-gray-200 dark:border-gray-800"
                                : ""
                            }
                          >
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                              Request Fields
                            </p>
                            {filteredRequestFields.map((f) => (
                              <button
                                key={f.key}
                                onClick={() =>
                                  navigator.clipboard.writeText(`{{${f.key}}}`)
                                }
                                title={f.description}
                                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 group mb-0.5 flex items-center"
                              >
                                <code className="text-xs font-mono text-gray-700 dark:text-gray-300 flex-1 truncate">{`{{${f.key}}}`}</code>
                                <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" />
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Right: header editor */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    <span>Header Name</span>
                    <span>Value</span>
                    <span />
                  </div>
                  <div className="space-y-2">
                    {headerRows.map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
                      >
                        <input
                          value={row.key}
                          onChange={(e) =>
                            setHeaderRows((prev) =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, key: e.target.value } : r,
                              ),
                            )
                          }
                          placeholder="X-Header-Name"
                          className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                        />
                        <input
                          value={row.value}
                          onChange={(e) =>
                            setHeaderRows((prev) =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, value: e.target.value } : r,
                              ),
                            )
                          }
                          placeholder="value or {{field_name}}"
                          className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                        />
                        <button
                          onClick={() =>
                            setHeaderRows((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() =>
                      setHeaderRows((prev) => [...prev, { key: "", value: "" }])
                    }
                    className="mt-3 inline-flex items-center gap-1 text-xs text-navy-600 dark:text-navy-400 hover:underline"
                  >
                    <Plus className="h-3 w-3" /> Add Header
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2 flex-shrink-0">
                <button
                  onClick={() => setHeadersModalWebhookId(null)}
                  className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveHeaders}
                  disabled={savingHeadersId === headersModalWebhookId}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingHeadersId === headersModalWebhookId ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                    </>
                  ) : (
                    "Save Headers"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Output Destination Field Mapping Modal (AC4/AC5) */}
      {mappingModalDestinationId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setMappingModalDestinationId(null)}
            />
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Field Mapping &amp; Filename Pattern
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Map template fields to CSV column names, in export order.
                  </p>
                </div>
                <button
                  onClick={() => setMappingModalDestinationId(null)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {outputMappingError && (
                  <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                    {outputMappingError}
                  </div>
                )}

                <div
                  className={`rounded-lg border px-3 py-2.5 text-xs ${
                    outputMappingIsMultiRow
                      ? "border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/10 text-violet-800 dark:text-violet-300"
                      : "border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-300"
                  }`}
                >
                  {outputMappingIsMultiRow ? (
                    <>
                      <span className="font-semibold">One row per line item.</span>{" "}
                      This mapping includes table columns (shown as{" "}
                      <span className="font-mono">Field › Column</span>), so the CSV will
                      contain one row for each line item, with the other columns repeated
                      on every row — the layout ERP imports usually expect.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">One row per request.</span>{" "}
                      To export line items as separate rows instead, map a table field&apos;s
                      individual columns (they appear as{" "}
                      <span className="font-mono">Field › Column</span> in the dropdown).
                    </>
                  )}
                </div>

                <div>
                  {/* Count and row actions live ABOVE the list. On a 53-field
                      template they were previously below it, so reaching
                      "Reset to defaults" meant scrolling past every row. */}
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Columns{" "}
                      <span className="text-gray-400">
                        ({outputFieldMappingRows.length})
                      </span>
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() =>
                          setOutputFieldMappingRows((prev) => [
                            ...prev,
                            { xtract_field: "", csv_column: "" },
                          ])
                        }
                        className="inline-flex items-center gap-1 text-xs text-navy-600 dark:text-navy-400 hover:underline"
                      >
                        <Plus className="h-3 w-3" /> Add row
                      </button>
                      <button
                        onClick={() => setOutputFieldMappingRows([])}
                        disabled={outputFieldMappingRows.length === 0}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-40 disabled:no-underline"
                      >
                        Clear all
                      </button>
                      <button
                        onClick={handleResetOutputFieldMappingToDefaults}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                      >
                        Reset to defaults
                      </button>
                    </div>
                  </div>
                  {/* The row list gets its OWN bounded scroll area. Previously
                      the whole modal body scrolled as one, so on a 53-field
                      template the Filename Pattern field below sat 53 rows
                      past the fold. */}
                  <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-800">
                      <span>Template Field</span>
                      <span>CSV Column Name</span>
                      <span className="w-6" />
                    </div>
                    {outputFieldMappingRows.length === 0 ? (
                      <p className="px-3 py-6 text-center text-xs text-gray-400">
                        No columns mapped. Exports will fail until at least one
                        is added.
                      </p>
                    ) : (
                    <div className="max-h-[40vh] overflow-y-auto p-2 space-y-2">
                    {outputFieldMappingRows.map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
                      >
                        <select
                          value={row.xtract_field}
                          onChange={(e) =>
                            setOutputFieldMappingRows((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? { ...r, xtract_field: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                        >
                          <option value="">Select field...</option>
                          {outputMappableOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <input
                          value={row.csv_column}
                          onChange={(e) =>
                            setOutputFieldMappingRows((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? { ...r, csv_column: e.target.value }
                                  : r,
                              ),
                            )
                          }
                          placeholder="CSV column name, e.g. OrderNumber"
                          className="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                        />
                        <button
                          onClick={() =>
                            setOutputFieldMappingRows((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                    Filename Pattern
                  </label>
                  <input
                    ref={outputFilenamePatternInputRef}
                    value={outputFilenamePattern}
                    onChange={(e) => setOutputFilenamePattern(e.target.value)}
                    placeholder="Xtract_{RequestId}_{Timestamp}.csv"
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                  />
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-[11px] text-gray-400 mr-0.5">Insert:</span>
                    {["RequestId", "Date", "Timestamp", "Template"].map((token) => (
                      <button
                        key={token}
                        type="button"
                        onClick={() => insertOutputFilenamePatternToken(token)}
                        className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-navy-50 dark:hover:bg-navy-900/30 hover:border-navy-400 hover:text-navy-700 dark:hover:text-navy-300 transition-colors"
                      >
                        {`{${token}}`}
                      </button>
                    ))}
                  </div>
                  {outputFilenamePattern.trim() && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                      Preview:{" "}
                      <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">
                        {getOutputFilenamePatternPreview()}
                      </code>
                    </p>
                  )}
                  <p className="text-[11px] text-gray-400 mt-1.5">
                    Must end in <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">.csv</code>.
                    Available tokens:{" "}
                    <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{"{RequestId}"}</code>{" "}
                    (the request&apos;s numeric ID),{" "}
                    <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{"{Date}"}</code>{" "}
                    (export date, YYYYMMDD),{" "}
                    <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{"{Timestamp}"}</code>{" "}
                    (export timestamp, YYYYMMDDTHHMMSSZ), and{" "}
                    <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{"{Template}"}</code>{" "}
                    (this template&apos;s name, sanitized).
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2 flex-shrink-0">
                <button
                  onClick={() => setMappingModalDestinationId(null)}
                  className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOutputMapping}
                  disabled={savingOutputMappingId === mappingModalDestinationId}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingOutputMappingId === mappingModalDestinationId ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                    </>
                  ) : (
                    "Save Mapping"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Output Destination Disconnect Confirmation */}
      {outputDestinationPendingDisconnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
            onClick={() =>
              unlinkingOutputDestinationId === null &&
              setOutputDestinationPendingDisconnect(null)
            }
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                  <Unlink className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Disconnect output destination?
                  </h3>
                  <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    This template will stop exporting approved requests to
                    this destination. The destination itself is not deleted.
                  </p>
                  <div className="mt-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                      {outputDestinationPendingDisconnect.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2.5">
              <button
                onClick={() => setOutputDestinationPendingDisconnect(null)}
                disabled={unlinkingOutputDestinationId !== null}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                Keep it
              </button>
              <button
                onClick={async () => {
                  const target = outputDestinationPendingDisconnect;
                  setOutputDestinationPendingDisconnect(null);
                  await handleUnlinkOutputDestination(target.id);
                }}
                disabled={unlinkingOutputDestinationId !== null}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {unlinkingOutputDestinationId !== null && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payload Mapping Modal */}
      {mappingModalWebhookId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setMappingModalWebhookId(null)}
            />
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl h-[80vh] max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Map Payload
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Define the JSON structure sent to this webhook. Use{" "}
                    <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">
                      {"{{field_name}}"}
                    </code>{" "}
                    for extracted values.
                  </p>
                </div>
                <button
                  onClick={() => setMappingModalWebhookId(null)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body — two-column */}
              <div className="flex flex-1 overflow-hidden">
                {/* Left: field reference panel */}
                <div className="w-64 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto p-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Available Fields
                  </p>
                  <input
                    type="text"
                    value={mappingFieldSearch}
                    onChange={(e) => setMappingFieldSearch(e.target.value)}
                    placeholder="Search fields..."
                    className="w-full px-2.5 py-1.5 mb-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-navy-500/40 focus:border-navy-500"
                  />
                  {!template?.fields?.length ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                      No fields defined on this template.
                    </p>
                  ) : (
                    (() => {
                      const fuzzy = (str, q) => {
                        if (!q) return true;
                        const s = str.toLowerCase(),
                          query = q.toLowerCase();
                        let si = 0;
                        for (let qi = 0; qi < query.length; qi++) {
                          si = s.indexOf(query[qi], si);
                          if (si === -1) return false;
                          si++;
                        }
                        return true;
                      };
                      const q = mappingFieldSearch.trim();
                      // Build a unified sorted list of all fields (scalars + arrays interleaved)
                      const allFields = template.fields
                        .map((f) => {
                          const isArray =
                            (f.field_type || f.data_type) ===
                            "array_of_objects";
                          if (isArray) {
                            const cols = parseFieldValues(f.field_values);
                            const matchesParent = fuzzy(f.field_name, q);
                            const filteredCols = (
                              matchesParent
                                ? cols
                                : cols.filter((c) => fuzzy(c.key, q))
                            ).sort((a, b) => a.key.localeCompare(b.key));
                            // 'Index' is a reserved placeholder available inside every $$map block
                            const showIndex =
                              matchesParent || fuzzy("Index", q);
                            const visible =
                              matchesParent ||
                              filteredCols.length > 0 ||
                              showIndex;
                            return {
                              f,
                              isArray: true,
                              cols,
                              filteredCols,
                              showIndex,
                              visible,
                            };
                          }
                          return {
                            f,
                            isArray: false,
                            visible: fuzzy(f.field_name, q),
                          };
                        })
                        .filter((item) => item.visible)
                        .sort((a, b) =>
                          a.f.field_name.localeCompare(b.f.field_name),
                        );
                      const noResults = allFields.length === 0;
                      return (
                        <>
                          {noResults && q ? (
                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                              No fields match "{q}".
                            </p>
                          ) : (
                            <>
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                Template Fields
                              </p>
                              {allFields.map(
                                ({
                                  f,
                                  isArray,
                                  cols,
                                  filteredCols,
                                  showIndex,
                                }) =>
                                  isArray ? (
                                    <div
                                      key={f.field_name}
                                      className="mb-2 mt-1"
                                    >
                                      <div className="flex items-center justify-between px-2 mb-1">
                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                                          {f.field_name}
                                        </span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">
                                          array
                                        </span>
                                      </div>
                                      <button
                                        onClick={() =>
                                          navigator.clipboard.writeText(
                                            JSON.stringify(
                                              {
                                                $$map: f.field_name,
                                                $$item: Object.fromEntries(
                                                  cols.map((c) => [
                                                    c.key,
                                                    `{{${c.key}}}`,
                                                  ]),
                                                ),
                                              },
                                              null,
                                              2,
                                            ),
                                          )
                                        }
                                        className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 group flex items-center gap-1 mb-1"
                                      >
                                        <code className="text-[10px] font-mono text-blue-600 dark:text-blue-400">
                                          Copy $$map block
                                        </code>
                                        <Copy className="h-3 w-3 text-blue-400 opacity-0 group-hover:opacity-100 ml-auto flex-shrink-0" />
                                      </button>
                                      {(filteredCols.length > 0 ||
                                        showIndex) && (
                                        <div className="ml-2 pl-3 border-l-2 border-blue-200 dark:border-blue-800 mb-1">
                                          {filteredCols.map((col) => (
                                            <button
                                              key={col.key}
                                              onClick={() =>
                                                navigator.clipboard.writeText(
                                                  `{{${col.key}}}`,
                                                )
                                              }
                                              title={
                                                col.description ||
                                                col.label ||
                                                undefined
                                              }
                                              className="w-full text-left px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 group flex items-center"
                                            >
                                              <code className="text-[10px] font-mono text-gray-600 dark:text-gray-400 flex-1 truncate">{`{{${col.key}}}`}</code>
                                              <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" />
                                            </button>
                                          ))}
                                          {showIndex && (
                                            <button
                                              onClick={() =>
                                                navigator.clipboard.writeText(
                                                  "{{Index}}",
                                                )
                                              }
                                              title="1-based position of the current row inside this $$map block"
                                              className="w-full text-left px-2 py-1 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 group flex items-center"
                                            >
                                              <code className="text-[10px] font-mono text-purple-600 dark:text-purple-400 flex-1 truncate">{`{{Index}}`}</code>
                                              <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-medium ml-1">
                                                special
                                              </span>
                                              <Copy className="h-3 w-3 text-purple-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <button
                                      key={f.field_name}
                                      onClick={() =>
                                        navigator.clipboard.writeText(
                                          `{{${f.field_name}}}`,
                                        )
                                      }
                                      title={
                                        isEmailSourced(f)
                                          ? `From the e-mail: ${emailFieldLabel(f.value_source) || "e-mail detail"}`
                                          : f.description || undefined
                                      }
                                      className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 group mb-0.5 flex items-center"
                                    >
                                      <code className="text-xs font-mono text-gray-700 dark:text-gray-300 flex-1 truncate">{`{{${f.field_name}}}`}</code>
                                      {isEmailSourced(f) && (
                                        <span className="text-[9px] px-1 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 font-medium ml-1 flex-shrink-0">
                                          e-mail
                                        </span>
                                      )}
                                      <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" />
                                    </button>
                                  ),
                              )}
                            </>
                          )}
                        </>
                      );
                    })()
                  )}

                  {/* Request fields section */}
                  {(() => {
                    const REQUEST_FIELDS = [
                      {
                        key: "request_id",
                        description: "The unique ID of this request",
                      },
                    ];
                    const q = mappingFieldSearch.trim();
                    const filtered = REQUEST_FIELDS.filter(
                      (f) =>
                        !q || f.key.toLowerCase().includes(q.toLowerCase()),
                    );
                    if (!filtered.length) return null;
                    return (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Request Fields
                        </p>
                        {filtered.map((f) => (
                          <button
                            key={f.key}
                            onClick={() =>
                              navigator.clipboard.writeText(`{{${f.key}}}`)
                            }
                            title={f.description}
                            className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 group mb-0.5 flex items-center"
                          >
                            <code className="text-xs font-mono text-gray-700 dark:text-gray-300 flex-1 truncate">{`{{${f.key}}}`}</code>
                            <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" />
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Right: editor */}
                <div className="flex-1 flex flex-col p-4 overflow-hidden">
                  {/* Boomi structured validation panel — consolidated here per
                      architecture decision (replaces the standalone Boomi tab).
                      Only shown when Boomi is enabled on this template; OpCo is
                      derived from the mapping's own company_id literal, not a
                      separate dropdown. */}
                  {template?.template_boomi_enabled && (
                    <div className="mb-3 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/10 px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold text-teal-700 dark:text-teal-300">Boomi Field Validation</p>
                          <p className="text-[11px] text-teal-600 dark:text-teal-400 mt-0.5">
                            Static presence check against mandatory fields in the Boomi order spec.
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={boomiValidating}
                          onClick={async () => {
                            setBoomiValidating(true)
                            setBoomiValidationResult(null)
                            try {
                              let parsed
                              try {
                                parsed = JSON.parse(jsonEditorValue)
                              } catch {
                                parsed = {}
                              }
                              const res = await boomiApi.validateMapping({ type: 'json_template', format: mappingFormat, template: parsed })
                              setBoomiValidationResult(res.data)
                            } catch (e) {
                              setBoomiValidationResult({ success: false, error: e?.response?.data?.error || 'Validation failed' })
                            } finally {
                              setBoomiValidating(false)
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          {boomiValidating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Run Validation'}
                        </button>
                      </div>
                      {boomiValidationResult && (
                        <div className="mt-2 pt-2 border-t border-teal-200 dark:border-teal-800">
                          {!boomiValidationResult.success ? (
                            <p className="text-xs text-red-600 dark:text-red-400">{boomiValidationResult.error}</p>
                          ) : boomiValidationResult.missing_mandatory_fields?.length > 0 ? (
                            <div className="space-y-1">
                              {(boomiValidationResult.warnings || []).map((w, i) => (
                                <div key={i} className="flex items-start gap-1.5">
                                  <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-amber-600 dark:text-amber-400">{w}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-300">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              All mandatory Boomi fields are mapped.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Posting rules — relocated from the standalone Boomi tab.
                          require_approval is intentionally shown as a status line,
                          not an interactive toggle: it's already fully enforced by
                          the template's existing reviewer/admin approve-gate, so a
                          toggle here would be misleading (it wouldn't do anything). */}
                      <div className="mt-2 pt-2 border-t border-teal-200 dark:border-teal-800 space-y-2">
                        <div className="flex items-center gap-1.5 text-[11px] text-teal-600 dark:text-teal-400">
                          <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                          Approval is already required before delivery via this template's existing reviewer/admin approval gate.
                        </div>
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <span className="text-xs text-teal-800 dark:text-teal-200">Validate before posting</span>
                          <Toggle
                            checked={boomiValidateBeforePosting}
                            onChange={() => { setBoomiValidateBeforePosting(v => !v); setBoomiTogglesDirty(true) }}
                          />
                        </label>
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <span className="text-xs text-teal-800 dark:text-teal-200">Block posting on errors</span>
                          <Toggle
                            checked={boomiBlockOnErrors}
                            onChange={() => { setBoomiBlockOnErrors(v => !v); setBoomiTogglesDirty(true) }}
                          />
                        </label>
                        {boomiTogglesDirty && (
                          <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                              type="button"
                              disabled={boomiTogglesSaving}
                              onClick={async () => {
                                setBoomiTogglesSaving(true)
                                try {
                                  const res = await templatesApi.update(id, {
                                    boomi_validate_before_posting: boomiValidateBeforePosting,
                                    boomi_block_on_errors: boomiBlockOnErrors,
                                  })
                                  setTemplate(res.data)
                                  setBoomiTogglesDirty(false)
                                  toast.success('Saved', 'Boomi posting rules updated.')
                                } catch (e) {
                                  toast.error('Save Failed', 'Could not save Boomi posting rules.')
                                } finally {
                                  setBoomiTogglesSaving(false)
                                }
                              }}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {boomiTogglesSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save posting rules'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Format selector */}
                  <div className="flex items-center gap-1 mb-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
                      Format
                    </span>
                    {[
                      { value: "json", label: "JSON" },
                      { value: "csv", label: "CSV", soon: true },
                      { value: "xml", label: "XML", soon: true },
                    ].map((fmt) => (
                      <button
                        key={fmt.value}
                        onClick={() => !fmt.soon && setMappingFormat(fmt.value)}
                        disabled={!!fmt.soon}
                        title={fmt.soon ? "Coming soon" : undefined}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                          mappingFormat === fmt.value
                            ? "bg-navy-800 text-white"
                            : fmt.soon
                              ? "border border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                              : "border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        {fmt.label}
                        {fmt.soon && (
                          <span className="ml-1 text-[10px] opacity-60">
                            soon
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Template
                  </label>
                  <textarea
                    className="flex-1 w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-500"
                    value={jsonEditorValue}
                    onChange={(e) => {
                      setJsonEditorValue(e.target.value);
                      setMappingServerCheck(null);
                    }}
                    placeholder={
                      '{\n  "order": {\n    "order_number": "{{order_number}}"\n  }\n}'
                    }
                    spellCheck={false}
                  />
                  {jsonError && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" /> {jsonError}
                    </p>
                  )}
                  <MappingCheckPanel
                    check={mappingServerCheck || mappingLiveCheck}
                    excluded={mappingExcluded}
                    onExclude={(keys) => {
                      setMappingServerCheck(null);
                      setMappingExcluded((prev) => [...new Set([...prev, ...keys])]);
                    }}
                    onInclude={(key) => {
                      setMappingServerCheck(null);
                      setMappingExcluded((prev) => prev.filter((k) => k !== key));
                    }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
                {(() => {
                  const activeWh = linkedWebhooks.find(
                    (w) => w.id === mappingModalWebhookId,
                  );
                  if (!activeWh?.payload_mapping) return <div />;
                  return clearMappingPending ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Remove mapping?
                      </span>
                      <button
                        onClick={handleClearMapping}
                        disabled={savingMappingId === mappingModalWebhookId}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium disabled:opacity-50"
                      >
                        {savingMappingId === mappingModalWebhookId ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          "Yes, clear"
                        )}
                      </button>
                      <button
                        onClick={() => setClearMappingPending(false)}
                        className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setClearMappingPending(true)}
                      disabled={savingMappingId === mappingModalWebhookId}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    >
                      Clear Mapping
                    </button>
                  );
                })()}
                <div className="flex gap-2">
                  <button
                    onClick={() => setMappingModalWebhookId(null)}
                    className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveMapping}
                    disabled={
                      savingMappingId === mappingModalWebhookId ||
                      mappingLiveCheck?.status === "error"
                    }
                    title={
                      mappingLiveCheck?.status === "error"
                        ? "Fix the problems listed under the editor first"
                        : undefined
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingMappingId === mappingModalWebhookId ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                      </>
                    ) : (
                      "Save Mapping"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Connector Confirmation Modal */}
      {webhookPendingDisconnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => {
              if (unlinkingWebhookId !== webhookPendingDisconnect.id) {
                setWebhookPendingDisconnect(null);
              }
            }}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                  <Unlink className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Disconnect connector?
                  </h3>
                  <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Published records will not be pushed in this system anymore.
                  </p>
                  {webhookPendingDisconnect.name && (
                    <div className="mt-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                        {webhookPendingDisconnect.name}
                      </p>
                      {webhookPendingDisconnect.url && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono truncate mt-0.5">
                          {webhookPendingDisconnect.url}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2.5">
              <button
                onClick={() => setWebhookPendingDisconnect(null)}
                disabled={unlinkingWebhookId === webhookPendingDisconnect.id}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                Keep it
              </button>
              <button
                onClick={async () => {
                  const target = webhookPendingDisconnect;
                  if (!target) return;
                  await handleUnlinkWebhook(target.id);
                  setWebhookPendingDisconnect(null);
                }}
                disabled={unlinkingWebhookId === webhookPendingDisconnect.id}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {unlinkingWebhookId === webhookPendingDisconnect.id && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generic Confirmation Modal */}
      {confirmDialog &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ width: "100vw", height: "100vh" }}
          >
            <div
              className="fixed inset-0 bg-black/40"
              onClick={confirmDialog.onCancel}
            />
            <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6">
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${confirmDialog.variant === "danger" ? "bg-red-100 dark:bg-red-900/30" : "bg-amber-100 dark:bg-amber-900/30"}`}
                >
                  <AlertTriangle
                    className={`w-5 h-5 ${confirmDialog.variant === "danger" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {confirmDialog.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                    {confirmDialog.message}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2.5 mt-6">
                <button
                  onClick={confirmDialog.onCancel}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white ${confirmDialog.variant === "danger" ? "bg-red-600 hover:bg-red-700" : "bg-navy-800 hover:bg-navy-900"}`}
                >
                  {confirmDialog.confirmLabel}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {jsonModalOpen && (
        <TemplateJsonImportModal
          templateId={template?.id || id}
          templateName={template?.name}
          onClose={() => setJsonModalOpen(false)}
          onApplied={() => loadTemplate()}
        />
      )}

      {/* Build Analyzer Reminder Modal - triggered by ANY navigation when fields are dirty */}
      {navBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => {
              setNavBlocked(false);
              setPendingNavPath(null);
            }}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Analyzer Not Rebuilt
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              You've made changes to the template fields but haven't rebuilt the
              analyzer.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              Without rebuilding, your changes{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                won't take effect
              </span>{" "}
              during extraction.
            </p>
            <div className="flex items-center justify-end gap-2.5">
              <button
                onClick={() => {
                  setNavBlocked(false);
                  const path = pendingNavPath;
                  setPendingNavPath(null);
                  if (path) navigate(path);
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Leave Anyway
              </button>
              <button
                onClick={async () => {
                  setNavBlocked(false);
                  setPendingNavPath(null);
                  await handleBuildAnalyzer();
                }}
                disabled={
                  buildingAnalyzer || (template?.fields || []).length === 0
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium disabled:opacity-50"
              >
                <Bot className="h-4 w-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
